import React, { useState, useEffect } from 'react';
import { printHtml as triggerPrint } from './printUtils';
import axios from '../offline/profileApi';
import { saveComplaintWithOffline } from '../offline/runtime';
import { selectOfflineProfile, refreshProfileRevision, isProfileQueued, pendingProfileActions } from '../offline/profileActions';
import { OfflineImage, savedMediaUrl } from '../offline/OfflineMedia';
import { getApps, initializeApp } from 'firebase/app';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { uploadBytes } from '../utils/protectedStorage';
import { useOwnerContext } from './OwnerContext';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Card, Row, Col, Typography, Button, Divider, Table, Modal, Space, List, Image, Tag, Select, Radio, Input, InputNumber, Form, message, AutoComplete, Checkbox, Tooltip, Dropdown } from 'antd';
import { PlusOutlined, FileTextOutlined, BellOutlined, CalendarOutlined, FileImageOutlined, EyeOutlined, DollarOutlined, ExperimentOutlined, CheckCircleOutlined, ClockCircleOutlined, EditOutlined, WarningOutlined, PrinterOutlined, PercentageOutlined, SwapOutlined, GiftOutlined, UploadOutlined, AimOutlined, WhatsAppOutlined, DeleteOutlined, InboxOutlined, DownOutlined, ArrowLeftOutlined, MoreOutlined, SoundOutlined } from '@ant-design/icons';
import moment from 'moment';
import ReceiptsList from './ReceiptsList';
import { Receipt, Patient, Visit, ExternalService, ExternalServiceRequest, PatientPackage, VoiceNote, PatientVitals } from '../components/type';
import { usePatientContext } from './PatientContext';
import { useAuth } from './AuthContext';
import API from '../config/api';
import SendNotification from './SendNotification';
import NextVisitForm from './NextVisitForm';
import BillingModal from './BillingModal';
import DynamicHistoryForm from './DynamicHistoryForm';
import MedicalReportModal from './MedicalReportModal';
import ReferralModal from './ReferralModal';
import { sendBillingNotificationToAllAssistants, updateVisitOwnerInFirestoreWaitingList, updateVisitTypeInFirestoreWaitingList } from '../services/notificationService';
import { useDoctorContext } from './DoctorContext';
import { useClinicContext } from './ClinicContext';
import { useLanguage } from './LanguageContext';
import { useDoctorSuggestions } from '../hooks/useDoctorSuggestions';
import UploadReportModal from './UploadReportModal';
import ReceiptModal from './ReceiptPatient';
import DiagramViewer, { DiagramData } from './DiagramViewer';
import VoiceNoteSection, { VoiceNoteSectionHandle } from './VoiceNoteSection';
import ManualInventoryDeductionModal from './ManualInventoryDeductionModal';
import InsuranceProgramModal from './InsuranceProgramModal';
import AssessmentFormsSection from './AssessmentFormsSection';
import { resolveAssessmentClinicId } from '../utils/assessmentContext';
import { getClinicOwnerDoctorId } from '../utils/clinicOwnerDoctor';
import { ClinicDoctorOption, getClinicDoctorOptions } from '../utils/clinicDoctors';
import { applyWhatsAppLogo } from '../utils/whatsappBranding';
import { buildComplaintIdentityPayload, buildPatientHistoryParams } from '../utils/patientClinicalIdentity';
import { selectableVisitTypes, visibleManualPackages } from '../utils/serviceCatalogSafety';
import { buildVisitTypeBillingContext } from '../utils/visitTypeBillingContext';
import '../styles/PatientDetail.css';

// ── Firebase Storage (WhatsApp HTML upload) ───────────────────────────────────
const _fbConfig = {
  apiKey: 'AIzaSyDJ8HyRAm7z0RlgmYy3EjubVujy7E3ZBfA',
  authDomain: 'drwaiting-30f56.firebaseapp.com',
  projectId: 'drwaiting-30f56',
  storageBucket: 'drwaiting-30f56.firebasestorage.app',
  messagingSenderId: '937005545176',
  appId: '1:937005545476:web:be56ec33fce7e64e9f49ad',
};
const _fbApp = getApps().find(a => a.name === '[DEFAULT]') || initializeApp(_fbConfig);
const _fbStorage = getStorage(_fbApp);

async function uploadHtmlToFirebaseStorage(html: string, path: string): Promise<string> {
  const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
  const fileRef = storageRef(_fbStorage, path);
  const snapshot = await uploadBytes(fileRef, blob, { contentType: 'text/html; charset=utf-8' });
  return getDownloadURL(snapshot.ref);
}

const { Title, Text } = Typography;

// ── Billing receipt helpers ───────────────────────────────────────────────────

function formatReceiptDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('ar-EG', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return dateStr; }
}

function printBillingReceipt(patient: any, settings: any, latestDiagnosis?: string): void {
  const ps = settings?.printSettings ?? {
    paperSize: 'a4', marginTop: 0, marginLeft: 0, marginRight: 0,
    showHeader: true, showFooter: true,
  };
  const showHeader = ps.showHeader !== false;

  const lineItems: Array<{ description: string; category: string; amount: number; status: string; method: string }> = [];

  const consult = patient.consultationPayment;
  if (consult && Number(consult.amount) > 0) {
    lineItems.push({
      description: consult.consultationType || patient.visit_type || 'كشف',
      category: 'زيارة',
      amount: Number(consult.amount),
      status: consult.paymentStatus,
      method: consult.paymentMethod,
    });
  }

  for (const bill of (patient.bills ?? [])) {
    // Always include consultation fee if present in billing record
    if (Number(bill.consultationFee) > 0) {
      lineItems.push({
        description: bill.consultationType || patient.visit_type || 'كشف',
        category: 'زيارة',
        amount: Number(bill.consultationFee),
        status: bill.paymentStatus,
        method: bill.paymentMethod,
      });
    }
    // Include all services
    for (const svc of (bill.services ?? [])) {
      lineItems.push({
        description: svc.service_name ?? svc.name ?? 'خدمة',
        category: 'خدمة',
        amount: Number(svc.subtotal ?? svc.price ?? 0),
        status: bill.paymentStatus,
        method: bill.paymentMethod,
      });
    }
  }

  const totalAmount   = lineItems.reduce((s, i) => s + i.amount, 0);
  const paidAmount    = lineItems.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const pendingAmount = totalAmount - paidAmount;

  const statusLabel = (status: string, method: string) => {
    if (status === 'paid') return `مدفوع (${method === 'cash' ? 'نقداً' : 'كارت'})`;
    if (status === 'refund_due') return 'مستحق استرداد';
    return 'غير مدفوع';
  };
  const statusColor = (status: string) => {
    if (status === 'paid') return '#16a34a';
    if (status === 'refund_due') return '#d97706';
    return '#dc2626';
  };

  const receiptId = `${(patient.date ?? '').replace(/-/g, '')}-${(patient.file_number ?? patient.patient_id ?? patient.id ?? 'xxxx').toString().slice(-4)}`;

  const headerParts: string[] = [];
  if (settings?.clinicName)    headerParts.push(`<div class="clinic-name">${settings.clinicName}</div>`);
  if (settings?.doctorTitle)   headerParts.push(`<div class="doctor-title">${settings.doctorTitle}</div>`);
  if (settings?.clinicAddress) headerParts.push(`<div class="clinic-detail">📍 ${settings.clinicAddress}</div>`);
  if (settings?.clinicPhone)   headerParts.push(`<div class="clinic-detail">📞 ${settings.clinicPhone}</div>`);

  const linesHtml = lineItems.map((item, idx) => `
    <tr class="${idx % 2 === 0 ? 'row-even' : 'row-odd'}">
      <td class="td-num">${idx + 1}</td>
      <td class="td-desc"><span class="item-cat">${item.category}</span>${item.description}</td>
      <td class="td-amount">${item.amount.toLocaleString('ar-EG')} ج</td>
      <td class="td-status" style="color:${statusColor(item.status)}">${statusLabel(item.status, item.method)}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8"/>
  <title>إيصال — ${patient.patient_name ?? ''}</title>
  <style>
    @page { size: ${ps.paperSize === 'custom' ? 'auto' : (ps.paperSize || 'a4')}; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, 'Segoe UI', sans-serif; direction: rtl; color: #111; font-size: 13px; background: #fff; }
    .page {
      padding-top: ${((ps.marginTop || 0) / 2)}mm;
      padding-right: calc(${((ps.marginRight || 0) / 2)}mm + 14px);
      padding-left: calc(${((ps.marginLeft || 0) / 2)}mm + 14px);
      padding-bottom: 20px;
    }
    .header { display: ${showHeader ? 'block' : 'none'}; text-align: center; border-bottom: 3px double #1a56db; padding-bottom: 12px; margin-bottom: 14px; }
    .clinic-name  { font-size: 20px; font-weight: 800; color: #1a56db; }
    .doctor-title { font-size: 14px; font-weight: 600; color: #374151; margin-top: 4px; }
    .clinic-detail{ font-size: 12px; color: #6b7280; margin-top: 3px; }
    .receipt-title-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; gap: 12px; }
    .receipt-title { font-size: 17px; font-weight: 800; color: #111; border-right: 4px solid #1a56db; padding-right: 8px; }
    .receipt-meta { text-align: left; font-size: 11px; color: #6b7280; line-height: 1.7; }
    .receipt-meta strong { color: #111; }
    .patient-box { background: #f0f7ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 10px 14px; margin-bottom: 16px; display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; }
    .patient-field { font-size: 12px; color: #374151; }
    .patient-field span { font-weight: 700; color: #111; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    thead th { background: #1a56db; color: #fff; font-size: 12px; font-weight: 700; padding: 8px 10px; text-align: right; }
    td { padding: 7px 10px; font-size: 12px; vertical-align: middle; }
    .row-even { background: #fff; }
    .row-odd  { background: #f9fafb; }
    tr:not(:last-child) td { border-bottom: 1px solid #e5e7eb; }
    .td-num    { width: 28px; color: #9ca3af; text-align: center; }
    .td-amount { width: 90px; text-align: center; font-weight: 700; white-space: nowrap; }
    .td-status { width: 130px; font-size: 11px; font-weight: 600; white-space: nowrap; }
    .item-cat  { display: inline-block; font-size: 10px; font-weight: 700; background: #e0e7ff; color: #3730a3; border-radius: 4px; padding: 1px 5px; margin-left: 6px; }
    .totals { width: 260px; margin-right: auto; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-bottom: 20px; }
    .total-row { display: flex; justify-content: space-between; padding: 7px 14px; font-size: 13px; }
    .total-row + .total-row { border-top: 1px solid #e5e7eb; }
    .total-row.grand  { background: #1a56db; color: #fff; font-weight: 800; font-size: 14px; }
    .total-row.paid-row    { color: #16a34a; }
    .total-row.pending-row { color: #dc2626; }
    .footer-section { display: ${(showHeader && settings?.receiptFooter) ? 'block' : 'none'}; text-align: center; border-top: 1px dashed #d1d5db; padding-top: 10px; font-size: 11px; color: #6b7280; margin-bottom: 16px; }
    .sig-row { display: flex; justify-content: space-between; margin-top: 24px; gap: 20px; }
    .sig-box { flex: 1; border-top: 1px solid #9ca3af; padding-top: 6px; text-align: center; font-size: 11px; color: #6b7280; }
    .last-diagnosis-box {
      margin-bottom: 14px;
      padding: 10px 12px;
      background: #f6ffed;
      border: 1px solid #b7eb8f;
      border-radius: 8px;
      font-size: 12px;
      color: #333;
    }
    .last-diagnosis-text { margin-top: 6px; line-height: 1.5; white-space: pre-wrap; }
    @media print { .no-print { display: none !important; } }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    ${headerParts.length > 0 ? headerParts.join('\n') : '<div class="clinic-name">إيصال طبي</div>'}
    ${settings?.receiptHeader ? `<div style="margin-top:6px;font-size:12px;color:#374151">${settings.receiptHeader}</div>` : ''}
  </div>
  <div class="receipt-title-row">
    <div class="receipt-title">إيصال خدمات طبية</div>
    <div class="receipt-meta">
      <strong>رقم الإيصال:</strong> ${receiptId}<br/>
      <strong>التاريخ:</strong> ${formatReceiptDate(patient.date)}<br/>
      <strong>الوقت:</strong> ${patient.time ?? ''}
    </div>
  </div>
  <div class="patient-box">
    <div class="patient-field">الاسم: <span>${patient.patient_name ?? ''}</span></div>
    <div class="patient-field">رقم الملف: <span>${patient.file_number ?? '—'}</span></div>
    <div class="patient-field">الهاتف: <span>${patient.patient_phone ?? ''}</span></div>
    <div class="patient-field">نوع الزيارة: <span>${patient.visit_type ?? ''}</span></div>
    ${patient.doctor_name ? `<div class="patient-field">الطبيب: <span>د. ${patient.doctor_name}</span></div>` : ''}
  </div>
  ${latestDiagnosis ? `<div class="last-diagnosis-box"><strong>Latest diagnosis / آخر تشخيص:</strong><div class="last-diagnosis-text">${latestDiagnosis.replace(/\n/g, '<br/>')}</div></div>` : ''}
  <table>
    <thead>
      <tr>
        <th class="td-num">#</th>
        <th>البيان</th>
        <th class="td-amount">المبلغ</th>
        <th class="td-status">حالة الدفع</th>
      </tr>
    </thead>
    <tbody>
      ${lineItems.length > 0 ? linesHtml : '<tr><td colspan="4" style="text-align:center;color:#9ca3af;padding:16px">لا توجد خدمات مسجلة</td></tr>'}
    </tbody>
  </table>
  <div class="totals">
    <div class="total-row grand"><span>الإجمالي الكلي</span><span>${totalAmount.toLocaleString('ar-EG')} ج</span></div>
    ${paidAmount > 0 ? `<div class="total-row paid-row"><span>✅ المدفوع</span><span>${paidAmount.toLocaleString('ar-EG')} ج</span></div>` : ''}
    ${pendingAmount > 0 ? `<div class="total-row pending-row"><span>⏳ المتبقي</span><span>${pendingAmount.toLocaleString('ar-EG')} ج</span></div>` : ''}
  </div>
  <div class="footer-section">${settings?.receiptFooter ?? ''}</div>
  <div class="sig-row">
    <div class="sig-box">توقيع المريض</div>
    <div class="sig-box">ختم العيادة</div>
  </div>
</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  }
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Helper function to get the correct image URL
 * Handles both Firebase Storage URLs (full URLs) and legacy local paths
 */
const getImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return '';
  if (/^(https?:|blob:|data:)/.test(imageUrl)) {
    return imageUrl;
  }
  return `${API.BASE_URL}${imageUrl}`;
};

/** Returns true if the URL points to a PDF file */
const isPdf = (url: string): boolean => {
  if (!url) return false;
  // Firebase Storage URLs encode the path — decode to check extension
  try {
    const decoded = decodeURIComponent(url);
    return decoded.toLowerCase().includes('.pdf');
  } catch {
    return url.toLowerCase().includes('.pdf');
  }
};

function buildUploadedReportWhatsAppHtml(
  report: any,
  patient: { patient_name?: string; age?: string | number } | null,
  latestDiagnosis: string
): string | null {
  const fileUrl = getImageUrl(report.image_url || '');
  if (!fileUrl) return null;
  const reportDate = report.uploaded_at
    ? moment(report.uploaded_at).format('DD-MM-YYYY')
    : moment().format('DD-MM-YYYY');
  const reportLabel = report.report_type
    ? report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)
    : 'Report';
  const diagnosisBlock = latestDiagnosis
    ? `<div class="diag"><strong>Latest diagnosis / آخر تشخيص:</strong><br/>${latestDiagnosis.replace(/\n/g, '<br/>')}</div>`
    : '';
  const contentBlock = isPdf(fileUrl)
    ? `<p style="text-align:center;margin:16px 0"><a href="${fileUrl}" style="font-size:15px;font-weight:600;color:#1677ff">Open report (PDF) / فتح التقرير</a></p>`
    : `<img src="${fileUrl}" alt="${reportLabel}" />`;
  return `<!DOCTYPE html><html lang="ar" dir="rtl"><head>
<meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${reportLabel}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;background:#f0f2f5;padding:16px}
  .card{background:#fff;border-radius:12px;padding:16px;max-width:500px;margin:0 auto;box-shadow:0 2px 12px rgba(0,0,0,.08)}
  .header{text-align:center;margin-bottom:12px;font-size:13px;color:#555}
  .diag{margin-bottom:12px;font-size:13px;color:#333;text-align:center;line-height:1.5}
  img{max-width:100%;display:block;margin:0 auto}
</style></head><body><div class="card">
  <div class="header">
    <strong>${patient?.patient_name || ''}</strong>
    ${patient?.age ? ` &nbsp;|&nbsp; Age: ${patient.age}` : ''}
    &nbsp;|&nbsp; ${reportLabel} &nbsp;|&nbsp; ${reportDate}
  </div>
  ${diagnosisBlock}
  ${contentBlock}
</div></body></html>`;
}

function buildCombinedUploadedReportsWhatsAppHtml(
  reports: any[],
  patient: { patient_name?: string; age?: string | number } | null,
  latestDiagnosis: string
): string {
  const reportSections = reports.map((report, index) => {
    const fileUrl = getImageUrl(report.image_url || '');
    const reportDate = report.uploaded_at
      ? moment(report.uploaded_at).format('DD-MM-YYYY')
      : moment().format('DD-MM-YYYY');
    const reportLabel = report.report_type
      ? report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)
      : 'Report';
    const content = isPdf(fileUrl)
      ? `<p class="pdf"><a href="${fileUrl}">Open report (PDF) / فتح التقرير</a></p>`
      : `<img src="${fileUrl}" alt="${reportLabel}" />`;
    return `<section><h2>${index + 1}. ${reportLabel} <small>${reportDate}</small></h2>${content}</section>`;
  }).join('');
  const diagnosisBlock = latestDiagnosis
    ? `<div class="diag"><strong>Latest diagnosis / آخر تشخيص:</strong><br/>${latestDiagnosis.replace(/\n/g, '<br/>')}</div>`
    : '';

  return `<!DOCTYPE html><html lang="ar" dir="rtl"><head>
<meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Patient reports</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,sans-serif;background:#f0f2f5;padding:16px;margin:0}
  main{max-width:700px;margin:0 auto}.header,.diag,section{background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 2px 12px rgba(0,0,0,.08)}
  .header,.diag{text-align:center}.header{font-size:14px;color:#555}.diag{font-size:13px;line-height:1.6}
  h2{font-size:16px;margin:0 0 12px;color:#222}small{font-size:12px;color:#777;font-weight:normal}
  img{max-width:100%;display:block;margin:0 auto}.pdf{text-align:center;margin:24px 0}.pdf a{font-size:15px;font-weight:600;color:#1677ff}
</style></head><body><main><div class="header"><strong>${patient?.patient_name || ''}</strong>${patient?.age ? ` &nbsp;|&nbsp; Age: ${patient.age}` : ''} &nbsp;|&nbsp; ${reports.length} reports</div>${diagnosisBlock}${reportSections}</main></body></html>`;
}

interface PatientDetailProps {
  isReceiptModalVisible: boolean;
  setIsReceiptModalVisible: (visible: boolean) => void;
  onPrintReceipt: (receipt: Receipt) => void;
  onBackToList: () => void;
  onGetReceiptHtml?: (receipt: Receipt) => string;
  clinicalHistoryRefreshToken?: number;
}

interface PatientHistoryResponse {
  patient_info: {
    name: string;
    phone: string;
    age: string;
    address: string;
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    totalVisits: number;
  };
  visits: Visit[];
  complaint_history?: { _id?: string; date: string; complaint: string; diagnosis: string; procedure?: string; doctor_id?: string; patient_mongo_id?: string; deletedAt?: string | null; }[];
  voice_notes?: VoiceNote[];
}

const isSoftDeleted = (item: any): boolean => Boolean(item?.deletedAt || item?.deleted_at || item?.isDeleted);
const getActiveReceipts = (receipts?: any[]): any[] => (receipts || []).filter(receipt => !isSoftDeleted(receipt));

const PatientDetail: React.FC<PatientDetailProps> = ({
  isReceiptModalVisible,
  setIsReceiptModalVisible,
  onPrintReceipt,
  onBackToList,
  onGetReceiptHtml,
  clinicalHistoryRefreshToken = 0,
}) => {
  const { isViewerOnly } = useOwnerContext();
  const { selectedPatient, setSelectedPatient, fetchPatients, clearPatientListCache } = usePatientContext();
  useEffect(() => {
    selectOfflineProfile(selectedPatient);
    if (selectedPatient) void refreshProfileRevision(selectedPatient).catch(() => {});
    return () => selectOfflineProfile(null);
  }, [selectedPatient]);
  const { username } = useAuth();
  const { settings: doctorSettings, doctorName, doctorId } = useDoctorContext();
  const resolvedDoctorNameForWA = username || doctorName || doctorSettings.doctorTitle || '';
  const sessionDoctorId = doctorId || localStorage.getItem('doctorId');
  const { selectedClinicId, clinicScopeIds } = useClinicContext();
  const { language, t, isRTL } = useLanguage();

  const _suggestionDoctorId = selectedPatient?.doctor_id || localStorage.getItem('doctorId') || '';
  const { diagnoses, complaints, saveDiagnosis: saveDiagnosisSuggestion, saveComplaint: saveComplaintSuggestion, filterDiagnoses: filterDiagnosesSuggestion, filterComplaints: filterComplaintsSuggestion } = useDoctorSuggestions(_suggestionDoctorId);
  const CHIP_LIMIT = 15;
  // Track typed complaint/diagnosis to pass to AutoComplete filterOption
  const [complaintInput, setComplaintInput] = useState('');
  const [diagnosisInput, setDiagnosisInput] = useState('');

  const [patientHistory, setPatientHistory] = useState<PatientHistoryResponse | null>(null);
  const [pendingComplaintEntries, setPendingComplaintEntries] = useState<any[]>([]);
  const [pendingPrescriptionReceipts, setPendingPrescriptionReceipts] = useState<any[]>([]);
  const [pendingUploadedReports, setPendingUploadedReports] = useState<any[]>([]);
  const [pendingMedicalReports, setPendingMedicalReports] = useState<any[]>([]);
  const [pendingReferrals, setPendingReferrals] = useState<any[]>([]);
  const [pendingBillings, setPendingBillings] = useState<any[]>([]);
  const [pendingExternalRequests, setPendingExternalRequests] = useState<any[]>([]);
  const [pendingPackages, setPendingPackages] = useState<any[]>([]);
  const [pendingMedicalHistory, setPendingMedicalHistory] = useState<any | null>(null);
  const pendingObjectUrlsRef = React.useRef<string[]>([]);
  const patientHistoryRequestIdRef = React.useRef(0);
  const selectedPatientMongoIdRef = React.useRef(selectedPatient?._id || '');
  const voiceNoteSectionRef = React.useRef<VoiceNoteSectionHandle>(null);
  selectedPatientMongoIdRef.current = selectedPatient?._id || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visitDetailsVisible, setVisitDetailsVisible] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [editReceiptVisible, setEditReceiptVisible] = useState(false);
  const [receiptToEdit, setReceiptToEdit] = useState<any>(null);
  const [editVisitId, setEditVisitId] = useState<string | null>(null);
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const [nextVisitModalVisible, setNextVisitModalVisible] = useState(false);
  const [billingModalVisible, setBillingModalVisible] = useState(false);
  const [discountModalVisible, setDiscountModalVisible] = useState(false);
  const [discountSubmitting, setDiscountSubmitting] = useState(false);
  const [refundAllSubmitting, setRefundAllSubmitting] = useState(false);
  const [billingReceiptLoading, setBillingReceiptLoading] = useState(false);
  const [patientReports, setPatientReports] = useState<any[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');

  const [sendingWA, setSendingWA] = useState<string | null>(null);
  const [sendingReferralWA, setSendingReferralWA] = useState<string | null>(null);
  const [sendingMedicalReportWA, setSendingMedicalReportWA] = useState<string | null>(null);
  const [sendingUploadedReportWA, setSendingUploadedReportWA] = useState<string | null>(null);
  const [selectedUploadedReportIds, setSelectedUploadedReportIds] = useState<string[]>([]);
  const [sendingSelectedReportsWA, setSendingSelectedReportsWA] = useState(false);
  const [sendingExternalServicesWA, setSendingExternalServicesWA] = useState(false);
  const [archivingPatientFile, setArchivingPatientFile] = useState(false);
  const [manualInventoryVisible, setManualInventoryVisible] = useState(false);
  const [insuranceProgramVisible, setInsuranceProgramVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferDoctors, setTransferDoctors] = useState<ClinicDoctorOption[]>([]);
  const [transferDoctorId, setTransferDoctorId] = useState<string>('');
  const [transferReason, setTransferReason] = useState<string>('');
  const [transferDoctorsLoading, setTransferDoctorsLoading] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    const refreshPendingPrescriptions = async () => {
      const patientMongoId = selectedPatient?._id;
      if (!patientMongoId) {
        if (active) {
          setPendingComplaintEntries([]);
          setPendingPrescriptionReceipts([]);
          setPendingUploadedReports([]);
          setPendingMedicalReports([]);
          setPendingReferrals([]);
          setPendingBillings([]);
          setPendingExternalRequests([]);
          setPendingPackages([]);
          setPendingMedicalHistory(null);
        }
        return;
      }
      const actions = await pendingProfileActions();
      if (!active) return;
      pendingObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      pendingObjectUrlsRef.current = [];
      const patientActions = actions.filter((action: any) => action.request?.patientMongoId === patientMongoId);
      const directPath = `/api/patients/${patientMongoId}`;
      const pendingMarker = (action: any) => ({
        _offlinePending: true,
        _offlineStatus: action.status,
        _offlineOperationId: action.operationId,
      });
      setPendingComplaintEntries(patientActions
        .filter((action: any) => action.request?.method === 'POST' && action.request?.path === '/api/patients/complaint-history')
        .map((action: any) => ({
          ...action.request.body,
          _id: `offline_complaint_${action.operationId}`,
          date: action.request.body?.date || new Date(action.createdAt).toISOString(),
          ...pendingMarker(action),
        })));
      setPendingPrescriptionReceipts(patientActions
        .filter((action: any) => action.request?.patientMongoId === patientMongoId &&
          action.request?.method === 'PUT' && action.request?.path === directPath &&
          (action.request?.body?.prescriptionTemplate === 'physician' ||
            action.request?.body?.drugs?.length || action.request?.body?.notes?.trim() ||
            action.request?.body?.investigations?.length ||
            Object.values(action.request?.body?.fetalAssessment || {}).some(value => String(value || '').trim())))
        .map((action: any) => ({
          ...action.request.body,
          _id: `offline_receipt_${action.operationId}`,
          receipt_id: `offline_receipt_${action.operationId}`,
          date: action.request.body.date || new Date(action.createdAt).toISOString(),
          visit_type: selectedPatient.visit_type || '',
          doctor_name: doctorName || username || '',
          ...pendingMarker(action),
        })));

      setPendingUploadedReports(patientActions
        .filter((action: any) => action.request?.path === '/api/patients/reports/upload-urls' && action.attachments?.length)
        .flatMap((action: any) => action.attachments.map((file: any, index: number) => {
          const imageUrl = file.url || URL.createObjectURL(file.blob);
          if (!file.url) pendingObjectUrlsRef.current.push(imageUrl);
          return {
            report_id: `offline_report_${action.operationId}_${index}`,
            image_url: imageUrl,
            report_type: action.request.body?.report_type || 'examination',
            description: action.request.body?.description || file.name || '',
            uploaded_at: new Date(action.createdAt).toISOString(),
            _offlineMimeType: file.blob?.type || '',
            ...pendingMarker(action),
          };
        })));
      setPendingMedicalReports(patientActions
        .filter((action: any) => action.request?.method === 'POST' && action.request?.path.startsWith('/api/medical-reports/patient/'))
        .map((action: any) => ({ ...action.request.body, report_id: `offline_medical_report_${action.operationId}`,
          date: new Date(action.createdAt).toISOString(), ...pendingMarker(action) })));
      setPendingReferrals(patientActions
        .filter((action: any) => action.request?.method === 'POST' && action.request?.path.startsWith('/api/referrals/patient/'))
        .map((action: any) => ({ ...action.request.body, referral_id: `offline_referral_${action.operationId}`,
          ...pendingMarker(action) })));
      setPendingBillings(patientActions
        .filter((action: any) => action.request?.method === 'POST' && action.request?.path === '/api/billing')
        .map((action: any) => ({ ...action.request.body, billing_id: `offline_billing_${action.operationId}`,
          billingDate: action.request.body?.billingDate || new Date(action.createdAt).toISOString(), ...pendingMarker(action) })));
      setPendingExternalRequests(patientActions
        .filter((action: any) => action.request?.method === 'POST' && action.request?.path === '/api/external-services/requests')
        .map((action: any) => ({ ...action.request.body, request_id: `offline_external_${action.operationId}`,
          status: 'pending', requestedAt: new Date(action.createdAt).toISOString(), ...pendingMarker(action) })));
      setPendingPackages(patientActions
        .filter((action: any) => action.request?.method === 'POST' && action.request?.path === '/api/patient-packages')
        .map((action: any) => ({ ...action.request.body, patient_package_id: `offline_package_${action.operationId}`,
          package_name: action.request.body?.package_name || action.request.body?.package_id || 'Package',
          status: 'active', ...pendingMarker(action) })));
      const historyAction = [...patientActions].reverse().find((action: any) =>
        action.request?.method === 'POST' && action.request?.path === '/api/medical-history/patient');
      setPendingMedicalHistory(historyAction ? {
        data: historyAction.request.body?.data || {},
        template_snapshot: historyAction.request.body?.template_snapshot || null,
        ...pendingMarker(historyAction),
      } : null);
    };
    void refreshPendingPrescriptions().catch(() => {});
    window.addEventListener('offlineProfileChanged', refreshPendingPrescriptions);
    window.addEventListener('offlineClinicalApplied', refreshPendingPrescriptions);
    return () => {
      active = false;
      window.removeEventListener('offlineProfileChanged', refreshPendingPrescriptions);
      window.removeEventListener('offlineClinicalApplied', refreshPendingPrescriptions);
      pendingObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      pendingObjectUrlsRef.current = [];
    };
  }, [selectedPatient?._id, selectedPatient?.visit_type, doctorName, username]);

  useEffect(() => {
    setSelectedUploadedReportIds([]);
  }, [selectedPatient?._id]);

  const buildReportParams = (report?: any): Record<string, string> => {
    if (!selectedPatient) return {};
    const params: Record<string, string> = {
      patient_id: selectedPatient.patient_id,
      patient_phone: selectedPatient.patient_phone,
      doctor_id: report?.doctor_id || selectedPatient.doctor_id,
    };
    const mongoId = report?.patient_mongo_id || selectedPatient._id;
    if (mongoId) params.mongo_id = mongoId;
    const fn = selectedPatient.file_number || selectedPatient.file_id;
    if (fn) params.file_number = String(fn).trim();
    return params;
  };

  const handleDeleteUploadedReport = (report: any) => {
    if (!selectedPatient || !report?.report_id) return;
    Modal.confirm({
      title: 'Delete this report?',
      content: 'This removes the report from the patient record and deletes the Firebase file when available.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setDeletingReportId(report.report_id);
          const response = await axios.delete(
            `${API.BASE_URL}/api/patients/reports/${report.report_id}`,
            { params: buildReportParams(report) }
          );
          if (response.data?.success) {
            message.success('Report deleted successfully');
            setPatientReports((prev) => prev.filter((item) => item.report_id !== report.report_id));
            setSelectedUploadedReportIds((prev) => prev.filter((id) => id !== report.report_id));
          } else {
            message.error(response.data?.message || 'Failed to delete report');
          }
        } catch (error: any) {
          console.error('Error deleting report:', error);
          message.error(error?.response?.data?.message || 'Failed to delete report');
        } finally {
          setDeletingReportId(null);
        }
      }
    });
  };

  const handleArchivePatientFile = () => {
    if (!selectedPatient?._id) return;
    Modal.confirm({
      title: 'Delete patient file from the patient list?',
      content: 'The file will disappear from the patient list. Visits, reports, billing, packages, and uploaded files will not be deleted.',
      okText: 'Soft delete file',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setArchivingPatientFile(true);
          await axios.patch(`${API.BASE_URL}${API.ENDPOINTS.ARCHIVE_PATIENT(selectedPatient._id)}`);
          message.success('Patient file archived successfully');
          setSelectedPatient(null);
          await fetchPatients({ forceRefresh: true });
          onBackToList();
        } catch (error: any) {
          message.error(error?.response?.data?.message || 'Failed to archive patient file');
          throw error;
        } finally {
          setArchivingPatientFile(false);
        }
      }
    });
  };

  const sendPrescriptionViaWhatsApp = async (receipt: Receipt) => {
    if (!selectedPatient?._id) return;
    const parentVisit = selectedPatient.visits?.find((v: any) =>
      v?.receipts?.some((r: any) => r?._id === receipt._id)
    );
    const parentVisitId = (parentVisit as any)?._id || (parentVisit as any)?.visit_id;
    setSendingWA(receipt._id);
    try {
      let firebaseUrl: string | undefined;
      if (onGetReceiptHtml) {
        try {
          const html = applyWhatsAppLogo(onGetReceiptHtml(receipt), doctorSettings);
          const path = `whatsapp/prescriptions/${selectedPatient._id}_${receipt._id}.html`;
          firebaseUrl = await uploadHtmlToFirebaseStorage(html, path);
        } catch (uploadErr: any) {
          console.warn('[WA] Firebase upload failed for prescription, backend will generate:', uploadErr?.message);
        }
      }
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.WHATSAPP_SEND_PRESCRIPTION(selectedPatient._id)}`,
        {
          receipt_id: receipt._id,
          doctor_name: resolvedDoctorNameForWA,
          ...(parentVisitId ? { visit_id: parentVisitId } : {}),
          ...(firebaseUrl ? { firebase_url: firebaseUrl } : {}),
        }
      );
      message.success(`✅ Prescription sent to ${selectedPatient.patient_phone} via WhatsApp`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingWA(null);
    }
  };

  const sendReferralViaWhatsApp = async (ref: any) => {
    if (!selectedPatient?._id) return;
    const key = ref.referral_id || ref._id;
    setSendingReferralWA(key);
    try {
      let pdfUrl: string | undefined;
      try {
        const html = applyWhatsAppLogo(buildReferralLetterHtml(ref), doctorSettings);
        const path = `whatsapp/referrals/${selectedPatient._id}_${ref.referral_id || ref._id}.html`;
        pdfUrl = await uploadHtmlToFirebaseStorage(html, path);
      } catch (uploadErr: any) {
        console.warn('[WA] Firebase upload failed for referral, backend will generate:', uploadErr?.message);
      }
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.WHATSAPP_SEND_REFERRAL(selectedPatient._id)}`,
        { referral_id: ref.referral_id, doctor_name: resolvedDoctorNameForWA, ...(pdfUrl ? { pdf_url: pdfUrl } : {}) }
      );
      message.success(`✅ Referral sent to ${selectedPatient.patient_phone} via WhatsApp`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingReferralWA(null);
    }
  };

  const sendMedicalReportViaWhatsApp = async (report: any) => {
    if (!selectedPatient?._id) return;
    const key = report.report_id || report._id;
    setSendingMedicalReportWA(key);
    try {
      let pdfUrl: string | undefined;
      try {
        const html = applyWhatsAppLogo(buildSavedMedicalReportHtml(report), doctorSettings);
        const path = `whatsapp/medical-reports/${selectedPatient._id}_${report.report_id || report._id}.html`;
        pdfUrl = await uploadHtmlToFirebaseStorage(html, path);
      } catch (uploadErr: any) {
        console.warn('[WA] Firebase upload failed for medical report, backend will generate:', uploadErr?.message);
      }
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.WHATSAPP_SEND_MEDICAL_REPORT(selectedPatient._id)}`,
        { report_id: report.report_id, doctor_name: resolvedDoctorNameForWA, ...(pdfUrl ? { pdf_url: pdfUrl } : {}) }
      );
      message.success(`✅ Medical report sent to ${selectedPatient.patient_phone} via WhatsApp`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingMedicalReportWA(null);
    }
  };

  const sendUploadedReportViaWhatsApp = async (report: any) => {
    if (!selectedPatient?._id) return;
    const key = report.report_id || report._id;
    setSendingUploadedReportWA(key);
    try {
      let pdfUrl: string | undefined;
      try {
        const reportHtml = buildUploadedReportWhatsAppHtml(report, selectedPatient, latestVisitDiagnosis);
        const html = reportHtml ? applyWhatsAppLogo(reportHtml, doctorSettings) : null;
        if (html) {
          const path = `whatsapp/uploaded-reports/${selectedPatient._id}_${report.report_id || report._id}.html`;
          pdfUrl = await uploadHtmlToFirebaseStorage(html, path);
        }
      } catch (uploadErr: any) {
        console.warn('[WA] Firebase upload failed for uploaded report, backend will send file link:', uploadErr?.message);
      }
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.WHATSAPP_SEND_UPLOADED_REPORT(selectedPatient._id)}`,
        {
          report_id: report.report_id,
          doctor_name: resolvedDoctorNameForWA,
          ...(pdfUrl ? { pdf_url: pdfUrl } : {}),
        }
      );
      message.success(`✅ Report sent to ${selectedPatient.patient_phone} via WhatsApp`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingUploadedReportWA(null);
    }
  };

  const sendSelectedUploadedReportsViaWhatsApp = async () => {
    if (!selectedPatient?._id || selectedUploadedReportIds.length === 0) return;
    const selectedReports = patientReports.filter((report) =>
      selectedUploadedReportIds.includes(report.report_id || report._id)
    );
    if (selectedReports.length === 0) return;

    setSendingSelectedReportsWA(true);
    try {
      const html = applyWhatsAppLogo(
        buildCombinedUploadedReportsWhatsAppHtml(selectedReports, selectedPatient, latestVisitDiagnosis),
        doctorSettings
      );
      const path = `whatsapp/uploaded-reports/${selectedPatient._id}_combined_${Date.now()}.html`;
      const pdfUrl = await uploadHtmlToFirebaseStorage(html, path);
      const firstReport = selectedReports[0];
      const reportPatientMongoId = firstReport.patient_mongo_id || selectedPatient._id;
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.WHATSAPP_SEND_UPLOADED_REPORT(reportPatientMongoId)}`,
        {
          report_id: firstReport.report_id,
          doctor_name: resolvedDoctorNameForWA,
          pdf_url: pdfUrl,
        }
      );
      message.success(`✅ ${selectedReports.length} reports sent in one WhatsApp link`);
      setSelectedUploadedReportIds([]);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to send selected reports via WhatsApp');
    } finally {
      setSendingSelectedReportsWA(false);
    }
  };

  const sendExternalServicesViaWhatsApp = async () => {
    if (!selectedPatient?._id || selectedExternalRequestIdsForWA.length === 0) return;
    const selectedRequests = externalRequests.filter((request) =>
      selectedExternalRequestIdsForWA.includes(request.request_id)
    );
    if (selectedRequests.length === 0) return;

    setSendingExternalServicesWA(true);
    try {
      let firebaseUrl: string | undefined;
      try {
        const externalServicesHtml = buildExternalServicesWhatsAppHtml(selectedRequests);
        const html = externalServicesHtml ? applyWhatsAppLogo(externalServicesHtml, doctorSettings) : null;
        if (html) {
          const path = `whatsapp/external-services/${selectedPatient._id}_${Date.now()}.html`;
          firebaseUrl = await uploadHtmlToFirebaseStorage(html, path);
        }
      } catch (uploadErr: any) {
        console.error('[WA] Failed to prepare selected external services:', uploadErr);
        throw new Error('Could not prepare the selected services. Nothing was sent. Please try again.');
      }
      if (!firebaseUrl) throw new Error('Could not prepare the selected services. Nothing was sent. Please try again.');
      await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.WHATSAPP_SEND_EXTERNAL_SERVICES(selectedPatient._id)}`,
        {
          doctor_name: resolvedDoctorNameForWA,
          pdf_url: firebaseUrl,
          firebase_url: firebaseUrl
        }
      );
      message.success(`✅ ${selectedRequests.length} external service${selectedRequests.length === 1 ? '' : 's'} sent to ${selectedPatient.patient_phone} via WhatsApp`);
      setExternalServicesWhatsAppModalVisible(false);
      setSelectedExternalRequestIdsForWA([]);
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Failed to send WhatsApp message');
    } finally {
      setSendingExternalServicesWA(false);
    }
  };

  // External Services state
  const [externalServices, setExternalServices] = useState<ExternalService[]>([]);
  const [externalRequests, setExternalRequests] = useState<ExternalServiceRequest[]>([]);
  const [externalServicesLoading, setExternalServicesLoading] = useState(false);
  const [deletingExternalRequestId, setDeletingExternalRequestId] = useState<string | null>(null);
  const [assignServiceModalVisible, setAssignServiceModalVisible] = useState(false);
  const [selectedExternalServiceIds, setSelectedExternalServiceIds] = useState<string[]>([]);
  const [assignExternalServicesLoading, setAssignExternalServicesLoading] = useState(false);
  const [addServiceFormVisible, setAddServiceFormVisible] = useState(false);
  const [addServiceLoading, setAddServiceLoading] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [newProviderName, setNewProviderName] = useState('');
  const [printSelectModalVisible, setPrintSelectModalVisible] = useState(false);
  const [selectedPrintIds, setSelectedPrintIds] = useState<string[]>([]);
  const [externalServicesWhatsAppModalVisible, setExternalServicesWhatsAppModalVisible] = useState(false);
  const [selectedExternalRequestIdsForWA, setSelectedExternalRequestIdsForWA] = useState<string[]>([]);
  const [patientBillings, setPatientBillings] = useState<any[]>([]);
  const [patientBillingsLoading, setPatientBillingsLoading] = useState(false);
  const [patientVitals, setPatientVitals] = useState<PatientVitals[]>([]);
  const [patientVitalsLoading, setPatientVitalsLoading] = useState(false);
  const [deletingPrescriptionDrugKey, setDeletingPrescriptionDrugKey] = useState<string | null>(null);
  const [deletingPrescriptionId, setDeletingPrescriptionId] = useState<string | null>(null);

  useEffect(() => {
    setExternalServicesWhatsAppModalVisible(false);
    setSelectedExternalRequestIdsForWA([]);
  }, [selectedPatient?._id]);

  // Complaint history add form state
  const [addComplaintVisible, setAddComplaintVisible] = useState(false);
  const [newComplaint, setNewComplaint] = useState('');
  const [newDiagnosis, setNewDiagnosis] = useState('');
  const [newProcedure, setNewProcedure] = useState('');
  const [addComplaintLoading, setAddComplaintLoading] = useState(false);

  // Complaint history edit modal state
  const [editComplaintEntry, setEditComplaintEntry] = useState<{ _id: string; complaint: string; diagnosis: string; procedure: string; doctor_id?: string; patient_mongo_id?: string } | null>(null);
  const [editComplaintValue, setEditComplaintValue] = useState('');
  const [editDiagnosisValue, setEditDiagnosisValue] = useState('');
  const [editProcedureValue, setEditProcedureValue] = useState('');
  const [editComplaintLoading, setEditComplaintLoading] = useState(false);

  // Medical History state
  const [medicalHistoryModalVisible, setMedicalHistoryModalVisible] = useState(false);

  // Medical Report state
  const [medicalReportModalVisible, setMedicalReportModalVisible] = useState(false);
  const [patientMedicalReports, setPatientMedicalReports] = useState<any[]>([]);
  const [medicalReportsLoading, setMedicalReportsLoading] = useState(false);

  // Referral state
  const [referralModalVisible, setReferralModalVisible] = useState(false);
  const [uploadReportModalVisible, setUploadReportModalVisible] = useState(false);
  const [patientReferrals, setPatientReferrals] = useState<any[]>([]);

  // Diagram modal state
  const [diagramModalVisible, setDiagramModalVisible] = useState(false);
  const [diagramVisitId, setDiagramVisitId] = useState<string | null>(null);
  const [diagramInitialData, setDiagramInitialData] = useState<DiagramData | null>(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralToEdit, setReferralToEdit] = useState<any | null>(null);
  const [medicalReportToEdit, setMedicalReportToEdit] = useState<any | null>(null);

  // Address inline editing
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);

  // Visit Type Editing
  const [editingVisitType, setEditingVisitType] = useState(false);
  const [availableVisitTypes, setAvailableVisitTypes] = useState<any[]>([]);
  const [selectedVisitTypeId, setSelectedVisitTypeId] = useState<string>('');
  const [selectedUrgency, setSelectedUrgency] = useState<string>('normal');
  const [savingVisitType, setSavingVisitType] = useState(false);

  // Package state
  const [activePackages, setActivePackages] = useState<PatientPackage[]>([]);
  const [allPatientPackages, setAllPatientPackages] = useState<PatientPackage[]>([]);
  const [activePackagePopupVisible, setActivePackagePopupVisible] = useState(false);
  const activePackagePopupPatientRef = React.useRef('');
  const [assignPackageModalVisible, setAssignPackageModalVisible] = useState(false);
  const [assignPackageSubmitting, setAssignPackageSubmitting] = useState(false);
  const [doctorPackagesForAssign, setDoctorPackagesForAssign] = useState<any[]>([]);
  const [selectedPackageToAssign, setSelectedPackageToAssign] = useState<string | null>(null);
  // Packages catalog is stored on the clinic owner's doctor profile; child
  // doctors in the same clinic share that catalog when assigning packages.
  const [ownerDoctorIdForClinic, setOwnerDoctorIdForClinic] = useState<string>('');

  // Medical history (examination) data — fetched on patient load, included in lab print
  const [medicalHistoryData, setMedicalHistoryData] = useState<{ data: Record<string, string>; template_snapshot: any } | null>(null);

  // Resolve the clinic owner's doctor_id (where the package catalog lives).
  // Linked doctors share the owner's catalog instead of using their own.
  useEffect(() => {
    let cancelled = false;
    const clinicId = selectedClinicId || selectedPatient?.clinic_id || localStorage.getItem('clinicId') || '';
    const fallbackDoctorId = sessionDoctorId || selectedPatient?.doctor_id || '';
    if (!clinicId && !fallbackDoctorId) {
      setOwnerDoctorIdForClinic('');
      return;
    }
    getClinicOwnerDoctorId(clinicId, fallbackDoctorId)
      .then((ownerId) => {
        if (!cancelled) setOwnerDoctorIdForClinic(ownerId || fallbackDoctorId || '');
      })
      .catch(() => {
        if (!cancelled) setOwnerDoctorIdForClinic(fallbackDoctorId || '');
      });
    return () => { cancelled = true; };
  }, [selectedClinicId, selectedPatient?.clinic_id, selectedPatient?.doctor_id, sessionDoctorId]);

  // Callable on-demand (e.g. after editing a receipt) — returns the fresh data
  const fetchPatientHistory = React.useCallback(async () => {
    if (!selectedPatient) return undefined;
    const requestId = ++patientHistoryRequestIdRef.current;
    const requestedPatientMongoId = selectedPatient._id || '';
    try {
      setLoading(true);
      setError(null);

      // CLINIC SCOPE: When enabled, pass clinic_ids + use_clinic_scope so the backend
      // merges visit history from all doctors/branches in the center.
      const visitParams = buildPatientHistoryParams(selectedPatient, clinicScopeIds || []);

      const response = await axios.get<PatientHistoryResponse>(`${API.BASE_URL}/api/patients/visits`, {
        params: visitParams
      });

      if (requestId !== patientHistoryRequestIdRef.current || selectedPatientMongoIdRef.current !== requestedPatientMongoId) {
        return undefined;
      }
      setPatientHistory(response.data);
      return response.data;
    } catch (historyError) {
      if (requestId === patientHistoryRequestIdRef.current && selectedPatientMongoIdRef.current === requestedPatientMongoId) {
        console.error('Error fetching patient history:', historyError);
        setError('Failed to fetch patient history');
      }
    } finally {
      if (requestId === patientHistoryRequestIdRef.current) setLoading(false);
    }
  }, [selectedPatient, clinicScopeIds]);

  useEffect(() => {
    if (clinicalHistoryRefreshToken > 0) fetchPatientHistory();
  }, [clinicalHistoryRefreshToken, fetchPatientHistory]);

  useEffect(() => {
    const refreshAppliedEntry = (event: Event) => {
      if ((event as CustomEvent).detail?.patientMongoId === selectedPatient?._id) {
        void fetchPatientHistory();
        setSelectedPatient((current: any) => current ? { ...current } : current);
      }
    };
    const reconnect = () => { if (selectedPatient) setSelectedPatient((current: any) => current ? { ...current } : current); };
    window.addEventListener('offlineClinicalApplied', refreshAppliedEntry);
    window.addEventListener('online', reconnect);
    return () => { window.removeEventListener('offlineClinicalApplied', refreshAppliedEntry); window.removeEventListener('online', reconnect); };
  }, [selectedPatient?._id, fetchPatientHistory]);

  const fetchPatientBillings = React.useCallback(async () => {
    if (!selectedPatient) return;
    const billingDoctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId');
    const patientId = selectedPatient.patient_id || (selectedPatient as any).id || selectedPatient._id;
    if (!billingDoctorId || !patientId) return;

    try {
      setPatientBillingsLoading(true);
      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_BILLINGS(billingDoctorId)}`, {
        params: {
          patient_id: patientId,
          patient_phone: selectedPatient.patient_phone || '',
          patient_file_number: selectedPatient.file_number || selectedPatient.file_id || '',
          clinic_id: selectedClinicId || selectedPatient.clinic_id || '',
          use_clinic_scope: selectedClinicId || selectedPatient.clinic_id ? 'true' : 'false',
          limit: 500
        }
      });
      if (response.data?.success) {
        setPatientBillings(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching patient service history:', error);
      setPatientBillings([]);
    } finally {
      setPatientBillingsLoading(false);
    }
  }, [selectedPatient, selectedClinicId]);

  const fetchPatientVitals = React.useCallback(async () => {
    if (!selectedPatient?.patient_id) {
      setPatientVitals([]);
      return;
    }
    const doctorIdForVitals = selectedPatient.doctor_id || localStorage.getItem('doctorId') || '';
    if (!doctorIdForVitals) {
      setPatientVitals([]);
      return;
    }

    try {
      setPatientVitalsLoading(true);
      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_VITALS(selectedPatient.patient_id)}`, {
        params: {
          doctor_id: doctorIdForVitals,
          clinic_id: selectedClinicId || selectedPatient.clinic_id || '',
        }
      });
      if (response.data?.success) {
        setPatientVitals(response.data.data || []);
      } else {
        setPatientVitals([]);
      }
    } catch (error) {
      console.error('Error fetching patient vitals:', error);
      setPatientVitals([]);
    } finally {
      setPatientVitalsLoading(false);
    }
  }, [selectedPatient, selectedClinicId]);

  const fetchExternalRequests = React.useCallback(async () => {
    if (!selectedPatient) return;
    try {
      setExternalServicesLoading(true);
      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_EXTERNAL_REQUESTS(selectedPatient.patient_id)}`, {
        params: { doctorId: selectedPatient.doctor_id }
      });
      if (response.data.success) {
        setExternalRequests(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching external requests:', error);
    } finally {
      setExternalServicesLoading(false);
    }
  }, [selectedPatient]);

  const handleDeleteComplaintHistory = (record: any) => {
    if (!selectedPatient || record.source !== 'history' || !record._key) return;

    Modal.confirm({
      title: 'Delete this diagnosis entry?',
      content: 'This removes the complaint/diagnosis row from the patient file.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const entryDoctorId = record.doctor_id || selectedPatient.doctor_id || localStorage.getItem('doctorId');
          await axios.delete(`${API.BASE_URL}/api/patients/complaint-history/${record._key}`, {
            params: {
              ...buildComplaintIdentityPayload(
                { ...selectedPatient, doctor_id: entryDoctorId || '' },
                record.patient_mongo_id,
              ),
            }
          });
          const refreshed = await fetchPatientHistory();
          message.success(refreshed ? 'Diagnosis entry deleted' : 'Entry deleted, but the file could not refresh');
        } catch (deleteError: any) {
          message.error(deleteError?.response?.data?.message || 'Failed to delete diagnosis entry');
          throw deleteError;
        }
      }
    });
  };

  const handleAssignSelectedExternalServices = async () => {
    if (!selectedPatient || selectedExternalServiceIds.length === 0) return;

    const selectedServices = externalServices.filter(service =>
      selectedExternalServiceIds.includes(service.service_id)
    );
    if (selectedServices.length === 0) return;

    try {
      setAssignExternalServicesLoading(true);
      const results = await Promise.allSettled(selectedServices.map(service =>
        axios.post(`${API.BASE_URL}${API.ENDPOINTS.EXTERNAL_REQUESTS}`, {
          doctor_id: selectedPatient.doctor_id,
          patient_id: selectedPatient.patient_id,
          patient_name: selectedPatient.patient_name,
          external_service_id: service.service_id,
          service_name: service.service_name,
          provider_name: service.provider_name
        })
      ));

      const failed = results.find((result): result is PromiseRejectedResult =>
        result.status === 'rejected' && !isProfileQueued(result.reason));
      if (failed) throw failed.reason;

      const queuedCount = results.filter(result =>
        result.status === 'rejected' && isProfileQueued(result.reason)).length;

      setAssignServiceModalVisible(false);
      setAddServiceFormVisible(false);
      setSelectedExternalServiceIds([]);
      setNewServiceName('');
      setNewProviderName('');

      if (queuedCount > 0) {
        message.warning(`${queuedCount} service${queuedCount === 1 ? '' : 's'} saved on this device — pending sync.`);
        return;
      }

      message.success(`${selectedServices.length} service${selectedServices.length === 1 ? '' : 's'} assigned`);
      await fetchExternalRequests();
    } catch (error) {
      if (isProfileQueued(error)) {
        setAssignServiceModalVisible(false);
        setAddServiceFormVisible(false);
        setSelectedExternalServiceIds([]);
        setNewServiceName('');
        setNewProviderName('');
        message.warning('Saved on this device — pending sync.');
        return;
      }
      console.error('Error assigning services:', error);
      message.error('Failed to assign selected services');
    } finally {
      setAssignExternalServicesLoading(false);
    }
  };

  const handleDeleteExternalRequest = (request: ExternalServiceRequest) => {
    if (!selectedPatient || !request.request_id) return;

    Modal.confirm({
      title: isRTL ? 'حذف هذه الخدمة الخارجية؟' : 'Remove this external service?',
      content: isRTL
        ? `سيتم حذف ${request.service_name} من ملف هذا المريض فقط.`
        : `${request.service_name} will be removed from this patient only.`,
      okText: isRTL ? 'حذف' : 'Remove',
      okType: 'danger',
      cancelText: isRTL ? 'إلغاء' : 'Cancel',
      onOk: async () => {
        try {
          setDeletingExternalRequestId(request.request_id);
          await axios.delete(
            `${API.BASE_URL}${API.ENDPOINTS.EXTERNAL_REQUEST_DELETE(request.request_id)}`,
            {
              params: {
                doctorId: selectedPatient.doctor_id,
                patientId: selectedPatient.patient_id,
              }
            }
          );
          setExternalRequests(previous => previous.filter(item => item.request_id !== request.request_id));
          message.success(isRTL ? 'تم حذف الخدمة الخارجية' : 'External service removed');
        } catch (error: any) {
          message.error(error?.response?.data?.message || (isRTL ? 'فشل حذف الخدمة' : 'Failed to remove service'));
          throw error;
        } finally {
          setDeletingExternalRequestId(null);
        }
      }
    });
  };

  const getLatestOwnedVisit = (): Visit | null => {
    const visits = [...(selectedPatient?.visits || [])].filter((visit) => visit?.visit_id || visit?._id);
    visits.sort((a, b) => {
      const aTime = new Date(a.date || 0).getTime();
      const bTime = new Date(b.date || 0).getTime();
      if (aTime !== bTime) return bTime - aTime;
      return String(b.time || '').localeCompare(String(a.time || ''));
    });
    return visits[0] || null;
  };

  const openTransferModal = async () => {
    const clinicId = selectedClinicId || selectedPatient?.clinic_id || localStorage.getItem('clinicId') || '';
    const sourceDoctorId = selectedPatient?.doctor_id || sessionDoctorId || '';
    const visit = getLatestOwnedVisit();
    if (!selectedPatient?._id || !clinicId || !sourceDoctorId || !visit) {
      message.warning(language === 'ar' ? 'لا توجد زيارة حالية يمكن تحويلها' : 'There is no current visit to transfer');
      return;
    }
    if (visit.owner_doctor_id && visit.owner_doctor_id !== sourceDoctorId) {
      message.warning(language === 'ar' ? 'هذه الزيارة محولة بالفعل لطبيب آخر' : 'This visit is already assigned to another doctor');
      return;
    }

    setTransferDoctorsLoading(true);
    try {
      const options = await getClinicDoctorOptions(clinicId, sourceDoctorId, doctorName || '');
      const receivingDoctors = options.filter((doctor) => doctor.id !== sourceDoctorId);
      if (receivingDoctors.length === 0) {
        message.warning(language === 'ar' ? 'لا يوجد طبيب آخر مرتبط بهذه العيادة' : 'No other doctor is linked to this clinic');
        return;
      }
      setTransferDoctors(receivingDoctors);
      setTransferDoctorId('');
      setTransferReason('');
      setTransferModalVisible(true);
    } catch (error) {
      console.error('[visit-transfer] Failed to load clinic doctors:', error);
      message.error(language === 'ar' ? 'تعذر تحميل أطباء العيادة' : 'Could not load clinic doctors');
    } finally {
      setTransferDoctorsLoading(false);
    }
  };

  const handleTransferVisit = async () => {
    if (!selectedPatient?._id || !transferDoctorId || transferSubmitting) return;
    const sourceDoctorId = selectedPatient.doctor_id || sessionDoctorId || '';
    const clinicId = selectedClinicId || selectedPatient.clinic_id || localStorage.getItem('clinicId') || '';
    const visit = getLatestOwnedVisit();
    const visitId = visit?.visit_id || visit?._id || '';
    const receivingDoctor = transferDoctors.find((doctor) => doctor.id === transferDoctorId);
    if (!sourceDoctorId || !clinicId || !visit || !visitId || !receivingDoctor) {
      message.error(language === 'ar' ? 'بيانات التحويل غير مكتملة' : 'Transfer information is incomplete');
      return;
    }

    const operationId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `transfer_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setTransferSubmitting(true);
    try {
      const response = await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.TRANSFER_PATIENT_VISIT(selectedPatient._id)}`,
        {
          source_doctor_id: sourceDoctorId,
          target_doctor_id: receivingDoctor.id,
          target_doctor_name: receivingDoctor.name,
          clinic_id: clinicId,
          visit_id: visitId,
          reason: transferReason,
          operation_id: operationId,
        }
      );
      const transfer = response.data?.data?.transfer;
      if (!response.data?.success || !transfer?.transfer_id) {
        throw new Error(response.data?.message || 'Transfer did not complete');
      }

      const queueUpdate = await updateVisitOwnerInFirestoreWaitingList({
        clinic_id: clinicId,
        patient_id: selectedPatient.patient_id,
        file_number: selectedPatient.file_number || selectedPatient.file_id || '',
        date: visit.date || selectedPatient.date,
        source_doctor_id: sourceDoctorId,
        target_doctor_id: receivingDoctor.id,
        target_doctor_name: receivingDoctor.name,
        transfer_id: transfer.transfer_id,
      });
      if (!queueUpdate.success) {
        console.warn('[visit-transfer] Visit moved, but live waiting-list refresh metadata was not found');
      }

      setTransferModalVisible(false);
      clearPatientListCache();
      setSelectedPatient(null);
      fetchPatients({ forceRefresh: true }).catch((refreshError) => {
        console.warn('[visit-transfer] Source list refresh will retry from the dashboard listener:', refreshError);
      });
      onBackToList();
      message.success(
        language === 'ar'
          ? `تم تحويل الزيارة إلى د. ${receivingDoctor.name} بدون إنشاء حساب جديد`
          : `Visit transferred to Dr. ${receivingDoctor.name} without creating another bill`
      );
    } catch (error: any) {
      console.error('[visit-transfer] Transfer failed:', error);
      message.error(error?.response?.data?.message || error?.message || (language === 'ar' ? 'فشل تحويل الزيارة' : 'Visit transfer failed'));
    } finally {
      setTransferSubmitting(false);
    }
  };

  const openAssignPackageModal = async () => {
    setSelectedPackageToAssign(null);
    const fallbackDoctorId = sessionDoctorId || localStorage.getItem('doctorId') || '';
    const clinicId = selectedClinicId || selectedPatient?.clinic_id || localStorage.getItem('clinicId') || '';
    let packagesDoctorId = ownerDoctorIdForClinic || fallbackDoctorId;
    if (!ownerDoctorIdForClinic && clinicId) {
      try {
        packagesDoctorId = await getClinicOwnerDoctorId(clinicId, fallbackDoctorId);
        setOwnerDoctorIdForClinic(packagesDoctorId);
      } catch {
        packagesDoctorId = fallbackDoctorId;
      }
    }
    if (packagesDoctorId) {
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_PACKAGES(packagesDoctorId)}`, { params: { activeOnly: 'true' } })
        .then(res => {
          if (res.data?.success) setDoctorPackagesForAssign(visibleManualPackages(res.data.data || []));
        });
    }
    setAssignPackageModalVisible(true);
  };

  const handleBillingMenuAction = async (key: string) => {
    switch (key) {
      case 'receipt':
        await handlePrintBillingReceipt();
        break;
      case 'package':
        await openAssignPackageModal();
        break;
      case 'insurance':
        setInsuranceProgramVisible(true);
        break;
      case 'discount':
        setDiscountModalVisible(true);
        break;
      case 'inventory':
        setManualInventoryVisible(true);
        break;
      case 'refund':
        await handleRefundAllMoney();
        break;
      default:
        break;
    }
  };

  const handleDeletePrescription = (receipt: any) => {
    if (!selectedPatient?._id || !receipt?._id || !receipt?._visitId) return;

    Modal.confirm({
      title: isRTL ? 'حذف هذه الروشتة؟' : 'Remove this prescription?',
      content: isRTL
        ? 'سيتم إخفاء الروشتة من صفحة المريض، ومعها الشكوى والتشخيص المرتبطان بهذه الزيارة إذا لم توجد روشتة أخرى في نفس الزيارة. لن يتم حذف البيانات نهائيًا.'
        : 'This will hide the prescription from the patient page. If it is the only active prescription in this visit, the linked complaint and diagnosis will be hidden too. Data will be soft-deleted only.',
      okText: isRTL ? 'حذف' : 'Remove',
      okType: 'danger',
      cancelText: isRTL ? 'إلغاء' : 'Cancel',
      onOk: async () => {
        try {
          setDeletingPrescriptionId(receipt._id);
          const response = await axios.delete(
            `${API.BASE_URL}${API.ENDPOINTS.DELETE_RECEIPT(selectedPatient._id, receipt._visitId, receipt._id)}`,
            { params: { doctor_id: selectedPatient.doctor_id || sessionDoctorId || '' } }
          );

          if (response.data?.patient?.visits) {
            setSelectedPatient((prev: any) =>
              prev ? { ...prev, visits: response.data.patient.visits } : prev
            );
          }

          message.success(isRTL ? 'تم حذف الروشتة' : 'Prescription removed');
          const freshHistory = await fetchPatientHistory();
          if (freshHistory && selectedVisit) {
            const targetId = selectedVisit._id || (selectedVisit as any).visit_id;
            const freshVisit = (freshHistory.visits || []).find(
              (v: any) => v._id === targetId || v.visit_id === targetId
            );
            if (freshVisit) setSelectedVisit(freshVisit);
          }
        } catch (error: any) {
          message.error(error?.response?.data?.message || (isRTL ? 'فشل حذف الروشتة' : 'Failed to remove prescription'));
          throw error;
        } finally {
          setDeletingPrescriptionId(null);
        }
      }
    });
  };

  const handleDeletePrescriptionDrug = (receipt: any, drugIndex: number) => {
    if (!selectedPatient?._id || !receipt?._id || !receipt?._visitId) return;
    const drugs = Array.isArray(receipt.drugs) ? receipt.drugs : [];
    const drug = drugs[drugIndex];
    if (!drug) return;
    const deletionKey = `${receipt._id}:${drugIndex}`;

    Modal.confirm({
      title: isRTL ? 'حذف هذا الدواء من الروشتة؟' : 'Remove this drug from the prescription?',
      content: isRTL
        ? `سيتم حذف ${drug.drug || 'الدواء'} من هذه الروشتة فقط.`
        : `${drug.drug || 'This drug'} will be removed from this prescription only.`,
      okText: isRTL ? 'حذف' : 'Remove',
      okType: 'danger',
      cancelText: isRTL ? 'إلغاء' : 'Cancel',
      onOk: async () => {
        try {
          setDeletingPrescriptionDrugKey(deletionKey);
          await axios.put(
            `${API.BASE_URL}${API.ENDPOINTS.UPDATE_RECEIPT(selectedPatient._id, receipt._visitId, receipt._id)}`,
            { drugs: drugs.filter((_: any, index: number) => index !== drugIndex) }
          );
          message.success(isRTL ? 'تم حذف الدواء من الروشتة' : 'Drug removed from prescription');
          await fetchPatientHistory();
        } catch (error: any) {
          message.error(error?.response?.data?.message || (isRTL ? 'فشل حذف الدواء' : 'Failed to remove drug'));
          throw error;
        } finally {
          setDeletingPrescriptionDrugKey(null);
        }
      }
    });
  };

  useEffect(() => {
    const packagePopupPatientId = String(selectedPatient?.patient_id || '');
    const shouldOpenPackagePopup = Boolean(packagePopupPatientId) &&
      activePackagePopupPatientRef.current !== packagePopupPatientId;
    activePackagePopupPatientRef.current = packagePopupPatientId;
    fetchPatientHistory();

    const fetchPatientReports = async () => {
      if (selectedPatient) {
        try {
          setReportsLoading(true);
          const reportParams: Record<string, string> = {
            patient_id: selectedPatient.patient_id,
            patient_phone: selectedPatient.patient_phone,
            doctor_id: selectedPatient.doctor_id,
          };
          // Exact Mongo row — isolates siblings sharing phone / duplicate patient_id
          if (selectedPatient._id) {
            reportParams.mongo_id = selectedPatient._id;
          }
          const fn = selectedPatient.file_number || selectedPatient.file_id;
          if (fn) {
            reportParams.file_number = String(fn).trim();
          }
          // Clinic-shared mode: every doctor in the clinic sees the same reports.
          if (clinicScopeIds && clinicScopeIds.length > 0) {
            reportParams.use_clinic_scope = 'true';
            reportParams.clinic_ids = clinicScopeIds.join(',');
          }
          const response = await axios.get(`${API.BASE_URL}/api/patients/reports`, {
            params: reportParams
          });

          if (response.data.success) {
            setPatientReports(response.data.data || []);
          }
        } catch (error) {
          console.error('Error fetching patient reports:', error);
        } finally {
          setReportsLoading(false);
        }
      }
    };

    const fetchExternalServices = async () => {
      const doctorId = localStorage.getItem('doctorId');
      if (doctorId) {
        try {
          const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_EXTERNAL_SERVICES(doctorId)}`);
          if (response.data.success) {
            setExternalServices(response.data.data || []);
          }
        } catch (error) {
          console.error('Error fetching external services:', error);
        }
      }
    };

    const fetchMedicalReports = async () => {
      if (!selectedPatient?._id) return;
      try {
        setMedicalReportsLoading(true);
        const response = await axios.get(
          `${API.BASE_URL}${API.ENDPOINTS.MEDICAL_REPORTS(selectedPatient._id)}`
        );
        if (response.data?.success) {
          const sorted = (response.data.medical_reports || []).slice().reverse();
          setPatientMedicalReports(sorted);
        }
      } catch (error) {
        console.error('Error fetching medical reports:', error);
      } finally {
        setMedicalReportsLoading(false);
      }
    };

    const fetchReferrals = async () => {
      if (!selectedPatient?._id) return;
      try {
        setReferralsLoading(true);
        const response = await axios.get(
          `${API.BASE_URL}${API.ENDPOINTS.REFERRALS(selectedPatient._id)}`
        );
        if (response.data?.success) {
          const sorted = (response.data.referrals || []).slice().reverse();
          setPatientReferrals(sorted);
        }
      } catch (error) {
        console.error('Error fetching referrals:', error);
      } finally {
        setReferralsLoading(false);
      }
    };

    const fetchPatientPackages = async () => {
      if (!selectedPatient?.patient_id) return;
      const doctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId');
      const clinicIdForPackages = selectedClinicId || selectedPatient.clinic_id || localStorage.getItem('clinicId') || '';
      // Prefer clinic-shared mode so packages stored under the clinic owner
      // (different doctor_id from the linked doctor) still surface here.
      const params: Record<string, any> = clinicIdForPackages
        ? { clinic_id: clinicIdForPackages }
        : { doctor_id: doctorId };
      try {
        const [activeRes, allRes] = await Promise.all([
          axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_ACTIVE_PACKAGES(selectedPatient.patient_id)}`, { params }),
          axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_PACKAGES(selectedPatient.patient_id)}`, { params })
        ]);
        if (activeRes.data?.success) {
          setActivePackages(activeRes.data.data || []);
          // Show the reminder only when entering a patient. Background refreshes
          // after insurance/package saves must not interrupt the completed action.
          if (shouldOpenPackagePopup && (activeRes.data.data || []).length > 0) {
            setActivePackagePopupVisible(true);
          }
        }
        if (allRes.data?.success) setAllPatientPackages(allRes.data.data || []);
      } catch (error) {
        console.error('Error fetching patient packages:', error);
      }
    };

    const fetchMedicalHistory = async () => {
      if (!selectedPatient?.patient_id) return;
      const doctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId');
      if (!doctorId) return;
      try {
        const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.MEDICAL_HISTORY_PATIENT}`, {
          params: { patient_id: selectedPatient.patient_id, doctor_id: doctorId }
        });
        if (res.data?.success && res.data.data) {
          setMedicalHistoryData({
            data: res.data.data.data || {},
            template_snapshot: res.data.data.template_snapshot || null
          });
        } else {
          setMedicalHistoryData(null);
        }
      } catch {
        setMedicalHistoryData(null);
      }
    };

    fetchPatientReports();
    fetchExternalServices();
    fetchExternalRequests();
    fetchPatientBillings();
    fetchPatientVitals();
    fetchMedicalReports();
    fetchReferrals();
    fetchPatientPackages();
    fetchMedicalHistory();

    // Reset visit type editing state
    setEditingVisitType(false);
    if (selectedPatient) {
      setSelectedVisitTypeId(selectedPatient.visit_type || '');
      setSelectedUrgency((selectedPatient as any).visit_urgency || 'normal');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient]);

  // Load visit types configuration
  useEffect(() => {
    const loadVisitTypes = async () => {
      const doctorId = localStorage.getItem('doctorId');
      if (doctorId) {
        try {
          const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`);
          if (response.data.success) {
            setAvailableVisitTypes(response.data.data.visit_types || []);
          }
        } catch (error) {
          console.error('Error loading visit types:', error);
        }
      }
    };
    loadVisitTypes();
  }, []);

  const handleUpdateVisitType = async () => {
    if (!selectedPatient) return;

    try {
      setSavingVisitType(true);
      const doctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId');
      const billingContext = buildVisitTypeBillingContext(selectedPatient, selectedClinicId);

      const response = await axios.put(`${API.BASE_URL}${API.ENDPOINTS.UPDATE_VISIT_TYPE(selectedPatient.patient_id)}`, {
        visit_type: selectedVisitTypeId,
        visit_urgency: selectedUrgency,
        doctor_id: doctorId,
        ...billingContext,
        changed_by: 'doctor'
      });

      if (response.data.success) {
        message.success('Visit type updated successfully');
        setEditingVisitType(false);

        // Handle billing notification if price changed
        const { newPrice, oldPrice, priceDifference } = response.data.data;

        // If there's a price difference, notify assistant
        if (priceDifference && priceDifference !== 0) {
          const isRefund = priceDifference < 0;
          const absDiff = Math.abs(priceDifference);
          const typeLabel = isRefund ? 'Refund' : 'Payment';

          try {
            // Parse date to ensure correct format for Firebase path
            let dateString = selectedPatient.date || '';
            if (dateString.includes('T')) {
              dateString = dateString.split('T')[0];
            }

            // Format to ensure single digits for month/day (Firebase path convention: e.g. 2026-1-5 NOT 2026-01-05)
            if (dateString && dateString.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
              const parts = dateString.split('-');
              dateString = `${parts[0]}-${parseInt(parts[1])}-${parseInt(parts[2])}`;
            }

            // Fallback to today if date is missing
            if (!dateString) {
              const today = new Date();
              dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
            }

            const billingId = 'diff_' + Date.now();
            const diffService = {
              service_id: 'visit_type_diff',
              service_name: isRefund ? 'استرداد فرق زيارة' : 'فرق تغيير نوع الكشف',
              price: absDiff,
              quantity: 1,
              subtotal: absDiff
            };

            // Record refund owed in Mongo (same pattern as discount) so "استرداد كامل المبلغ" nets out prior refund_due rows
            if (isRefund) {
              try {
                await axios.post(`${API.BASE_URL}${API.ENDPOINTS.BILLING}`, {
                  doctor_id: doctorId,
                  patient_id: selectedPatient.patient_id,
                  patient_name: selectedPatient.patient_name || 'Unknown',
                  patient_phone: selectedPatient.patient_phone || '',
                  clinic_id: selectedPatient.clinic_id || '',
                  consultationFee: 0,
                  consultationType: 'استرداد فرق زيارة',
                  services: [diffService],
                  paymentStatus: 'refund_due',
                  paymentMethod: 'cash',
                  amountPaid: 0,
                  notes: `Changed visit type from ${selectedPatient.visit_type} to ${selectedVisitTypeId}.`,
                  billingDate: dateString,
                  billing_id: billingId,
                });
              } catch (mongoErr) {
                console.error('Failed to record visit-type refund to billing:', mongoErr);
                message.warning('Refund notification will be sent, but billing record failed to save');
              }
            }

            // Create billing notification
            await sendBillingNotificationToAllAssistants({
              doctor_id: doctorId || '',
              clinic_id: selectedPatient.clinic_id || selectedClinicId || '',
              patient_id: selectedPatient.patient_id,
              date: dateString,
              patient_name: selectedPatient.patient_name,
              totalAmount: absDiff,
              amountPaid: 0,
              paymentStatus: isRefund ? 'refund_due' : 'pending',
              paymentMethod: 'cash',
              consultationFee: 0,
              consultationType: isRefund ? 'استرداد فرق زيارة' : 'فرق زيارة',
              services: [diffService],
              servicesTotal: absDiff,
              billing_id: billingId,
              clinic_name: '',
              doctor_name: '',
              notes: `Changed visit type from ${selectedPatient.visit_type} to ${selectedVisitTypeId}.`,
              new_visit_type: selectedVisitTypeId,
            });
            message.info(`${typeLabel} notification sent: ${absDiff} EGP`);
          } catch (notifError) {
            console.error('Failed to send billing notification:', notifError);
            message.warning('Failed to send billing notification to assistant');
          }
        }

        const updatedPatient = response.data.data.patient;
        setSelectedPatient(updatedPatient);
        // Visit-type update just changed the price on the server; bypass the
        // short-lived FE response cache so the list reflects the change now.
        fetchPatients({ forceRefresh: true });

        // Mirror the change to Firestore so the assistant portal card updates in real time
        try {
          const today = new Date();
          const todayStr = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
          await updateVisitTypeInFirestoreWaitingList({
            clinic_id: selectedPatient.clinic_id || selectedClinicId || '',
            patient_id: selectedPatient.patient_id || selectedPatient._id || '',
            date: todayStr,
            visit_type: selectedVisitTypeId,
          });
        } catch (fsErr) {
          console.warn('Could not sync visit_type to Firestore:', fsErr);
        }
      }
    } catch (error) {
      console.error('Error updating visit type:', error);
      message.error('Failed to update visit type');
    } finally {
      setSavingVisitType(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!selectedPatient) return;
    try {
      setSavingAddress(true);
      const doctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId');
      await axios.put(
        `${API.BASE_URL}${API.ENDPOINTS.PATIENT_BY_ID(selectedPatient._id)}`,
        { address: addressInputValue },
        { params: { doctor_id: doctorId } }
      );
      message.success(language === 'ar' ? 'تم حفظ العنوان' : 'Address saved');
      setEditingAddress(false);
      setSelectedPatient({ ...selectedPatient, address: addressInputValue });
    } catch (error) {
      if (isProfileQueued(error)) { message.warning('Saved on this device — pending sync.'); return; }
      console.error('Error saving address:', error);
      message.error(language === 'ar' ? 'فشل حفظ العنوان' : 'Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleAssignPackage = async () => {
    if (!selectedPatient?.patient_id || !selectedPackageToAssign) {
      message.warning('Please select a package');
      return;
    }
    const fallbackDoctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId') || '';
    const clinicId = selectedClinicId || selectedPatient.clinic_id || '';
    // The selected package_id only exists under the clinic owner's doctor
    // profile, so we must assign with the owner's doctor_id (not the linked
    // doctor's own id, which has no copy of the catalog).
    let assignDoctorId = ownerDoctorIdForClinic || fallbackDoctorId;
    if (!ownerDoctorIdForClinic && clinicId) {
      try {
        assignDoctorId = await getClinicOwnerDoctorId(clinicId, fallbackDoctorId);
        setOwnerDoctorIdForClinic(assignDoctorId);
      } catch {
        assignDoctorId = fallbackDoctorId;
      }
    }
    if (!assignDoctorId) return;
    setAssignPackageSubmitting(true);
    try {
      const res = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.ASSIGN_PATIENT_PACKAGE}`, {
        patient_id: selectedPatient.patient_id,
        package_id: selectedPackageToAssign,
        doctor_id: assignDoctorId,
        clinic_id: clinicId,
        patient_name: selectedPatient.patient_name
      });
      if (res.data?.success) {
        message.success('Package assigned to patient');
        setAssignPackageModalVisible(false);
        setSelectedPackageToAssign(null);
        // After assigning we re-fetch using clinic-shared mode (when a clinic
        // id is available) so the patient_package shows up regardless of
        // which doctor_id is on the record.
        const refetchParams: Record<string, any> = clinicId
          ? { clinic_id: clinicId }
          : { doctor_id: assignDoctorId };
        const [activeRes, allRes] = await Promise.all([
          axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_ACTIVE_PACKAGES(selectedPatient.patient_id)}`, { params: refetchParams }),
          axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_PACKAGES(selectedPatient.patient_id)}`, { params: refetchParams })
        ]);
        if (activeRes.data?.success) {
          setActivePackages(activeRes.data.data || []);
          setActivePackagePopupVisible(true);
        }
        if (allRes.data?.success) setAllPatientPackages(allRes.data.data || []);
      }
    } catch (error: any) {
      if (isProfileQueued(error)) {
        setAssignPackageModalVisible(false);
        setSelectedPackageToAssign(null);
        message.warning('Saved on this device — pending sync.');
        return;
      }
      message.error(error.response?.data?.message || t('assignPackageFailed'));
    } finally {
      setAssignPackageSubmitting(false);
    }
  };

  const handleApplyDiscount = async (values: { amount: number; reason?: string }) => {
    if (!selectedPatient) return;
    const doctorId = localStorage.getItem('doctorId');
    const amount = Number(values.amount) || 0;
    if (amount <= 0) {
      message.error(t('discountInvalidAmount'));
      return;
    }
    if (discountSubmitting) return;
    setDiscountSubmitting(true);
    setDiscountModalVisible(false); // Close immediately on submit

    const patientId = selectedPatient.patient_id || (selectedPatient as any).id || selectedPatient._id;
    if (!patientId) {
      message.error(t('discountPatientIdMissing'));
      setDiscountSubmitting(false);
      setDiscountModalVisible(true);
      return;
    }

    let dateString = selectedPatient.date || '';
    if (dateString.includes('T')) dateString = dateString.split('T')[0];
    if (dateString && dateString.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
      const parts = dateString.split('-');
      dateString = `${parts[0]}-${parseInt(parts[1])}-${parseInt(parts[2])}`;
    }
    if (!dateString) {
      const today = new Date();
      dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
    }

    const billingId = 'discount_' + Date.now();
    const discountService = {
      service_id: 'discount',
      service_name: 'خصم',
      price: amount,
      quantity: 1,
      subtotal: amount
    };

    const clinicId = selectedPatient.clinic_id || selectedClinicId || '';

    try {
      await axios.post(`${API.BASE_URL}${API.ENDPOINTS.BILLING}`, {
        doctor_id: doctorId,
        patient_id: patientId,
        patient_name: selectedPatient.patient_name || 'Unknown',
        patient_phone: selectedPatient.patient_phone || '',
        clinic_id: clinicId,
        consultationFee: 0,
        consultationType: 'خصم',
        services: [discountService],
        paymentStatus: 'refund_due',
        paymentMethod: 'cash',
        amountPaid: 0,
        notes: values.reason || 'خصم على كشف المريض',
        billingDate: dateString,
        billing_id: billingId
      });

      try {
        await sendBillingNotificationToAllAssistants({
          doctor_id: doctorId || '',
          clinic_id: clinicId,
          patient_id: patientId,
          date: dateString,
          patient_name: selectedPatient.patient_name,
          totalAmount: amount,
          amountPaid: 0,
          paymentStatus: 'refund_due',
          paymentMethod: 'cash',
          consultationFee: 0,
          consultationType: 'خصم',
          services: [discountService],
          servicesTotal: amount,
          billing_id: billingId,
          clinic_name: '',
          doctor_name: '',
          notes: values.reason || 'خصم على كشف المريض'
        });
        message.success(t('discountRecordedSuccess').replace('{amount}', amount.toLocaleString()));
      } catch (notifErr) {
        console.error('Failed to notify assistant:', notifErr);
        message.warning(t('discountRecordedWarning'));
      }
    } catch (err: any) {
      if (isProfileQueued(err)) { message.warning('Saved on this device — pending sync.'); return; }
      console.error('Failed to apply discount:', err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || t('discountRecordFailed');
      message.error(errMsg);
      setDiscountModalVisible(true);
    } finally {
      setDiscountSubmitting(false);
    }
  };

  const handleRefundAllMoney = async () => {
    if (!selectedPatient || refundAllSubmitting) return;
    const doctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId');
    if (!doctorId) { message.error('لم يتم العثور على معرّف الطبيب'); return; }

    setRefundAllSubmitting(true);
    try {
      const patientId = selectedPatient.patient_id || (selectedPatient as any).id || selectedPatient._id;

      // Fetch today's paid and refund-due billings for this patient using server-side filters
      const today = new Date();
      const todayISO = today.toISOString().split('T')[0]; // "2026-04-29"
      const [paidBillingRes, refundDueBillingRes] = await Promise.all([
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_BILLINGS(doctorId)}`, {
          params: {
            patient_id: patientId,
            paymentStatus: 'paid',
            startDate: todayISO,
            endDate: todayISO,
            limit: 100,
          },
        }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_BILLINGS(doctorId)}`, {
          params: {
            patient_id: patientId,
            paymentStatus: 'refund_due',
            startDate: todayISO,
            endDate: todayISO,
            limit: 100,
          },
        }),
      ]);

      const paidBillings: any[] = paidBillingRes.data?.data || [];
      const refundDueBillings: any[] = refundDueBillingRes.data?.data || [];

      const totalPaid = paidBillings.reduce(
        (sum: number, b: any) => sum + (Number(b.amountPaid ?? b.totalAmount) || 0),
        0
      );
      const totalRefundRequested = refundDueBillings.reduce(
        (sum: number, b: any) => sum + (Number(b.totalAmount ?? b.amountPaid) || 0),
        0
      );
      const refundableAmount = Math.max(0, totalPaid - totalRefundRequested);

      if (refundableAmount <= 0) {
        message.warning('لا توجد مدفوعات مسجلة لهذا المريض اليوم');
        setRefundAllSubmitting(false);
        return;
      }

      const confirmed = window.confirm(
        `سيتم إرسال طلب استرداد كامل المبلغ (${refundableAmount.toLocaleString()} ج) للسكرتيرة. هل تريد المتابعة؟`
      );
      if (!confirmed) { setRefundAllSubmitting(false); return; }

      let dateString = selectedPatient.date || '';
      if (dateString.includes('T')) dateString = dateString.split('T')[0];
      if (dateString && dateString.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
        const parts = dateString.split('-');
        dateString = `${parts[0]}-${parseInt(parts[1])}-${parseInt(parts[2])}`;
      }
      if (!dateString) {
        dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
      }

      const billingId = 'refund_all_' + Date.now();
      const refundService = {
        service_id: 'refund_all',
        service_name: 'استرداد كامل المبلغ',
        price: refundableAmount,
        quantity: 1,
        subtotal: refundableAmount,
      };
      const clinicId = selectedPatient.clinic_id || selectedClinicId || '';

      await axios.post(`${API.BASE_URL}${API.ENDPOINTS.BILLING}`, {
        doctor_id: doctorId,
        patient_id: patientId,
        patient_name: selectedPatient.patient_name || 'Unknown',
        patient_phone: selectedPatient.patient_phone || '',
        clinic_id: clinicId,
        consultationFee: 0,
        consultationType: 'استرداد كامل المبلغ',
        services: [refundService],
        paymentStatus: 'refund_due',
        paymentMethod: 'cash',
        amountPaid: 0,
        totalAmount: refundableAmount,
        notes: 'استرداد كامل مبلغ الزيارة',
        billingDate: dateString,
        billing_id: billingId,
      });

      try {
        const notificationResult: any = await sendBillingNotificationToAllAssistants({
          doctor_id: doctorId,
          clinic_id: clinicId,
          patient_id: patientId,
          date: dateString,
          patient_name: selectedPatient.patient_name,
          totalAmount: refundableAmount,
          amountPaid: 0,
          paymentStatus: 'refund_due',
          paymentMethod: 'cash',
          consultationFee: 0,
          consultationType: 'استرداد كامل المبلغ',
          services: [refundService],
          servicesTotal: refundableAmount,
          billing_id: billingId,
          clinic_name: '',
          doctor_name: '',
          notes: 'استرداد كامل مبلغ الزيارة',
        });

        if (!notificationResult?.success || !notificationResult?.notified) {
          message.warning('تم تسجيل الاسترداد لكن لم يتم إرسال إشعار للسكرتيرة');
          return;
        }

        message.success(`تم إرسال طلب استرداد ${refundableAmount.toLocaleString()} ج للسكرتيرة`);
      } catch (notifErr) {
        console.error('Failed to notify assistant of full refund:', notifErr);
        message.warning('تم تسجيل الاسترداد ولكن فشل إرسال الإشعار للسكرتيرة');
      }
    } catch (err: any) {
      if (isProfileQueued(err)) { message.warning('Saved on this device — pending sync.'); return; }
      console.error('Failed to process full refund:', err);
      message.error(err?.response?.data?.message || err?.message || 'حدث خطأ أثناء إرسال طلب الاسترداد');
    } finally {
      setRefundAllSubmitting(false);
    }
  };

  // ── Print billing receipt (same as assistant) ─────────────────────────────
  const handlePrintBillingReceipt = async () => {
    if (!selectedPatient) return;
    setBillingReceiptLoading(true);
    try {
      const patientId = selectedPatient.patient_id || (selectedPatient as any).id || selectedPatient._id;
      const today = new Date();
      const todayISO = today.toISOString().split('T')[0];

      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_BILLINGS(doctorId)}`, {
        params: { patient_id: patientId, startDate: todayISO, endDate: todayISO, limit: 100 },
      });
      const billings: any[] = res.data?.data || [];

      // Build consultationPayment from the patient's consultation_fee field
      // (recorded by the assistant at check-in time, stored on the patient record).
      // This is separate from billing API records which track services.
      const consultFee = Number((selectedPatient as any).consultation_fee ?? 0);
      const billingHasConsultFee = billings.some(b => Number(b.consultationFee) > 0);
      const consultationPayment = (consultFee > 0 && !billingHasConsultFee) ? {
        amount: consultFee,
        consultationType: selectedPatient.visit_type || 'كشف',
        paymentStatus: 'paid',
        paymentMethod: (selectedPatient as any).payment_type === 'insurance' ? 'insurance' : 'cash',
      } : undefined;

      printBillingReceipt(
        { ...selectedPatient, consultationPayment, bills: billings },
        doctorSettings,
        latestVisitDiagnosis
      );
    } catch (err) {
      console.error('Print billing receipt error:', err);
      message.error('Failed to print receipt');
    } finally {
      setBillingReceiptLoading(false);
    }
  };

  // Most recent non-empty diagnosis from the same sources as the Complaint & Diagnosis table
  // (complaint_history + visits with complaint or diagnosis), sorted by date descending.
  const latestVisitDiagnosis = React.useMemo(() => {
    const historyEntries = [
      ...(patientHistory?.complaint_history || []),
      ...(((selectedPatient as any)?.complaint_history || []) as any[]),
    ];
    const visitEntries = [
      ...(patientHistory?.visits || []),
      ...((selectedPatient?.visits || []) as Visit[]),
    ];
    const fromHistory = historyEntries.map((e: any) => ({
      date: e.date,
      diagnosis: e.diagnosis || '',
    }));
    const fromVisits = visitEntries
      .filter((v: Visit) => v.complaint || v.diagnosis)
      .map((v: Visit) => ({
        date: v.date,
        diagnosis: v.diagnosis || '',
      }));
    const combined = [...fromHistory, ...fromVisits].sort(
      (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
    );
    for (const row of combined) {
      const t = row.diagnosis && String(row.diagnosis).trim();
      if (t) return t;
    }
    return '';
  }, [patientHistory, selectedPatient]);

  const latestPrescriptionDiagnosis = React.useMemo(() => {
    const prescribedVisits = [
      ...(patientHistory?.visits || []),
      ...((selectedPatient?.visits || []) as Visit[]),
    ]
      .filter((visit: Visit) => getActiveReceipts(visit.receipts).length > 0 && String(visit.diagnosis || '').trim())
      .sort((a: Visit, b: Visit) => {
        const latestReceiptDate = (visit: Visit) => Math.max(
          moment(visit.date).valueOf(),
          ...getActiveReceipts(visit.receipts).map(receipt => moment(receipt.date).valueOf())
        );
        return latestReceiptDate(b) - latestReceiptDate(a);
      });

    return prescribedVisits.length > 0
      ? String(prescribedVisits[0].diagnosis).trim()
      : latestVisitDiagnosis;
  }, [patientHistory, selectedPatient, latestVisitDiagnosis]);

  if (!selectedPatient) {
    return null;
  }

  const synchronizedReceipts = patientHistory?.visits.flatMap((visit: Visit) =>
    getActiveReceipts(visit.receipts).map(receipt => ({
      ...receipt,
      _visitId: visit.visit_id || visit._id,
      visit_type: visit.visit_type,
      doctor_name: (visit as any).doctor_name || '',
    }))
  ) || [];
  const allReceipts = [...pendingPrescriptionReceipts, ...synchronizedReceipts];
  const visiblePatientReports = [...pendingUploadedReports, ...patientReports];
  const visibleMedicalReports = [...pendingMedicalReports, ...patientMedicalReports];
  const visibleReferrals = [...pendingReferrals, ...patientReferrals];
  const visibleExternalRequests = [...pendingExternalRequests, ...externalRequests];
  const visiblePatientBillings = [...pendingBillings, ...patientBillings];
  const visibleActivePackages = [...pendingPackages, ...activePackages];
  const visibleAllPatientPackages = [...pendingPackages, ...allPatientPackages];
  const visibleMedicalHistory = pendingMedicalHistory || medicalHistoryData;
  const selectedVisitReceipts = selectedVisit ? getActiveReceipts(selectedVisit.receipts) : [];

  const vitalDisplayRows = (vital: PatientVitals | null): Array<[string, string]> => vital
    ? ([
        [t('bloodPressure'), vital.bp],
        [t('pulse'), vital.pulse],
        [t('temperature'), vital.temp],
        [t('respiratoryRate'), vital.rr],
        [t('oxygenSaturation'), vital.o2_sat],
      ].filter((row): row is [string, string] => Boolean(row[1])))
    : [];
  const displayableVitals = patientVitals.filter(vital =>
    vitalDisplayRows(vital).length > 0 || Boolean(vital.notes?.trim())
  );
  const latestVitals = displayableVitals[0] || null;
  const previousVitals = displayableVitals.slice(1, 6);

  const serviceHistoryRows = visiblePatientBillings.flatMap((bill: any) =>
    (bill.services || [])
      .filter((service: any) => service && service.service_name)
      .map((service: any, index: number) => ({
        key: `${bill.billing_id || bill._id}-${service.service_id || index}`,
        billing_id: bill.billing_id,
        date: bill.billingDate || bill.createdAt,
        service_name: service.service_name,
        quantity: service.quantity || 1,
        price: Number(service.price || 0),
        subtotal: Number(service.subtotal ?? (Number(service.price || 0) * Number(service.quantity || 1))),
        paymentStatus: bill.paymentStatus,
        paymentMethod: bill.paymentMethod,
        notes: bill.notes || '',
        _offlinePending: bill._offlinePending,
      }))
  );



  const buildSavedMedicalReportHtml = (report: any): string => {
    const printSettings = (doctorSettings as any).printSettings || {
      paperSize: 'a4',
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      showHeader: true,
      showFooter: true,
      showPatientInfo: true,
    };
    const isCustomPaper = !printSettings.showHeader;

    const clinicInfoParts: string[] = [];
    if (doctorSettings.clinicName) clinicInfoParts.push(`<h1 style="margin:4px 0">${doctorSettings.clinicName}</h1>`);
    if (doctorSettings.doctorTitle) clinicInfoParts.push(`<h3 style="margin:4px 0">${doctorSettings.doctorTitle}</h3>`);
    if (doctorSettings.clinicAddress) clinicInfoParts.push(`<p style="margin:2px 0">${doctorSettings.clinicAddress}</p>`);
    if (doctorSettings.clinicPhone) clinicInfoParts.push(`<p style="margin:2px 0">هاتف: ${doctorSettings.clinicPhone}</p>`);

    const savedDiagnosis = String(report.diagnosis || '').trim();
    const diagnosis = savedDiagnosis || latestVisitDiagnosis;
    const additionalLatestDiagnosis =
      savedDiagnosis && latestVisitDiagnosis && savedDiagnosis !== latestVisitDiagnosis
        ? latestVisitDiagnosis
        : '';
    const medicalReportText = report.medical_report || '';
    const signature = report.signature || '';
    const reportDate = report.date ? moment(report.date).format('DD / MM / YYYY') : moment().format('DD / MM / YYYY');

    const printHtml = `
      <html>
        <head>
          <title>تقرير طبي</title>
          <style>
            * { box-sizing: border-box; }
            @page {
              size: ${printSettings.paperSize === 'custom' ? 'auto' : (printSettings.paperSize || 'a4')};
              margin: 0;
            }
            html, body { font-family: Arial, sans-serif; direction: ${isRTL ? 'rtl' : 'ltr'}; margin: 0; padding: 0; color: #222; }
            .top-spacer { display: block; height: ${((printSettings.marginTop || 0) / 2)}mm; width: 100%; }
            .receipt {
              padding-top: 0;
              padding-left: calc(${((printSettings.marginLeft || 0) / 2)}mm + 10px);
              padding-right: calc(${((printSettings.marginRight || 0) / 2)}mm + 10px);
              padding-bottom: 0;
              box-sizing: border-box;
            }
            .header {
              text-align: center;
              border-bottom: ${isCustomPaper ? 'none' : '2px solid #333'};
              padding-bottom: 6px; margin-bottom: 8px;
              display: ${isCustomPaper ? 'none' : 'block'};
            }
            .title { font-size: 17px; font-weight: bold; margin: 6px 0 4px; text-align: center; letter-spacing: 1px; }
            .patient-info {
              background: ${isCustomPaper ? 'transparent' : '#f8f8f8'};
              border-radius: 4px; padding: 6px 10px; margin-bottom: 8px; font-size: 13px;
              display: ${printSettings.showPatientInfo ? 'block' : 'none'};
            }
            .patient-info p { margin: 2px 0; }
            .section { margin-bottom: 8px; }
            .section-label { font-weight: bold; font-size: 13px; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 4px; color: #444; }
            .section-content { font-size: 13px; line-height: 1.5; white-space: pre-wrap; }
            .signature-area { margin-top: 16px; display: flex; justify-content: flex-end; }
            .signature-box { text-align: center; min-width: 160px; }
            .signature-line { border-top: 1px solid #333; margin-top: 20px; padding-top: 4px; font-size: 12px; }
            .footer {
              margin-top: 10px;
              border-top: ${isCustomPaper ? 'none' : '1px solid #ccc'};
              padding-top: 6px; text-align: center; font-style: italic; font-size: 11px; color: #666;
              display: ${printSettings.showFooter ? 'block' : 'none'};
            }
            h1 { margin: 3px 0; font-size: 18px; }
            h2 { margin: 3px 0; font-size: 16px; }
            h3 { margin: 2px 0; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="top-spacer"></div>
          <div class="receipt">
            <div class="header">
              ${clinicInfoParts.length > 0 ? clinicInfoParts.join('') : '<h2>عيادة طبية</h2>'}
              ${(doctorSettings as any).receiptHeader ? `<div>${(doctorSettings as any).receiptHeader}</div>` : ''}
            </div>
            <div class="title">تقـريـر طـبـي &nbsp;|&nbsp; Medical Report</div>
            <div class="patient-info">
              <p><strong>Patient:</strong> ${selectedPatient?.patient_name || ''}</p>
              <p><strong>Age:</strong> ${selectedPatient?.age || ''}</p>
              <p><strong>Date:</strong> ${reportDate}</p>
            </div>
            ${diagnosis ? `<div class="section"><div class="section-label">Diagnosis</div><div class="section-content">${diagnosis.replace(/\n/g, '<br/>')}</div></div>` : ''}
            ${additionalLatestDiagnosis ? `<div class="section"><div class="section-label" style="color:#389e0d">Last Diagnosis / آخر تشخيص</div><div class="section-content" style="background:#f6ffed;border:1px solid #b7eb8f;border-radius:4px;padding:6px 10px;">${additionalLatestDiagnosis.replace(/\n/g, '<br/>')}</div></div>` : ''}
            ${medicalReportText ? `<div class="section"><div class="section-label">Medical Report</div><div class="section-content">${medicalReportText.replace(/\n/g, '<br/>')}</div></div>` : ''}
            <div class="signature-area">
              <div class="signature-box">
                <div class="signature-line">${signature || 'Doctor Signature'}</div>
              </div>
            </div>
            ${printSettings.showFooter && (doctorSettings as any).receiptFooter ? `<div class="footer">${(doctorSettings as any).receiptFooter}</div>` : ''}
          </div>
        </body>
      </html>
    `;

    return printHtml;
  };

  const handlePrintSavedMedicalReport = (report: any) => {
    triggerPrint(buildSavedMedicalReportHtml(report));
  };

  const buildReferralLetterHtml = (ref: any): string => {
    const printSettings = (doctorSettings as any).printSettings || {
      paperSize: 'a4',
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      showHeader: true,
      showFooter: true,
    };
    const isCustomPaper = !printSettings.showHeader;
    const esc = (v: unknown) => String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const clinicHeaderParts: string[] = [];
    if (doctorSettings.clinicName) clinicHeaderParts.push(`<h1 style="margin:4px 0">${esc(doctorSettings.clinicName)}</h1>`);
    if (doctorSettings.doctorTitle) clinicHeaderParts.push(`<h3 style="margin:4px 0">${esc(doctorSettings.doctorTitle)}</h3>`);
    if (doctorSettings.clinicAddress) clinicHeaderParts.push(`<p style="margin:2px 0">${esc(doctorSettings.clinicAddress)}</p>`);
    if (doctorSettings.clinicPhone) clinicHeaderParts.push(`<p style="margin:2px 0">Phone: ${esc(doctorSettings.clinicPhone)}</p>`);

    const dateLine = ref.referral_date ? moment(ref.referral_date).format('DD MMM YYYY') : '—';
    const fromLine = `${ref.from_doctor_name || '—'}${ref.from_doctor_title ? ` · ${ref.from_doctor_title}` : ''}`;
    const toMain = `${ref.to_doctor_name || '—'}${ref.to_doctor_title ? ` · ${ref.to_doctor_title}` : ''}`;
    const toClinic = ref.to_clinic_name
      ? `<div class="ref-to-clinic">${esc(ref.to_clinic_name)}</div>`
      : '';
    const subjectLine = ref.subject ? esc(ref.subject) : '—';

    const bodyHtml = esc(ref.referral_body || '').replace(/\n/g, '<br/>');
    const summarySection = ref.referral_body
      ? `<div class="ref-summary-wrap">
           <div class="ref-label">Summary</div>
           <div class="ref-summary">${bodyHtml}</div>
         </div>`
      : '';

    const patient = selectedPatient;
    const patientHint = patient
      ? `<div class="patient-hint">
           <div class="patient-hint-cap">Patient Info (for reference):</div>
           <div class="patient-hint-body"><strong>${esc(patient.patient_name || '')}</strong>${patient.age ? `, ${esc(patient.age)}` : ''}${patient.patient_phone ? ` · ${esc(patient.patient_phone)}` : ''}${patient.address ? ` · ${esc(patient.address)}` : ''}</div>
         </div>`
      : '';

    const sigSection = ref.signature
      ? `<div class="ref-grid" style="margin-top:12px">
           <div class="ref-cell ref-cell-full">
             <div class="ref-label">Signature / التوقيع</div>
             <div class="ref-val">${esc(ref.signature)}</div>
           </div>
         </div>`
      : '';

    const referralDiagnosis = String(ref.diagnosis || '').trim() || latestPrescriptionDiagnosis;
    const diagnosisSection = referralDiagnosis
      ? `<div class="ref-diagnosis-wrap">
           <div class="ref-label">Last Diagnosis / آخر تشخيص</div>
           <div class="ref-diagnosis">${esc(referralDiagnosis)}</div>
         </div>`
      : '';

    return `
                        <html><head><title>Referral Letter</title>
                        <style>
                          * { box-sizing: border-box; }
                          @page { size: ${printSettings.paperSize === 'custom' ? 'auto' : (printSettings.paperSize || 'a4')}; margin: 0; }
                          html, body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; direction: ${isRTL ? 'rtl' : 'ltr'}; margin: 0; padding: 0; color: rgba(0,0,0,0.88); font-size: 14px; line-height: 1.5715; }
                          .top-spacer { display: block; height: ${((printSettings.marginTop || 0) / 2)}mm; width: 100%; }
                          .receipt {
                            padding-top: 0;
                            padding-left: calc(${((printSettings.marginLeft || 0) / 2)}mm + 10px);
                            padding-right: calc(${((printSettings.marginRight || 0) / 2)}mm + 10px);
                            padding-bottom: 0;
                            box-sizing: border-box;
                            max-width: 720px;
                            margin: 0 auto;
                          }
                          .header { text-align: center; margin-bottom: 16px; border-bottom: ${isCustomPaper ? 'none' : '1px solid #ccc'}; padding-bottom: 12px; display: ${isCustomPaper ? 'none' : 'block'}; }
                          h1, h2, h3 { margin: 5px 0; }
                          .ref-preview-card {
                            border: 1px solid #f0f0f0;
                            border-left: 4px solid #08979c;
                            border-radius: 8px;
                            background: #fff;
                            padding: 12px 16px;
                            margin-top: 8px;
                            margin-bottom: 8px;
                          }
                          .ref-grid {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 8px 16px;
                          }
                          .ref-cell-full { grid-column: 1 / -1; }
                          .ref-label { font-size: 12px; color: rgba(0,0,0,0.45); line-height: 1.66; margin-bottom: 0; }
                          .ref-val-date { font-weight: 600; font-size: 14px; }
                          .ref-val-subject { font-weight: 500; font-size: 14px; }
                          .ref-val { font-size: 14px; }
                          .ref-to-clinic { font-size: 12px; color: #888; margin-top: 2px; }
                          .ref-summary-wrap { margin-top: 8px; }
                          .ref-summary-wrap .ref-label { margin-bottom: 4px; }
                          .ref-summary {
                            margin-top: 4px;
                            padding: 8px 12px;
                            background: #f0faff;
                            border-radius: 6px;
                            font-size: 13px;
                            line-height: 1.6;
                            white-space: pre-wrap;
                            border: 1px solid #bae0ff;
                          }
                          .patient-hint {
                            background: #f6f8fa;
                            border: 1px solid #e0e7ef;
                            border-radius: 6px;
                            padding: 10px 14px;
                            margin-bottom: 12px;
                            font-size: 12px;
                            color: #555;
                          }
                          .patient-hint-cap { font-size: 11px; color: rgba(0,0,0,0.45); margin-bottom: 4px; }
                          .patient-hint-body { line-height: 1.5; }
                          .ref-diagnosis-wrap { margin-top: 10px; }
                          .ref-diagnosis-wrap .ref-label { margin-bottom: 4px; }
                          .ref-diagnosis {
                            margin-top: 4px;
                            padding: 8px 12px;
                            background: #f6ffed;
                            border-radius: 6px;
                            font-size: 13px;
                            font-weight: 500;
                            line-height: 1.6;
                            white-space: pre-wrap;
                            border: 1px solid #b7eb8f;
                          }
                          .footer { margin-top: 20px; border-top: ${isCustomPaper ? 'none' : '1px solid #ccc'}; padding-top: 12px; text-align: center; font-style: italic; font-size: 11px; color: #666; display: ${printSettings.showFooter ? 'block' : 'none'}; }
                        </style></head><body>
                        <div class="top-spacer"></div>
                        <div class="receipt">
                          <div class="header">
                            ${clinicHeaderParts.length > 0 ? clinicHeaderParts.join('') : '<h2>Medical Clinic</h2>'}
                            ${(doctorSettings as any).receiptHeader ? `<div>${(doctorSettings as any).receiptHeader}</div>` : ''}
                          </div>
                          ${patientHint}
                          <div class="ref-preview-card">
                            <div class="ref-grid">
                              <div class="ref-cell">
                                <div class="ref-label">Date</div>
                                <div class="ref-val-date">${esc(dateLine)}</div>
                              </div>
                              <div class="ref-cell">
                                <div class="ref-label">Subject</div>
                                <div class="ref-val-subject">${subjectLine}</div>
                              </div>
                              <div class="ref-cell">
                                <div class="ref-label">From</div>
                                <div class="ref-val">${esc(fromLine)}</div>
                              </div>
                              <div class="ref-cell">
                                <div class="ref-label">To</div>
                                <div class="ref-val">${esc(toMain)}</div>
                                ${toClinic}
                              </div>
                            </div>
                            ${diagnosisSection}
                            ${summarySection}
                            ${sigSection}
                          </div>
                          ${printSettings.showFooter && (doctorSettings as any).receiptFooter ? `<div class="footer">${(doctorSettings as any).receiptFooter}</div>` : ''}
                        </div></body></html>`;
  };

  // Function to show visit details modal
  const showVisitDetails = (visit: Visit) => {
    setSelectedVisit(visit);
    setVisitDetailsVisible(true);
  };

  const resolveVisitTypeName = (rawValue: string): string => {
    if (!rawValue) return '';
    const byId = availableVisitTypes.find(t => t.type_id === rawValue);
    if (byId) return language === 'en' ? (byId.name || rawValue) : (byId.name_ar || rawValue);
    const byNameAr = availableVisitTypes.find(t => t.name_ar === rawValue);
    if (byNameAr) return language === 'en' ? (byNameAr.name || rawValue) : (byNameAr.name_ar || rawValue);
    const byName = availableVisitTypes.find(t => t.name === rawValue);
    if (byName) return language === 'en' ? (byName.name || rawValue) : (byName.name_ar || rawValue);
    return rawValue;
  };

  const frequencyMap: Record<string, string> = {
    'مرة واحدة': 'Once',
    'مرتين': 'Twice',
    '3 مرات': '3 times',
    '4 مرات': '4 times',
    'يومياً': 'Daily',
    'أسبوعياً': 'Weekly',
  };

  const periodMap: Record<string, string> = {
    'يوميًا': 'Daily',
    'يومياً': 'Daily',
    '1 يوم': '1 day',
    '2 يوم': '2 days',
    '3 يوم': '3 days',
    '4 يوم': '4 days',
    '5 يوم': '5 days',
    '6 يوم': '6 days',
    'أسبوع': 'Week',
    'أسبوعين': '2 weeks',
    '3 اسابيع': '3 weeks',
    'شهر': 'Month',
  };

  const timingMap: Record<string, string> = {
    'قبل النوم': 'Before sleep',
    'بعد الأكل': 'After meals',
    'قبل الأكل': 'Before meals',
    'وسط الاكل': 'During meals',
    'بعد الفطار': 'After breakfast',
    'بعد الغداء': 'After lunch',
    'قبل العشا': 'Before dinner',
    'بعد العشاء': 'After dinner',
  };

  const handleInsuranceProgramSaved = (result?: any) => {
    if (!selectedPatient?.patient_id) return;
    const savedPackages: PatientPackage[] = Array.isArray(result?.patient_packages) ? result.patient_packages : [];
    if (savedPackages.length) {
      setActivePackages(savedPackages);
      setAllPatientPackages((current) => {
        const merged = new Map(current.map((pkg) => [pkg.patient_package_id, pkg]));
        savedPackages.forEach((pkg) => merged.set(pkg.patient_package_id, { ...merged.get(pkg.patient_package_id), ...pkg }));
        return Array.from(merged.values());
      });
    }
    if (result?.billing?.billing_id) {
      setPatientBillings((current) => {
        const existingIndex = current.findIndex((bill) => bill.billing_id === result.billing.billing_id);
        if (existingIndex < 0) return [result.billing, ...current];
        return current.map((bill, index) => index === existingIndex ? result.billing : bill);
      });
    }
    const packageDoctorId = selectedPatient.doctor_id || sessionDoctorId || '';
    const packageClinicId = selectedClinicId || selectedPatient.clinic_id || '';
    const params: Record<string, any> = packageClinicId
      ? { clinic_id: packageClinicId }
      : { doctor_id: packageDoctorId };
    void Promise.allSettled([
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_ACTIVE_PACKAGES(selectedPatient.patient_id)}`, { params }),
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_PACKAGES(selectedPatient.patient_id)}`, { params }),
      fetchPatientBillings(),
    ]).then(([activeResult, allResult]) => {
      if (activeResult.status === 'fulfilled' && activeResult.value.data?.success) {
        setActivePackages(activeResult.value.data.data || []);
      }
      if (allResult.status === 'fulfilled' && allResult.value.data?.success) {
        setAllPatientPackages(allResult.value.data.data || []);
      }
    });
  };

  const doseQtyMap: Record<string, string> = {
    قرص: '1 tablet',
    قرصين: '2 tablets',
    '3 أقراص': '3 tablets',
    '4 أقراص': '4 tablets',
    '1': '1 tablet',
    '1.5': '1.5',
    '2': '2 tablets',
    '2.5': '2.5',
    '3': '3 tablets',
    '4': '4 tablets',
  };

  const translateDrugField = (value: string, map: Record<string, string>): string => {
    if (language === 'en' && value && map[value]) return map[value];
    return value || '';
  };

  const getDrugSpecParts = (drug: any): string[] => {
    const parts: string[] = [];
    const form = typeof drug.form === 'string' ? drug.form.trim() : '';
    if (form && form !== '_') parts.push(`${t('drugForm')}: ${form}`);
    const route = typeof drug.route === 'string' ? drug.route.trim() : '';
    if (route && route !== '_') parts.push(`${t('drugRoute')}: ${route}`);
    const freq = translateDrugField(drug.frequency, frequencyMap);
    if (freq && freq !== '_') parts.push(freq);
    const period = translateDrugField(drug.period, periodMap);
    if (period && period !== '_') parts.push(period);
    const timing = translateDrugField(drug.timing, timingMap);
    if (timing && timing !== '_') parts.push(timing);
    const doseQ = translateDrugField(drug.doseQty || '', doseQtyMap);
    if (doseQ && doseQ !== '_') parts.push(doseQ);
    const medDet = typeof drug.medicineDetails === 'string' ? drug.medicineDetails.trim() : '';
    if (medDet) parts.push(`${t('medicineDetails')}: ${medDet}`);
    return parts;
  };

  // Column definitions for the receipts table
  const receiptsColumns = [
    {
      title: t('patientColDate'),
      dataIndex: 'date',
      key: 'date',
      render: (date: string, record: any) => (
        <Space size={6} wrap>
          <span>{moment(date).format('YYYY-MM-DD HH:mm')}</span>
          {record._offlinePending && <Tag color="gold">{isRTL ? 'في انتظار المزامنة' : 'Pending sync'}</Tag>}
        </Space>
      )
    },
    {
      title: t('patientColDrugs'),
      key: 'drugs',
      render: (_: any, record: any) => {
        const isObgyn =
          record.prescriptionTemplate === 'obgyn' ||
          record.prescriptionTemplate === 'infertility';
        const templateLabel =
          record.prescriptionTemplate === 'infertility' ? t('infertilityTemplate') : 'OB/GYN';
        const drugs: any[] = record.drugs || [];
        const investigations: string[] = record.investigations || [];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 280 }}>
            {isObgyn && (
              <span style={{ alignSelf: 'flex-start', background: '#d63384', color: '#fff', borderRadius: 4, padding: '1px 5px', fontSize: 11 }}>
                {templateLabel}
              </span>
            )}
            {drugs.length > 0
              ? drugs.map((drug: any, drugIndex: number) => {
                  const parts = [drug.drug, ...getDrugSpecParts(drug)];
                  const deletionKey = `${record._id}:${drugIndex}`;
                  return (
                    <div
                      key={drug._id || deletionKey}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '5px 6px 5px 10px',
                        border: '1px solid #edf0f3',
                        borderRadius: 6,
                        background: '#fafbfc',
                      }}
                    >
                      <span style={{ flex: 1, lineHeight: 1.45 }}>{parts.join(' - ')}</span>
                      <Tooltip title={isRTL ? 'حذف الدواء' : 'Remove drug'}>
                        <Button
                          type="text"
                          danger
                          size="small"
                          aria-label={isRTL ? `حذف ${drug.drug || 'الدواء'}` : `Remove ${drug.drug || 'drug'}`}
                          icon={<DeleteOutlined />}
                          loading={deletingPrescriptionDrugKey === deletionKey}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeletePrescriptionDrug(record, drugIndex);
                          }}
                          style={{ flexShrink: 0, paddingInline: 6 }}
                        />
                      </Tooltip>
                    </div>
                  );
                })
              : (isObgyn ? '' : t('noDrugsInReceipt'))
            }
            {investigations.length > 0 && (
              <span style={{ display: 'block', fontSize: 11, color: '#888', marginTop: 2 }}>
                🔬 {investigations.join(', ')}
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: t('patientColNotes'),
      dataIndex: 'notes',
      key: 'notes'
    },
    {
      title: t('visitType'),
      dataIndex: 'visit_type',
      key: 'visit_type',
      render: (vt: string) => resolveVisitTypeName(vt || '') || '—'
    },
    {
      title: t('patientColDoctor'),
      dataIndex: 'doctor_name',
      key: 'doctor_name',
      render: (name: string) => name ? `د. ${name}` : <span style={{ color: '#bbb' }}>—</span>
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_: any, record: Receipt) => (
        <Space size={4} wrap>
          <Button
            type="link"
            onClick={(event) => {
              event.stopPropagation();
              onPrintReceipt(record);
            }}
          >
            {t('printReceipt')}
          </Button>
          <Button
            type="link"
            className="patient-whatsapp-button"
            icon={<WhatsAppOutlined />}
            loading={sendingWA === record._id}
            disabled={!selectedPatient?.patient_phone || (record as any)._offlinePending}
            onClick={(event) => {
              event.stopPropagation();
              sendPrescriptionViaWhatsApp(record);
            }}
            title={selectedPatient?.patient_phone ? `Send to ${selectedPatient.patient_phone}` : 'No phone number'}
          >
            WhatsApp
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            loading={deletingPrescriptionId === record._id}
            disabled={(record as any)._offlinePending}
            onClick={(event) => {
              event.stopPropagation();
              handleDeletePrescription(record);
            }}
          >
            {isRTL ? 'حذف' : 'Remove'}
          </Button>
        </Space>
      )
    }
  ];

  const buildLabOrdersHtml = (requestsOverride?: ExternalServiceRequest[]): string | null => {
    const pendingRequests = requestsOverride ?? externalRequests.filter(r => r.status === 'pending');

    if (pendingRequests.length === 0) {
      return null;
    }

    const printSettings = doctorSettings.printSettings || {
      paperSize: 'a4',
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      showHeader: true,
      showFooter: true,
      showPatientInfo: true
    };

    const isCustomPaper = !printSettings.showHeader;

    // Build examination section HTML from medical history data if it exists
    let examHtml = '';
    if (visibleMedicalHistory && visibleMedicalHistory.template_snapshot) {
      const tpl = visibleMedicalHistory.template_snapshot;
      const data = visibleMedicalHistory.data || {};
      const rows: string[] = [];

      (tpl.sections || []).forEach((section: any) => {
        const sectionRows: string[] = [];
        (section.fields || []).forEach((field: any) => {
          const key = `${section.section_id}.${field.field_id}`;
          const val = data[key];
          if (val !== undefined && val !== null && val !== '' && val !== 'false') {
            const displayVal = val === 'true' ? '✓' : val;
            sectionRows.push(`<tr><td class="exam-label">${field.label}</td><td class="exam-val">${displayVal}</td></tr>`);
          }
        });
        if (sectionRows.length > 0) {
          rows.push(`
            <div class="exam-section-name">${section.section_name || ''}</div>
            <table class="exam-table">${sectionRows.join('')}</table>
          `);
        }
      });

      if (rows.length > 0) {
        examHtml = `
          <div class="section-title" style="margin-top:16px">Patient Examination / الفحص</div>
          ${rows.join('')}
        `;
      }
    }

    const diagnosisHtml = latestVisitDiagnosis
      ? `<div class="latest-diagnosis">
              <p style="margin:0 0 6px"><strong>Latest diagnosis / آخر تشخيص</strong></p>
              <p style="margin:0; line-height:1.5; white-space:pre-wrap">${latestVisitDiagnosis.replace(/\n/g, '<br/>')}</p>
            </div>`
      : '';

    const printHtml = `
      <html>
        <head>
          <title>طلب فحوصات</title>
          <style>
            @page {
              size: ${printSettings.paperSize === 'custom' ? 'auto' : printSettings.paperSize};
              margin: 0;
            }
            html, body { font-family: Arial, sans-serif; direction: ${isRTL ? 'rtl' : 'ltr'}; margin: 0; padding: 0; }
            .top-spacer { display: block; height: ${((printSettings.marginTop || 0) / 2)}mm; width: 100%; }
            .content {
              padding-top: 0;
              padding-left: calc(${((printSettings.marginLeft || 0) / 2)}mm + 10px);
              padding-right: calc(${((printSettings.marginRight || 0) / 2)}mm + 10px);
              padding-bottom: 0;
              box-sizing: border-box;
            }
            .patient-info {
              margin-bottom: 16px; padding: 10px;
              background-color: ${isCustomPaper ? 'transparent' : '#f8f8f8'};
              display: ${printSettings.showPatientInfo ? 'block' : 'none'};
            }
            .patient-info p { margin: 4px 0; font-size: 14px; }
            .section-title { font-size: 16px; font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 6px; margin-bottom: 12px; }
            .service-item { display: flex; align-items: center; padding: 8px 4px; border-bottom: 1px dashed #ddd; font-size: 15px; }
            .service-num { font-weight: bold; margin-left: 10px; min-width: 24px; }
            .service-name { flex: 1; }
            .exam-section-name { font-size: 13px; font-weight: bold; color: #444; margin: 8px 0 4px; }
            .exam-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 8px; }
            .exam-table tr { border-bottom: 1px solid #eee; }
            .exam-label { color: #666; padding: 3px 6px 3px 0; white-space: nowrap; width: 40%; }
            .exam-val { padding: 3px 0; font-weight: 500; }
            .latest-diagnosis {
              margin-bottom: 16px;
              padding: 10px 12px;
              background-color: ${isCustomPaper ? 'transparent' : '#f0f7ff'};
              border: ${isCustomPaper ? 'none' : '1px solid #d6e4ff'};
              border-radius: 4px;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="top-spacer"></div>
          <div class="content">
            <div class="patient-info">
              <p><strong>Patient:</strong> ${selectedPatient?.patient_name || ''}</p>
              <p><strong>Age:</strong> ${selectedPatient?.age || ''}</p>
              <p><strong>Phone:</strong> ${selectedPatient?.patient_phone || ''}</p>
              <p><strong>Date:</strong> ${moment().format('DD-MM-YYYY')}</p>
            </div>
            ${diagnosisHtml}
            <div class="section-title">Lab & Radiology Orders</div>
            ${pendingRequests.map((req, i) => `
              <div class="service-item">
                <span class="service-num">${i + 1}.</span>
                <span class="service-name">${req.service_name}</span>
              </div>
            `).join('')}
            ${examHtml}
          </div>
        </body>
      </html>
    `;

    return printHtml;
  };

  const handlePrintExternalServices = (requestsOverride?: ExternalServiceRequest[]) => {
    const html = buildLabOrdersHtml(requestsOverride);
    if (html) triggerPrint(html);
  };

  // ── WhatsApp-optimised external services HTML (all statuses, mobile card layout) ──
  const buildExternalServicesWhatsAppHtml = (requests: ExternalServiceRequest[]): string | null => {
    if (!requests.length) return null;
    const dir = isRTL ? 'rtl' : 'ltr';
    const dateStr = moment().format('DD/MM/YYYY');
    const patientLabel = isRTL ? 'المريض' : 'Patient';
    const dateLabel = isRTL ? 'التاريخ' : 'Date';
    const titleLabel = isRTL ? 'الخدمات الخارجية' : 'External Services';
    const completedLabel = isRTL ? 'مكتمل' : 'Done';
    const pendingLabel = isRTL ? 'قيد الانتظار' : 'Pending';
    const clinicName = (doctorSettings as any).clinicName || '';
    const doctorTitle = (doctorSettings as any).doctorTitle || '';

    const rows = requests.map((r: any, i: number) => {
      const isDone = r.status === 'completed';
      const bg = isDone ? '#f6ffed' : '#fff7e6';
      const border = isDone ? '#b7eb8f' : '#ffd591';
      const statusBg = isDone ? '#52c41a' : '#faad14';
      const statusText = isDone ? completedLabel : pendingLabel;
      const icon = isDone ? '✅' : '⏳';
      return `<div style="display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;background:${bg};border:1px solid ${border};margin-bottom:8px">
        <span style="font-size:18px">${icon}</span>
        <div style="flex:1">
          <div style="font-weight:600;direction:${dir}">${i + 1}. ${r.service_name}</div>
          ${r.provider_name ? `<div style="font-size:12px;color:#888">${r.provider_name}</div>` : ''}
        </div>
        <span style="font-size:11px;background:${statusBg};color:#fff;padding:2px 8px;border-radius:10px">${statusText}</span>
      </div>`;
    }).join('');

    return `<!DOCTYPE html><html lang="${isRTL ? 'ar' : 'en'}" dir="${dir}"><head>
<meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${titleLabel}</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Noto Sans Arabic',Arial,sans-serif;background:#f0f2f5;display:flex;justify-content:center;padding:16px;min-height:100vh;direction:${dir}}
  .card{background:#fff;border-radius:16px;padding:24px;max-width:500px;width:100%;box-shadow:0 4px 20px rgba(0,0,0,.1)}
  .clinic-header{text-align:center;border-bottom:2px solid #13c2c2;padding-bottom:12px;margin-bottom:16px}
  .doc-title{font-size:20px;font-weight:700;color:#13c2c2;text-align:center;margin-bottom:8px}
  .patient-strip{background:#f8f8f8;border-radius:8px;padding:8px 12px;font-size:13px;direction:${dir};margin-bottom:14px;color:#555}
  .footer{text-align:center;color:#aaa;font-size:12px;margin-top:16px;padding-top:12px;border-top:1px solid #f0f0f0}
</style></head><body><div class="card">
  <div class="clinic-header">
    ${clinicName ? `<h2 style="margin:3px 0">${clinicName}</h2>` : ''}
    ${doctorTitle ? `<p style="margin:3px 0;color:#555">${doctorTitle}</p>` : ''}
  </div>
  <div class="doc-title">${titleLabel}</div>
  <div class="patient-strip">${patientLabel}: <strong>${selectedPatient?.patient_name || ''}</strong> &nbsp;·&nbsp; ${dateLabel}: ${dateStr}</div>
  ${latestVisitDiagnosis ? `<div style="margin-bottom:14px;padding:10px 12px;background:#f6ffed;border:1px solid #b7eb8f;border-radius:8px;font-size:13px;line-height:1.5;direction:${dir}"><strong>Latest diagnosis / آخر تشخيص:</strong><br/>${latestVisitDiagnosis.replace(/\n/g, '<br/>')}</div>` : ''}
  <div>${rows}</div>
  <div class="footer">DrWaiting</div>
</div></body></html>`;
  };

  if (loading) {
    return <div>{t('loadingPatientHistory')}</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="patient-detail-page">
      <div className="patient-summary-sticky">
        <div className="patient-summary-card">
              <div className="patient-summary-identity">
                <div className="patient-summary-avatar" aria-hidden="true">
                  {(selectedPatient.patient_name || '?').trim().charAt(0)}
                </div>
                <div className="patient-summary-identity-text">
                  <Text strong className="patient-summary-name">{selectedPatient.patient_name}</Text>
                  {selectedPatient.file_number && (
                    <span className="patient-summary-file">{t('fileIdTag')} {selectedPatient.file_number}</span>
                  )}
                </div>
              </div>

              <div className="patient-summary-item">
                <div className="patient-summary-icon"><DollarOutlined /></div>
                <div className="patient-summary-content">
                  <span className="patient-summary-label">{language === 'ar' ? 'طريقة الدفع' : 'Payment'}</span>
                  <span className="patient-summary-value">
                    {(selectedPatient as any).payment_type === 'insurance'
                      ? (language === 'ar' ? 'تعاقد' : 'Insurance')
                      : (language === 'ar' ? 'نقدي' : 'Cash')}
                  </span>
                </div>
              </div>

              {(selectedPatient as any).insurance_company_id && (
                <div className="patient-summary-item">
                  <div className="patient-summary-icon"><CheckCircleOutlined /></div>
                  <div className="patient-summary-content">
                    <span className="patient-summary-label">{language === 'ar' ? 'التأمين' : 'Insurance'}</span>
                    <span className="patient-summary-value patient-summary-value--wrap">
                      {(selectedPatient as any).insurance_company_name || (selectedPatient as any).insurance_company_id}
                      {(selectedPatient as any).insurance_number ? ` · ${(selectedPatient as any).insurance_number}` : ''}
                      {(selectedPatient as any).coverage_percentage ? (
                        <span className="patient-summary-badge">{(selectedPatient as any).coverage_percentage}%</span>
                      ) : null}
                    </span>
                  </div>
                </div>
              )}

              {String(selectedPatient.nationality || '').trim() && (
                <div className="patient-summary-item">
                  <div className="patient-summary-icon" aria-hidden="true">🌐</div>
                  <div className="patient-summary-content">
                    <span className="patient-summary-label">{language === 'ar' ? 'الجنسية' : 'Nationality'}</span>
                    <span className="patient-summary-value">{String(selectedPatient.nationality).trim()}</span>
                  </div>
                </div>
              )}

              <div className="patient-summary-item patient-summary-item--visit">
                <div className="patient-summary-icon"><CalendarOutlined /></div>
                <div className="patient-summary-content">
                  <span className="patient-summary-label">{language === 'ar' ? 'الزيارة' : 'Visit'}</span>
                  {!isViewerOnly && editingVisitType ? (
                    <div className="patient-summary-editor">
                      <Select
                        value={selectedVisitTypeId}
                        onChange={setSelectedVisitTypeId}
                        style={{ minWidth: 140 }}
                        size="small"
                      >
                        {selectableVisitTypes(availableVisitTypes, selectedVisitTypeId).map(type => (
                          <Select.Option
                            key={type.type_id}
                            value={type.type_id}
                            disabled={type.is_active === false}
                          >
                            {type.name} ({type.name_ar}){type.is_active === false ? ' — Inactive' : ''}
                          </Select.Option>
                        ))}
                        {selectedVisitTypeId && !availableVisitTypes.find(type => type.type_id === selectedVisitTypeId) && (
                          <Select.Option key={selectedVisitTypeId} value={selectedVisitTypeId}>
                            {selectedVisitTypeId}
                          </Select.Option>
                        )}
                      </Select>
                      <Radio.Group
                        value={selectedUrgency}
                        onChange={(event) => setSelectedUrgency(event.target.value)}
                        buttonStyle="solid"
                        size="small"
                      >
                        <Radio.Button value="normal">{t('urgencyNormal')}</Radio.Button>
                      </Radio.Group>
                      <Button size="small" onClick={() => setEditingVisitType(false)}>{t('cancel')}</Button>
                      <Button type="primary" size="small" loading={savingVisitType} onClick={handleUpdateVisitType}>{t('save')}</Button>
                    </div>
                  ) : (
                    <span className="patient-summary-value patient-summary-value--wrap">
                      {resolveVisitTypeName(selectedPatient.visit_type || '') || t('visitRegular')}
                      <span className={`patient-summary-status ${(selectedPatient as any).visit_urgency === 'urgent' ? 'is-urgent' : ''}`}>
                        {(selectedPatient as any).visit_urgency === 'urgent' ? t('urgencyUrgent') : t('urgencyNormal')}
                      </span>
                      {!isViewerOnly && <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        aria-label={t('visitChangeType')}
                        title={t('visitChangeType')}
                        onClick={() => setEditingVisitType(true)}
                        className="patient-summary-edit"
                      />}
                    </span>
                  )}
                </div>
              </div>

              {(() => {
                const history = getLatestOwnedVisit()?.transfer_history || [];
                const latestTransfer = history[history.length - 1];
                if (!latestTransfer) return null;
                const isReceivingDoctor = latestTransfer.to_doctor_id === selectedPatient.doctor_id;
                const doctorLabel = isReceivingDoctor
                  ? (latestTransfer.from_doctor_name || latestTransfer.from_doctor_id)
                  : (latestTransfer.to_doctor_name || latestTransfer.to_doctor_id);
                return (
                  <Tooltip title={latestTransfer.reason || undefined}>
                    <div className="patient-summary-item">
                      <div className="patient-summary-icon"><SwapOutlined /></div>
                      <div className="patient-summary-content">
                        <span className="patient-summary-label">
                          {isReceivingDoctor
                            ? (language === 'ar' ? 'محولة من' : 'Transferred from')
                            : (language === 'ar' ? 'محولة إلى' : 'Transferred to')}
                        </span>
                        <span className="patient-summary-value">{language === 'ar' ? 'د. ' : 'Dr. '}{doctorLabel}</span>
                      </div>
                    </div>
                  </Tooltip>
                );
              })()}

              {selectedPatient.address && (
                <div className="patient-summary-item patient-summary-item--address">
                  <div className="patient-summary-icon"><AimOutlined /></div>
                  <div className="patient-summary-content">
                    <span className="patient-summary-label">{language === 'ar' ? 'العنوان' : 'Address'}</span>
                    {!isViewerOnly && editingAddress ? (
                      <div className="patient-summary-editor">
                        <Input
                          size="small"
                          value={addressInputValue}
                          onChange={event => setAddressInputValue(event.target.value)}
                          onPressEnter={handleSaveAddress}
                        />
                        <Button size="small" onClick={() => setEditingAddress(false)}>{t('cancel')}</Button>
                        <Button type="primary" size="small" loading={savingAddress} onClick={handleSaveAddress}>{t('save')}</Button>
                      </div>
                    ) : (
                      <span className="patient-summary-value patient-summary-value--wrap">
                        {selectedPatient.address}
                        {!isViewerOnly && <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          aria-label={language === 'ar' ? 'تعديل العنوان' : 'Edit address'}
                          title={language === 'ar' ? 'تعديل العنوان' : 'Edit address'}
                          onClick={() => { setAddressInputValue(selectedPatient.address || ''); setEditingAddress(true); }}
                          className="patient-summary-edit"
                        />}
                      </span>
                    )}
                  </div>
                </div>
              )}
        </div>
      </div>
      <Card
        className="patient-detail-card"
        title={
          <div>
            <div className="patient-action-card">
              <div className="patient-action-main-row">
                {!isViewerOnly && <div className="patient-action-primary">
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    onClick={() => setBillingModalVisible(true)}
                    className="patient-action-button patient-action-button--bill"
                  >
                    {t('createBill')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={() => setUploadReportModalVisible(true)}
                    className="patient-action-button patient-action-button--bill"
                  >
                    {t('uploadReport')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsReceiptModalVisible(true)}
                    className="patient-action-button patient-action-button--prescription"
                  >
                    {t('addPrescription')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => setMedicalHistoryModalVisible(true)}
                    className="patient-action-button patient-action-button--history"
                  >
                    {t('medicalHistoryButton')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<SoundOutlined />}
                    onClick={() => voiceNoteSectionRef.current?.openRecorder()}
                    className="patient-action-button patient-action-button--voice-note"
                  >
                    {t('voiceNoteAddNew')}
                  </Button>
                  <Dropdown
                    trigger={['click']}
                    placement={isRTL ? 'bottomRight' : 'bottomLeft'}
                    overlayClassName="patient-billing-menu"
                    menu={{
                      onClick: ({ key }) => handleBillingMenuAction(key),
                      items: [
                        { key: 'receipt', icon: <PrinterOutlined />, label: language === 'ar' ? 'إيصال الخدمات' : 'Services receipt', disabled: billingReceiptLoading },
                        { key: 'package', icon: <GiftOutlined />, label: t('assignPackage') },
                        { key: 'insurance', icon: <ExperimentOutlined />, label: language === 'ar' ? 'برنامج التأمين والجلسات' : 'Insurance & sessions' },
                        { key: 'discount', icon: <PercentageOutlined />, label: t('discountButton') },
                        { key: 'inventory', icon: <InboxOutlined />, label: language === 'ar' ? 'خصم من المخزون' : 'Deduct inventory' },
                        { key: 'refund', icon: <DollarOutlined />, label: language === 'ar' ? 'استرداد كل المبلغ' : 'Refund all', danger: true, disabled: refundAllSubmitting },
                      ]
                    }}
                  >
                    <Button className="patient-billing-trigger">
                      {language === 'ar' ? 'الحسابات' : 'Billing'} <DownOutlined />
                    </Button>
                  </Dropdown>
                </div>}

                <div className="patient-action-nav">
                  <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBackToList}>
                    {t('backToPatientList')}
                  </Button>
                  {!isViewerOnly && <Dropdown
                    trigger={['click']}
                    placement={isRTL ? 'bottomLeft' : 'bottomRight'}
                    overlayClassName="patient-options-menu"
                    menu={{
                      onClick: ({ key }) => {
                        if (key === 'delete-file') handleArchivePatientFile();
                      },
                      items: [{
                        key: 'delete-file',
                        danger: true,
                        disabled: archivingPatientFile,
                        icon: <DeleteOutlined />,
                        label: language === 'ar' ? 'حذف ملف المريض' : 'Delete patient file'
                      }]
                    }}
                  >
                    <Button
                      type="text"
                      icon={<MoreOutlined />}
                      loading={archivingPatientFile}
                      className="patient-options-trigger"
                    >
                      {language === 'ar' ? 'حذف ملف المريض' : 'Delete patient file'} <DownOutlined />
                    </Button>
                  </Dropdown>}
                </div>
              </div>

              {!isViewerOnly && <div className="patient-action-secondary">
                {sessionDoctorId && (
                  <Button icon={<CalendarOutlined />} onClick={() => setNextVisitModalVisible(true)}>
                    {t('scheduleReminder')}
                  </Button>
                )}
                <Button icon={<BellOutlined />} onClick={() => setNotificationModalVisible(true)}>
                  {t('notifyAssistant')}
                </Button>
                <Button icon={<FileTextOutlined />} onClick={() => { setMedicalReportToEdit(null); setMedicalReportModalVisible(true); }}>
                  {t('medicalReport')}
                </Button>
                <Button icon={<SwapOutlined />} onClick={() => { setReferralToEdit(null); setReferralModalVisible(true); }}>
                  {t('referralLetter')}
                </Button>
                {sessionDoctorId && selectedPatient.doctor_id === sessionDoctorId && (() => {
                  const latestVisit = getLatestOwnedVisit();
                  return !latestVisit?.owner_doctor_id || latestVisit.owner_doctor_id === sessionDoctorId;
                })() && (
                  <Button icon={<SwapOutlined />} onClick={openTransferModal} loading={transferDoctorsLoading}>
                    {language === 'ar' ? 'تحويل الملف داخل العيادة' : 'Transfer file within clinic'}
                  </Button>
                )}
                {(doctorSettings as any)?.diagramType === 'dental' && (() => {
                  const currentVisit = patientHistory?.visits?.[0];
                  const visitId = currentVisit?.visit_id || (currentVisit as any)?._id;
                  if (!visitId) return null;
                  return (
                    <Button
                      icon={<AimOutlined />}
                      onClick={() => {
                        setDiagramVisitId(String(visitId));
                        setDiagramInitialData((currentVisit as any)?.diagram_data || null);
                        setDiagramModalVisible(true);
                      }}
                    >
                      {(doctorSettings as any).diagramType === 'dental' ? t('diagramDental') : t('diagramBody')}
                    </Button>
                  );
                })()}
              </div>}
            </div>
          </div>
        }
      >
        {/* Package balance */}
        {(visibleAllPatientPackages.length > 0 || visibleActivePackages.length > 0) && (
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f5ff' }} title={<Space><GiftOutlined /> {t('packageBalance')}</Space>}>
            <List
              size="small"
              dataSource={visibleAllPatientPackages.length > 0 ? visibleAllPatientPackages : visibleActivePackages}
              renderItem={(pp: PatientPackage) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Space><Text strong>{pp.package_name}</Text>{(pp as any)._offlinePending && <Tag color="gold">{isRTL ? 'في انتظار المزامنة' : 'Pending sync'}</Tag>}</Space>
                    <Text type="secondary">
                      {t('packageRemainingLabel')}: {pp.remaining_sessions} / {pp.total_sessions} · {t('packageStatusLabel')}:{' '}
                      <Tag color={pp.status === 'active' ? 'green' : pp.status === 'finished' ? 'blue' : 'default'}>
                        {pp.status === 'active' ? t('active') : pp.status === 'finished' ? t('patientPackageFinished') : pp.status === 'expired' ? t('patientPackageExpired') : pp.status}
                      </Tag>
                    </Text>
                    {pp.expiry_date && <Text type="secondary">{t('packageExpiresLabel')}: {moment(pp.expiry_date).format('YYYY-MM-DD')}</Text>}
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        )}

        {(patientVitalsLoading || latestVitals) && (
          <Card
            size="small"
            bodyStyle={{ padding: '10px 14px' }}
            headStyle={{ minHeight: 38, padding: '0 14px', fontSize: 14, fontWeight: 700 }}
            style={{ marginBottom: 12, backgroundColor: '#fffaf5', borderColor: '#f1c79f' }}
            title={<Space size={6}><ExperimentOutlined /> {t('vitals')}</Space>}
            loading={patientVitalsLoading}
          >
            {latestVitals ? (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Row gutter={[6, 6]}>
                {vitalDisplayRows(latestVitals).map(([label, value]) => (
                  <Col xs={12} sm={8} md={4} key={String(label)}>
                    <div style={{ background: '#fff', border: '1px solid #d9f7be', borderRadius: 8, padding: '6px 8px', minHeight: 46 }}>
                      <div style={{ color: '#6b7280', fontSize: 11, lineHeight: 1.2 }}>{label}</div>
                      <Text strong style={{ fontSize: 14, lineHeight: 1.3 }}>{value}</Text>
                    </div>
                  </Col>
                ))}
              </Row>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('recordedAt')}: {latestVitals.recorded_at ? moment(latestVitals.recorded_at).format('DD MMM YYYY HH:mm') : '-'}
                {latestVitals.recorded_by ? ` · ${t('recordedBy')}: ${latestVitals.recorded_by}` : ''}
              </Text>
              {latestVitals.notes && (
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, background: '#fff', border: '1px solid #edf7df', borderRadius: 8, padding: '6px 8px' }}>
                  <Text strong>{t('notes')}: </Text>{latestVitals.notes}
                </div>
              )}
              {previousVitals.length > 0 && (
                <div style={{ marginTop: 2 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#389e0d', marginBottom: 6 }}>
                    {t('previousVitalsRecords')}
                  </div>
                <List
                  size="small"
                  split={false}
                  dataSource={previousVitals}
                  renderItem={(vital) => {
                    const rows = vitalDisplayRows(vital);
                    return (
                    <List.Item style={{ padding: '6px 8px', marginBottom: 6, background: '#fff', border: '1px solid #edf7df', borderRadius: 8 }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
                          {vital.recorded_at ? moment(vital.recorded_at).format('DD MMM YYYY HH:mm') : '-'}
                        </Text>
                        <Space size={[4, 4]} wrap>
                          {rows.length > 0
                            ? rows.map(([label, value]) => (
                                <Tag key={`${label}-${value}`} color="green" style={{ marginInlineEnd: 0, fontSize: 12, lineHeight: '20px' }}>
                                  {label}: <strong>{value}</strong>
                                </Tag>
                              ))
                            : <Text type="secondary" style={{ fontSize: 12 }}>{t('noVitalsValues')}</Text>}
                        </Space>
                        {vital.notes && (
                          <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                            <Text strong>{t('notes')}: </Text>{vital.notes}
                          </Text>
                        )}
                      </Space>
                    </List.Item>
                    );
                  }}
                />
                </div>
              )}
            </Space>
            ) : null}
          </Card>
        )}

        <Divider />

        {/* Complaint & Diagnosis history */}
        <Title level={4}>
          <FileTextOutlined style={{ color: '#52c41a' }} /> {t('complaintDiagnosisHeading')}
        </Title>

        {/* Add new entry form */}
        {addComplaintVisible ? (
          <div className="complaint-entry-form">
            <Row gutter={[12, 12]} className="complaint-entry-fields">
              <Col xs={24} sm={8}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('complaint')}</div>
                <AutoComplete
                  value={newComplaint}
                  options={filterComplaintsSuggestion(complaintInput)}
                  filterOption={false}
                  style={{ width: '100%' }}
                  onChange={(val) => { setNewComplaint(val); setComplaintInput(val); }}
                  onSelect={(val) => { setNewComplaint(val); setComplaintInput(val); }}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder={t('enterComplaintPlaceholder')}
                    style={{ fontSize: 14 }}
                  />
                </AutoComplete>
                {complaints.slice(0, CHIP_LIMIT).length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {complaints.slice(0, CHIP_LIMIT).map((c) => (
                      <Tag
                        key={c}
                        style={{ cursor: 'pointer', marginBottom: 0 }}
                        onClick={() => { setNewComplaint(c); setComplaintInput(c); }}
                      >
                        {c}
                      </Tag>
                    ))}
                  </div>
                )}
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('diagnosis')}</div>
                <AutoComplete
                  value={newDiagnosis}
                  options={filterDiagnosesSuggestion(diagnosisInput)}
                  filterOption={false}
                  style={{ width: '100%' }}
                  onChange={(val) => { setNewDiagnosis(val); setDiagnosisInput(val); }}
                  onSelect={(val) => { setNewDiagnosis(val); setDiagnosisInput(val); }}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder={t('enterDiagnosisPlaceholder')}
                    style={{ fontSize: 14 }}
                  />
                </AutoComplete>
                {diagnoses.slice(0, CHIP_LIMIT).length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {diagnoses.slice(0, CHIP_LIMIT).map((d) => (
                      <Tag
                        key={d}
                        style={{ cursor: 'pointer', marginBottom: 0 }}
                        onClick={() => { setNewDiagnosis(d); setDiagnosisInput(d); }}
                      >
                        {d}
                      </Tag>
                    ))}
                  </div>
                )}
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('procedure')}</div>
                <Input.TextArea
                  rows={3}
                  value={newProcedure}
                  onChange={(event) => setNewProcedure(event.target.value)}
                  placeholder={t('enterProcedurePlaceholder')}
                  style={{ fontSize: 14 }}
                />
              </Col>
              <Col xs={24} className="complaint-entry-actions">
                <Button
                  type="primary"
                  loading={addComplaintLoading}
                  className="complaint-entry-save"
                  onClick={async () => {
                    if (!newComplaint.trim() && !newDiagnosis.trim() && !newProcedure.trim()) return;
                    try {
                      setAddComplaintLoading(true);
                      const doctorId = selectedPatient?.doctor_id || localStorage.getItem('doctorId');
                      const currentUserId = localStorage.getItem('doctorId');
                      const resolvedDoctorName = username || doctorName || doctorSettings.doctorTitle || '';
                      const saveResult = await saveComplaintWithOffline({
                        ...buildComplaintIdentityPayload({
                          ...(selectedPatient || {}),
                          doctor_id: doctorId || '',
                        }),
                        complaint: newComplaint.trim(),
                        diagnosis: newDiagnosis.trim(),
                        procedure: newProcedure.trim(),
                        doctor_name: resolvedDoctorName,
                        added_by_doctor_id: currentUserId || undefined,
                      });
                      if (newComplaint.trim()) saveComplaintSuggestion(newComplaint.trim());
                      if (newDiagnosis.trim()) saveDiagnosisSuggestion(newDiagnosis.trim());
                      setNewComplaint('');
                      setNewDiagnosis('');
                      setNewProcedure('');
                      setComplaintInput('');
                      setDiagnosisInput('');
                      setAddComplaintVisible(false);
                      if (saveResult.queued) {
                        message.warning(isRTL
                          ? 'تم الحفظ على هذا الجهاز وسيظهر الآن في السجل. ستتم المزامنة تلقائياً عند عودة الإنترنت.'
                          : 'Saved on this device and shown in the history. It will sync automatically when the internet connection returns.');
                      } else {
                        const refreshed = await fetchPatientHistory();
                        message.success(refreshed ? 'Complaint and diagnosis saved' : 'Saved, but the patient file could not refresh');
                      }
                    } catch (err: any) {
                      console.error('Failed to add complaint entry', err);
                      message.error(err?.response?.data?.message || 'Failed to save complaint and diagnosis');
                    } finally {
                      setAddComplaintLoading(false);
                    }
                  }}
                >
                  {t('save')}
                </Button>
                <Button className="complaint-entry-cancel" onClick={() => { setAddComplaintVisible(false); setNewComplaint(''); setNewDiagnosis(''); setNewProcedure(''); setComplaintInput(''); setDiagnosisInput(''); }}>
                  {t('cancel')}
                </Button>
              </Col>
            </Row>
          </div>
        ) : (
          <Button
            icon={<PlusOutlined />}
            style={{ marginBottom: 12 }}
            onClick={() => setAddComplaintVisible(true)}
          >
            {t('addComplaintDiagnosis')}
          </Button>
        )}

        <div style={{ overflowX: 'auto', marginBottom: 24 }}>
          <Table
            size="small"
            dataSource={(() => {
              // Combine complaint_history entries + visit-level entries, dedupe, sort newest first
              const fromHistory = [...(patientHistory?.complaint_history || []), ...pendingComplaintEntries]
                .filter((e: any) => !isSoftDeleted(e))
                .map((e: any) => ({
                  _key: e._id || e.date,
                  date: e.date,
                  complaint: e.complaint,
                  diagnosis: e.diagnosis,
                  procedure: e.procedure || '',
                  doctor_id: e.doctor_id || '',
                  patient_mongo_id: e.patient_mongo_id || '',
                  doctor_name: e.doctor_name || '',
                  _offlinePending: !!e._offlinePending,
                  _offlineStatus: e._offlineStatus || '',
                  source: 'history'
                }));
              const fromVisits = (patientHistory?.visits || [])
                .filter((v: Visit) => v.complaint || v.diagnosis)
                .map((v: Visit) => ({
                  _key: v.visit_id || v._id,
                  date: v.date,
                  complaint: v.complaint,
                  diagnosis: v.diagnosis,
                  procedure: '',
                  doctor_name: (v as any).doctor_name || '',
                  source: 'visit'
                }));
              return [...fromHistory, ...fromVisits].sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
            })()}
            rowKey={(r: any) => r._key || r.date}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: t('noComplaintDiagnosisYet') }}
            columns={[
              {
                title: t('patientColDate'),
                dataIndex: 'date',
                key: 'date',
                width: 160,
                render: (date: string, record: any) =>
                  date
                    ? record.source === 'history'
                      ? moment(date).format('DD MMM YYYY · HH:mm')
                      : moment(date).format('DD MMM YYYY')
                    : '—'
              },
              {
                title: t('complaint'),
                dataIndex: 'complaint',
                key: 'complaint',
                render: (text: string) => text
                  ? <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
                  : <span style={{ color: '#bbb' }}>—</span>
              },
              {
                title: t('diagnosis'),
                dataIndex: 'diagnosis',
                key: 'diagnosis',
                render: (text: string) => text
                  ? <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
                  : <span style={{ color: '#bbb' }}>—</span>
              },
              {
                title: t('procedure'),
                dataIndex: 'procedure',
                key: 'procedure',
                render: (text: string) => text
                  ? <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
                  : <span style={{ color: '#bbb' }}>—</span>
              },
              {
                title: t('patientColDoctor'),
                dataIndex: 'doctor_name',
                key: 'doctor_name',
                width: 150,
                render: (name: string) => name
                  ? <span style={{ color: '#1677ff' }}>د. {name}</span>
                  : <span style={{ color: '#bbb' }}>—</span>
              },
              {
                title: '',
                key: 'actions',
                width: 110,
                render: (_: any, record: any) =>
                  record.source === 'history' && record._key && !record._offlinePending ? (
                    <Space size="small">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                          setEditComplaintEntry({
                            _id: record._key,
                            complaint: record.complaint || '',
                            diagnosis: record.diagnosis || '',
                            procedure: record.procedure || '',
                            doctor_id: record.doctor_id || '',
                            patient_mongo_id: record.patient_mongo_id || '',
                          });
                          setEditComplaintValue(record.complaint || '');
                          setEditDiagnosisValue(record.diagnosis || '');
                          setEditProcedureValue(record.procedure || '');
                        }}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => handleDeleteComplaintHistory(record)}
                      />
                    </Space>
                  ) : null
              }
            ]}
          />
        </div>

        <Divider />

        {/* Prescription History */}
        <Title level={4}>{t('prescriptionHistoryTitle')}</Title>
        <div style={{ overflowX: 'auto' }}>
          <Table
            dataSource={allReceipts}
            columns={receiptsColumns}
            rowKey={(record: any) => record._id || record.receipt_id}
            scroll={{ x: 'max-content' }}
            onRow={(record) => {
              return {
                onClick: () => {
                  if ((record as any)._offlinePending) return;
                  const visit = patientHistory?.visits.find(v =>
                    v.receipts?.some(r => r._id === record._id)
                  );
                  if (visit) {
                    showVisitDetails(visit);
                  }
                }
              };
            }}
            pagination={{
              pageSize: 10,
              responsive: true
            }}
          />
        </div>

        <Divider />

        {/* Service History */}
        <Title level={4}>
          <DollarOutlined style={{ color: '#52c41a' }} /> Service History
        </Title>
        <div style={{ overflowX: 'auto', marginBottom: 24 }}>
          <Table
            size="small"
            dataSource={serviceHistoryRows}
            rowKey="key"
            loading={patientBillingsLoading}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            locale={{ emptyText: 'No services used yet' }}
            columns={[
              {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                width: 150,
                render: (date: string, record: any) => (
                  <Space size={4} wrap>
                    <span>{date ? moment(date).format('DD MMM YYYY') : '-'}</span>
                    {record._offlinePending && <Tag color="gold">{isRTL ? 'في انتظار المزامنة' : 'Pending sync'}</Tag>}
                  </Space>
                )
              },
              {
                title: 'Service',
                dataIndex: 'service_name',
                key: 'service_name',
                render: (name: string) => <Text strong>{name}</Text>
              },
              {
                title: 'Qty',
                dataIndex: 'quantity',
                key: 'quantity',
                width: 70
              },
              ...(!isViewerOnly ? [{
                title: 'Amount',
                dataIndex: 'subtotal',
                key: 'subtotal',
                width: 120,
                render: (amount: number) => `${amount.toLocaleString()} EGP`
              }] : []),
              {
                title: 'Status',
                dataIndex: 'paymentStatus',
                key: 'paymentStatus',
                width: 120,
                render: (status: string) => (
                  <Tag color={status === 'paid' ? 'green' : status === 'partial' ? 'blue' : status === 'refund_due' ? 'orange' : 'default'}>
                    {status || 'pending'}
                  </Tag>
                )
              },
              {
                title: 'Notes',
                dataIndex: 'notes',
                key: 'notes',
                render: (notes: string) => notes ? <span style={{ whiteSpace: 'pre-wrap' }}>{notes}</span> : <span style={{ color: '#bbb' }}>-</span>
              }
            ]}
          />
        </div>

        {!isViewerOnly && (
          <>
            <Divider />

            {/* Voice Notes Section
                voice_notes live in patientHistory (fetched by getPatientVisitHistory).
                selectedPatient comes from the patient list and never carries voice_notes.
                We merge them here so VoiceNoteSection always sees the correct notes.
                On save/edit/delete we update patientHistory only — touching selectedPatient
                would re-trigger the [selectedPatient] useEffect and re-fetch everything. */}
            <VoiceNoteSection
              ref={voiceNoteSectionRef}
              patient={selectedPatient ? {
                ...selectedPatient,
                voice_notes: patientHistory?.voice_notes ?? selectedPatient.voice_notes ?? [],
              } as Patient : null}
              onPatientUpdate={(updated) => {
                if (updated.voice_notes !== undefined) {
                  setPatientHistory(prev => prev ? { ...prev, voice_notes: updated.voice_notes } : prev);
                }
              }}
              doctorSpecialization={doctorSettings?.doctorTitle || ''}
            />
          </>
        )}

        <Divider />

        {/* Reports Section */}
        <AssessmentFormsSection
          patient={selectedPatient}
          doctorId={sessionDoctorId || ''}
          clinicId={resolveAssessmentClinicId(selectedClinicId, selectedPatient.clinic_id, localStorage.getItem('clinicId'))}
          readOnly={isViewerOnly}
        />

        <Title level={4}>{t('reportsAndExaminations')}</Title>
        {reportsLoading ? (
          <div>{t('loadingReports')}</div>
        ) : visiblePatientReports.length > 0 ? (
          <div style={{ marginBottom: 24 }}>
            <Space wrap style={{ marginBottom: 16 }}>
              <Checkbox
                checked={selectedUploadedReportIds.length === visiblePatientReports.filter(report => !report._offlinePending).length}
                indeterminate={selectedUploadedReportIds.length > 0 && selectedUploadedReportIds.length < visiblePatientReports.filter(report => !report._offlinePending).length}
                onChange={(event) => {
                  setSelectedUploadedReportIds(
                    event.target.checked
                      ? visiblePatientReports.filter(report => !report._offlinePending).map((report) => report.report_id || report._id).filter(Boolean)
                      : []
                  );
                }}
              >
                Select all reports
              </Checkbox>
              <Button
                type="primary"
                className="patient-whatsapp-button"
                icon={<WhatsAppOutlined />}
                loading={sendingSelectedReportsWA}
                disabled={!selectedPatient?.patient_phone || selectedUploadedReportIds.length === 0}
                onClick={sendSelectedUploadedReportsViaWhatsApp}
              >
                Send selected in one WhatsApp link ({selectedUploadedReportIds.length})
              </Button>
            </Space>
            <Row gutter={[16, 16]}>
              {visiblePatientReports.map((report) => {
                const url = getImageUrl(report.image_url);
                const pdf = report._offlineMimeType === 'application/pdf' || isPdf(url);
                const reportKey = String(report.report_id || report._id || '');
                const handleOpen = () => {
                  if (pdf) {
                    const tab = window.open('', '_blank');
                    void savedMediaUrl(url).then(saved => {
                      if (tab) { tab.opener = null; tab.location.href = saved; }
                    }).catch(() => { tab?.close(); message.warning('This PDF has not been downloaded on this device.'); });
                  } else {
                    setPreviewImage(url);
                    setImagePreviewVisible(true);
                  }
                };
                const buildUploadedImageReportHtml = (): string => {
                  const reportDate = report.uploaded_at
                    ? moment(report.uploaded_at).format('DD-MM-YYYY')
                    : moment().format('DD-MM-YYYY');
                  const reportLabel = report.report_type
                    ? report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)
                    : 'Report';
                  return `
                    <html><head><title>${reportLabel}</title>
                    <style>
                      @page { size: auto; margin: 10mm; }
                      html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
                      .header { text-align: center; margin-bottom: 12px; font-size: 13px; color: #555; }
                      .diag { margin-bottom: 10px; font-size: 13px; color: #333; text-align: center; }
                      img { max-width: 100%; display: block; margin: 0 auto; }
                    </style></head>
                    <body>
                      <div class="header">
                        <strong>${selectedPatient?.patient_name || ''}</strong>
                        ${selectedPatient?.age ? ` &nbsp;|&nbsp; Age: ${selectedPatient.age}` : ''}
                        &nbsp;|&nbsp; ${reportLabel} &nbsp;|&nbsp; ${reportDate}
                      </div>
                      ${latestVisitDiagnosis ? `<div class="diag"><strong>Latest diagnosis / آخر تشخيص:</strong><br/>${latestVisitDiagnosis.replace(/\n/g, '<br/>')}</div>` : ''}
                      <img src="${url}" alt="${reportLabel}" />
                    </body></html>`;
                };
                const handlePrintReport = () => {
                  triggerPrint(buildUploadedImageReportHtml());
                };
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={report.report_id}>
                    <Card
                      hoverable
                      cover={
                        <div
                          onClick={handleOpen}
                          style={{ height: 150, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', cursor: 'pointer' }}
                        >
                          {pdf ? (
                            <div style={{ textAlign: 'center', color: '#d32f2f' }}>
                              <div style={{ fontSize: 52 }}>📄</div>
                              <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>PDF</div>
                              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Click to open</div>
                            </div>
                          ) : (
                            <OfflineImage
                              alt={report.description || 'Report'}
                              src={url}
                              loading="lazy"
                              decoding="async"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          )}
                        </div>
                      }
                      actions={[
                        pdf ? (
                          <span
                            key="open"
                            onClick={handleOpen}
                            style={{ cursor: 'pointer', color: '#1677ff', fontSize: 13 }}
                          >
                            <EyeOutlined /> Open PDF
                          </span>
                        ) : (
                          <EyeOutlined key="view" onClick={handleOpen} />
                        ),
                        ...(pdf
                          ? []
                          : [
                              <span
                                key="print"
                                onClick={handlePrintReport}
                                style={{ cursor: 'pointer', color: '#52c41a', fontSize: 13 }}
                              >
                                <PrinterOutlined /> Print
                              </span>,
                            ]),
                        <Button
                          key="wa"
                          type="text"
                          size="small"
                          className="patient-whatsapp-button"
                          icon={<WhatsAppOutlined />}
                          loading={sendingUploadedReportWA === (report.report_id || report._id)}
                          disabled={!selectedPatient?.patient_phone || report._offlinePending}
                          onClick={() => sendUploadedReportViaWhatsApp(report)}
                          style={{ fontSize: 13, padding: '0 8px' }}
                          title={selectedPatient?.patient_phone ? `Send to ${selectedPatient.patient_phone}` : 'No phone number'}
                        >
                          WhatsApp
                        </Button>,
                        <Button
                          key="delete"
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deletingReportId === report.report_id}
                          disabled={report._offlinePending}
                          onClick={() => handleDeleteUploadedReport(report)}
                          style={{ fontSize: 13, padding: '0 4px' }}
                        >
                          Delete
                        </Button>
                      ]}
                    >
                      {report._offlinePending && <Tag color="gold" style={{ marginBottom: 8 }}>{isRTL ? 'في انتظار المزامنة' : 'Pending sync'}</Tag>}
                      <Checkbox
                        checked={selectedUploadedReportIds.includes(reportKey)}
                        disabled={report._offlinePending}
                        onChange={(event) => {
                          setSelectedUploadedReportIds((prev) =>
                            event.target.checked
                              ? Array.from(new Set([...prev, reportKey]))
                              : prev.filter((id) => id !== reportKey)
                          );
                        }}
                        style={{ marginBottom: 10 }}
                      >
                        Select for combined WhatsApp link
                      </Checkbox>
                      <Card.Meta
                        title={
                          <div>
                            <div style={{ textTransform: 'capitalize', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span>{report.report_type || 'Examination'}</span>
                              {report.visible_to_patient === false && <Tag color="default">Hidden from patient</Tag>}
                            </div>
                            <div style={{ fontSize: 12, color: '#999' }}>
                              {moment(report.uploaded_at).format('YYYY-MM-DD HH:mm')}
                            </div>
                          </div>
                        }
                        description={
                          report.description ? (
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              {report.description}
                            </div>
                          ) : null
                        }
                      />
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ) : (
          <div style={{ marginBottom: 24, color: '#999' }}>No reports available</div>
        )}

        <Divider />

        {/* External Services Section */}
        <Title level={4}>
          <ExperimentOutlined /> External Services / الخدمات الخارجية
        </Title>
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAssignServiceModalVisible(true)}
              style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
            >
              {t('assignExternalServices')}
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={() => handlePrintExternalServices(visibleExternalRequests.filter(r => r.status === 'pending'))}
              disabled={visibleExternalRequests.filter(r => r.status === 'pending').length === 0}
              title={visibleExternalRequests.filter(r => r.status === 'pending').length === 0 ? 'No pending orders to print' : 'Print pending lab/radiology orders'}
            >
              طباعة الفحوصات المطلوبة
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={() => {
                const pending = visibleExternalRequests.filter(r => r.status === 'pending');
                setSelectedPrintIds(pending.map(r => r.request_id));
                setPrintSelectModalVisible(true);
              }}
              disabled={visibleExternalRequests.filter(r => r.status === 'pending').length === 0}
              title="Select which services to print"
            >
              Select Print
            </Button>
            <Button
              className="patient-whatsapp-button"
              icon={<WhatsAppOutlined />}
              loading={sendingExternalServicesWA}
              disabled={externalRequests.length === 0 || !selectedPatient?.patient_phone}
              onClick={() => {
                setSelectedExternalRequestIdsForWA([]);
                setExternalServicesWhatsAppModalVisible(true);
              }}
              title={!selectedPatient?.patient_phone ? 'No phone number on file' : 'Send services list via WhatsApp'}
            >
              إرسال عبر واتساب
            </Button>
          </Space>

          {externalServicesLoading ? (
            <div>Loading external services...</div>
          ) : visibleExternalRequests.length > 0 ? (
            <List
              dataSource={visibleExternalRequests}
              renderItem={(request) => (
                <List.Item
                  actions={[
                    ...(request.status === 'pending' && !(request as any)._offlinePending ? [
                      <Button
                        key="complete"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={async () => {
                          try {
                            await axios.put(
                              `${API.BASE_URL}${API.ENDPOINTS.EXTERNAL_REQUEST_STATUS(request.request_id)}`,
                              { status: 'completed' }
                            );
                            // Refresh the list
                            const response = await axios.get(
                              `${API.BASE_URL}${API.ENDPOINTS.PATIENT_EXTERNAL_REQUESTS(selectedPatient.patient_id)}`,
                              { params: { doctorId: selectedPatient.doctor_id } }
                            );
                            if (response.data.success) {
                              setExternalRequests(response.data.data || []);
                            }
                          } catch (error) {
                            console.error('Error marking as completed:', error);
                          }
                        }}
                      >
                        Mark Completed
                      </Button>
                    ] : []),
                    <Button
                      key="delete"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={deletingExternalRequestId === request.request_id}
                      disabled={(request as any)._offlinePending}
                      onClick={() => handleDeleteExternalRequest(request)}
                    >
                      {isRTL ? 'حذف' : 'Remove'}
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      request.status === 'completed' ? (
                        <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                      ) : (
                        <ClockCircleOutlined style={{ fontSize: 24, color: '#faad14' }} />
                      )
                    }
                    title={
                      <Space>
                        <Text strong>{request.service_name}</Text>
                        <Tag color={request.status === 'completed' ? 'green' : 'orange'}>
                          {(request as any)._offlinePending
                            ? (isRTL ? 'في انتظار المزامنة' : 'Pending sync')
                            : request.status === 'completed' ? 'Completed / مكتمل' : 'Pending / قيد الانتظار'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">Provider: {request.provider_name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Requested: {moment(request.requestedAt).format('YYYY-MM-DD HH:mm')}
                        </Text>
                        {request.completedAt && (
                          <>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Completed: {moment(request.completedAt).format('YYYY-MM-DD HH:mm')}
                            </Text>
                          </>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <div style={{ color: '#999' }}>{t('noExternalServicesAssigned')}</div>
          )}
        </div>

        {/* Assign Service Modal */}
        <Modal
          title={t('assignExternalServices')}
          open={assignServiceModalVisible}
          onCancel={() => {
            setAssignServiceModalVisible(false);
            setAddServiceFormVisible(false);
            setSelectedExternalServiceIds([]);
            setNewServiceName('');
            setNewProviderName('');
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setAssignServiceModalVisible(false);
                setAddServiceFormVisible(false);
                setSelectedExternalServiceIds([]);
                setNewServiceName('');
                setNewProviderName('');
              }}
            >
              Cancel
            </Button>,
            <Button
              key="assign"
              type="primary"
              loading={assignExternalServicesLoading}
              disabled={selectedExternalServiceIds.length === 0}
              onClick={handleAssignSelectedExternalServices}
              style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
            >
              Assign selected ({selectedExternalServiceIds.length})
            </Button>
          ]}
          width={600}
        >
          {/* Quick-add new service inline form */}
          {addServiceFormVisible ? (
            <div style={{ background: '#f0f9ff', border: '1px solid #91d5ff', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 12, color: '#1890ff' }}>
                <PlusOutlined style={{ marginRight: 6 }} />
                Add New External Service
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <Input
                  placeholder="Service name (e.g. X-Ray, Blood Test)"
                  value={newServiceName}
                  onChange={e => setNewServiceName(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Input
                  placeholder="Provider name (e.g. Lab, Hospital)"
                  value={newProviderName}
                  onChange={e => setNewProviderName(e.target.value)}
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="primary"
                  loading={addServiceLoading}
                  disabled={!newServiceName.trim()}
                  onClick={async () => {
                    if (!newServiceName.trim()) return;
                    const doctorId = selectedPatient.doctor_id || localStorage.getItem('doctorId');
                    try {
                      setAddServiceLoading(true);
                      await axios.post(`${API.BASE_URL}${API.ENDPOINTS.EXTERNAL_SERVICES}`, {
                        service_name: newServiceName.trim(),
                        provider_name: newProviderName.trim(),
                        doctor_id: doctorId
                      });
                      // Refresh the services list inside the modal
                      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_EXTERNAL_SERVICES(doctorId)}`);
                      if (res.data.success) setExternalServices(res.data.data || []);
                      setNewServiceName('');
                      setNewProviderName('');
                      setAddServiceFormVisible(false);
                    } catch (err) {
                      console.error('Error creating service:', err);
                    } finally {
                      setAddServiceLoading(false);
                    }
                  }}
                >
                  Save Service
                </Button>
                <Button onClick={() => { setAddServiceFormVisible(false); setNewServiceName(''); setNewProviderName(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <Button
                icon={<PlusOutlined />}
                type="dashed"
                onClick={() => setAddServiceFormVisible(true)}
                style={{ width: '100%', color: '#13c2c2', borderColor: '#13c2c2' }}
              >
                Add New External Service
              </Button>
            </div>
          )}

          <Checkbox.Group
            value={selectedExternalServiceIds}
            onChange={(values) => setSelectedExternalServiceIds(values as string[])}
            style={{ width: '100%' }}
          >
            <List
              dataSource={externalServices.filter(s => s.isActive !== false)}
              renderItem={(service) => (
                <List.Item>
                  <Checkbox value={service.service_id} style={{ width: '100%' }}>
                    <Space direction="vertical" size={0}>
                      <Text strong>{service.service_name}</Text>
                      <Text type="secondary">Provider: {service.provider_name}</Text>
                    </Space>
                  </Checkbox>
                </List.Item>
              )}
            />
          </Checkbox.Group>
          {externalServices.filter(s => s.isActive !== false).length === 0 && !addServiceFormVisible && (
            <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
              No services yet. Use the button above to add your first external service.
            </div>
          )}
        </Modal>

        <Divider />

        {/* Referrals Section */}
        <Title level={4}>
          <SwapOutlined style={{ color: '#08979c' }} /> {t('referralLettersSection')}
        </Title>
        {referralsLoading ? (
          <div style={{ marginBottom: 24, color: '#999' }}>{t('loadingReferrals')}</div>
        ) : visibleReferrals.length > 0 ? (
          <div style={{ marginBottom: 24 }}>
            {visibleReferrals.map((ref: any, idx: number) => (
              <Card
                key={ref.referral_id || idx}
                style={{ marginBottom: 12, borderLeft: '4px solid #08979c' }}
                size="small"
                extra={
                  <Space size="small">
                    {ref._offlinePending && <Tag color="gold">{isRTL ? 'في انتظار المزامنة' : 'Pending sync'}</Tag>}
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      disabled={ref._offlinePending}
                      onClick={() => { setReferralToEdit(ref); setReferralModalVisible(true); }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => triggerPrint(buildReferralLetterHtml(ref))}
                    >
                      Print
                    </Button>
                    <Button
                      size="small"
                      className="patient-whatsapp-button"
                      icon={<WhatsAppOutlined />}
                      loading={sendingReferralWA === (ref.referral_id || ref._id)}
                      disabled={!selectedPatient?.patient_phone || ref._offlinePending}
                      onClick={() => sendReferralViaWhatsApp(ref)}
                      title={selectedPatient?.patient_phone ? `Send to ${selectedPatient.patient_phone}` : 'No phone number'}
                    >
                      WhatsApp
                    </Button>
                  </Space>
                }
              >
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Date</Text>
                    <div style={{ fontWeight: 600 }}>
                      {ref.referral_date ? moment(ref.referral_date).format('DD MMM YYYY') : '—'}
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Subject</Text>
                    <div style={{ fontWeight: 500 }}>{ref.subject || '—'}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>From</Text>
                    <div>{ref.from_doctor_name || '—'}{ref.from_doctor_title ? ` · ${ref.from_doctor_title}` : ''}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>To</Text>
                    <div>{ref.to_doctor_name || '—'}{ref.to_doctor_title ? ` · ${ref.to_doctor_title}` : ''}</div>
                    {ref.to_clinic_name && <div style={{ fontSize: 12, color: '#888' }}>{ref.to_clinic_name}</div>}
                  </Col>
                  {ref.referral_body && (
                    <Col xs={24}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Summary</Text>
                      <div style={{ marginTop: 4, padding: '8px 12px', background: '#f0faff', borderRadius: 6, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', border: '1px solid #bae0ff', maxHeight: 120, overflow: 'hidden' }}>
                        {ref.referral_body.length > 300 ? ref.referral_body.slice(0, 300) + '…' : ref.referral_body}
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>
            ))}
          </div>
        ) : (
          <div style={{ marginBottom: 24, color: '#999' }}>{t('noReferralLettersYet')}</div>
        )}

        <Divider />

        {/* Medical Reports Section */}
        <Title level={4}>
          <FileTextOutlined style={{ color: '#52c41a' }} /> التقارير الطبية / Medical Reports
        </Title>
        {medicalReportsLoading ? (
          <div style={{ marginBottom: 24, color: '#999' }}>Loading reports...</div>
        ) : visibleMedicalReports.length > 0 ? (
          <div style={{ marginBottom: 24 }}>
            {visibleMedicalReports.map((report: any, idx: number) => (
              <Card
                key={report.report_id || idx}
                style={{ marginBottom: 12, borderLeft: '4px solid #52c41a' }}
                size="small"
                extra={
                  <Space size="small">
                    {report._offlinePending && <Tag color="gold">{isRTL ? 'في انتظار المزامنة' : 'Pending sync'}</Tag>}
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      disabled={report._offlinePending}
                      onClick={() => { setMedicalReportToEdit(report); setMedicalReportModalVisible(true); }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSavedMedicalReport(report)}
                    >
                      Print
                    </Button>
                    <Button
                      size="small"
                      className="patient-whatsapp-button"
                      icon={<WhatsAppOutlined />}
                      loading={sendingMedicalReportWA === (report.report_id || report._id)}
                      disabled={!selectedPatient?.patient_phone || report._offlinePending}
                      onClick={() => sendMedicalReportViaWhatsApp(report)}
                      title={selectedPatient?.patient_phone ? `Send to ${selectedPatient.patient_phone}` : 'No phone number'}
                    >
                      WhatsApp
                    </Button>
                  </Space>
                }
              >
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Date / التاريخ</Text>
                    <div style={{ fontWeight: 600 }}>
                      {report.date ? moment(report.date).format('DD MMM YYYY') : '—'}
                    </div>
                  </Col>
                  {report.doctor_name && (
                    <Col xs={24} sm={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Doctor / الطبيب</Text>
                      <div>{report.doctor_name}</div>
                    </Col>
                  )}
                  {report.diagnosis && (
                    <Col xs={24}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Diagnosis / التشخيص</Text>
                      <div style={{ fontWeight: 500, marginTop: 2 }}>{report.diagnosis}</div>
                    </Col>
                  )}
                  {report.medical_report && (
                    <Col xs={24}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Report / التقرير</Text>
                      <div style={{
                        marginTop: 4,
                        padding: '8px 12px',
                        background: '#f6ffed',
                        borderRadius: 6,
                        fontSize: 13,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        border: '1px solid #b7eb8f',
                        maxHeight: 120,
                        overflow: 'hidden',
                      }}>
                        {report.medical_report.length > 300
                          ? report.medical_report.slice(0, 300) + '…'
                          : report.medical_report}
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>
            ))}
          </div>
        ) : (
          <div style={{ marginBottom: 24, color: '#999' }}>No medical reports yet / لا توجد تقارير طبية بعد</div>
        )}

        {/* Visit Details Modal */}
        <Modal
          title={`Visit Details - ${moment(selectedVisit?.date).format('YYYY-MM-DD HH:mm')}`}
          open={visitDetailsVisible}
          onCancel={() => setVisitDetailsVisible(false)}
          footer={[
            (doctorSettings as any)?.diagramType === 'dental' && (
              <Button
                key="diagram"
                icon={<AimOutlined />}
                type="primary"
                style={{ backgroundColor: '#531dab', borderColor: '#531dab' }}
                onClick={() => {
                  const vid = selectedVisit?.visit_id || (selectedVisit as any)?._id;
                  if (vid) {
                    setDiagramVisitId(String(vid));
                    setDiagramInitialData((selectedVisit as any)?.diagram_data || null);
                    setVisitDetailsVisible(false);
                    setDiagramModalVisible(true);
                  }
                }}
              >
                {(doctorSettings as any).diagramType === 'dental' ? t('diagramDental') : t('diagramBody')}
              </Button>
            ),
            <Button key="back" onClick={() => setVisitDetailsVisible(false)}>
              Close
            </Button>
          ]}
          width={700}
        >
          {selectedVisit && (
            <div>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Visit Date: </Text>
                  {moment(selectedVisit.date).format('YYYY-MM-DD HH:mm')}
                </div>
                <div>
                  <Text strong>Visit Type: </Text>
                  {resolveVisitTypeName(selectedVisit.visit_type || '') || 'Not specified'}
                </div>
                <div>
                  <Text strong>Complaint: </Text>
                  {selectedVisit.complaint || 'None recorded'}
                </div>
                <div>
                  <Text strong>Diagnosis: </Text>
                  {selectedVisit.diagnosis || 'None recorded'}
                </div>

                <Divider />

                <Title level={5}>Prescriptions</Title>
                {selectedVisitReceipts.length > 0 ? (
                  <List
                    itemLayout="vertical"
                    dataSource={selectedVisitReceipts}
                    renderItem={(receipt) => {
                      const isObgyn =
                        (receipt as any).prescriptionTemplate === 'obgyn' ||
                        (receipt as any).prescriptionTemplate === 'infertility';
                      const templateLabel =
                        (receipt as any).prescriptionTemplate === 'infertility'
                          ? t('infertilityTemplate')
                          : 'OB/GYN';
                      const investigations: string[] = (receipt as any).investigations || [];
                      const obgyn = (selectedPatient as any)?.obgynHistory || {};

                      const buildGplad = () => [
                        obgyn.gravidity != null ? `G${obgyn.gravidity}` : '',
                        obgyn.parity != null ? `P${obgyn.parity}` : '',
                        obgyn.living != null ? `L${obgyn.living}` : '',
                        obgyn.abortion != null ? `A${obgyn.abortion}` : '',
                        obgyn.deaths != null ? `D${obgyn.deaths}` : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <List.Item
                          extra={
                            <Space>
                              <Button
                                icon={<EditOutlined />}
                                onClick={() => {
                                  setReceiptToEdit(receipt);
                                  setEditVisitId(selectedVisit?.visit_id || selectedVisit?._id || null);
                                  setEditReceiptVisible(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                type="primary"
                                icon={<FileTextOutlined />}
                                onClick={() => {
                                  // Close the Visit Details modal BEFORE
                                  // printing. On Samsung tablets the print
                                  // service snapshots the viewport for
                                  // its on-screen preview, so if the
                                  // modal is still open the preview shows
                                  // "Visit Details" behind the paper.
                                  // printHtml() already waits 400 ms
                                  // before calling print(), which gives
                                  // the modal's exit animation time to
                                  // finish.
                                  setVisitDetailsVisible(false);
                                  onPrintReceipt(receipt);
                                }}
                              >
                                Print
                              </Button>
                              <Button
                                className="patient-whatsapp-button"
                                icon={<WhatsAppOutlined />}
                                loading={sendingWA === receipt._id}
                                disabled={!selectedPatient?.patient_phone}
                                onClick={() => sendPrescriptionViaWhatsApp(receipt)}
                                title={selectedPatient?.patient_phone ? `Send to ${selectedPatient.patient_phone}` : 'No phone number'}
                              >
                                WhatsApp
                              </Button>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                loading={deletingPrescriptionId === receipt._id}
                                onClick={() => handleDeletePrescription({
                                  ...receipt,
                                  _visitId: selectedVisit?.visit_id || selectedVisit?._id,
                                })}
                              >
                                {isRTL ? 'حذف' : 'Remove'}
                              </Button>
                            </Space>
                          }
                        >
                          <List.Item.Meta
                            title={
                              <span>
                                {isObgyn && (
                                  <span style={{
                                    background: '#d63384', color: '#fff',
                                    borderRadius: 4, padding: '1px 7px',
                                    fontSize: 11, marginLeft: 8, fontWeight: 'normal'
                                  }}>
                                    {templateLabel}
                                  </span>
                                )}
                                {` Receipt — ${moment(receipt.date).format('YYYY-MM-DD HH:mm')}`}
                              </span>
                            }
                          />

                          {/* ── OB/GYN layout ── */}
                          {isObgyn && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>

                                {/* Left: Obstetric history */}
                                <div style={{
                                  flex: '0 0 32%', border: '1.5px solid #d63384',
                                  borderRadius: 6, padding: '8px 10px'
                                }}>
                                  <div style={{ fontSize: 12, fontWeight: 'bold', color: '#d63384', marginBottom: 6 }}>
                                    history / التاريخ المرضي
                                  </div>
                                  {[
                                    obgyn.marriedSince && ['Married since / متزوجة منذ', obgyn.marriedSince],
                                    buildGplad() && ['GPLAD', buildGplad()],
                                    (obgyn.deliveryType || obgyn.deliverySince) && [
                                      'Delivery / الولادة',
                                      [obgyn.deliveryType, obgyn.deliverySince].filter(Boolean).join(' — ')
                                    ],
                                    obgyn.lmp && ['LMP', obgyn.lmp],
                                    obgyn.edd && ['EDD', obgyn.edd],
                                    obgyn.medications && ['Medications / الأدوية', obgyn.medications],
                                    obgyn.pastHistory && ['Past history / التاريخ المرضي', obgyn.pastHistory],
                                    obgyn.surgicalHistory && ['Surgical history / الجراحي', obgyn.surgicalHistory],
                                    obgyn.husbandName && ['Husband name / اسم الزوج', obgyn.husbandName],
                                    obgyn.husbandAge && ['Husband age / عمر الزوج', obgyn.husbandAge],
                                    obgyn.husbandJob && ['Husband job / مهنة الزوج', obgyn.husbandJob],
                                    obgyn.husbandResidency && ['Residency / الإقامة', obgyn.husbandResidency],
                                    obgyn.consanguinity && ['Consanguinity / أقارب', obgyn.consanguinity === 'yes' ? 'نعم / Yes' : 'لا / No'],
                                    obgyn.bloodGroup && ['Blood group / فصيلة الدم', obgyn.bloodGroup],
                                    obgyn.bloodPressure && ['Blood pressure / ضغط الدم', obgyn.bloodPressure],
                                    obgyn.weight && ['Weight / الوزن', obgyn.weight],
                                    obgyn.semen && ['Semen', obgyn.semen],
                                    obgyn.hormones && ['Hormones', obgyn.hormones],
                                    obgyn.hsg && ['HSG', obgyn.hsg],
                                  ].filter(Boolean).map((row: any, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 3, fontSize: 12 }}>
                                      <span style={{ color: '#666', fontWeight: 600, minWidth: 100 }}>{row[0]}:</span>
                                      <span>{row[1]}</span>
                                    </div>
                                  ))}
                                  {!buildGplad() && !obgyn.marriedSince && !obgyn.lmp && (
                                    <span style={{ color: '#bbb', fontSize: 12 }}>No obstetric history recorded</span>
                                  )}
                                </div>

                                {/* Right: Drugs */}
                                <div style={{
                                  flex: 1, border: '1.5px solid #d63384',
                                  borderRadius: 6, padding: '8px 10px'
                                }}>
                                  <div style={{ fontSize: 12, fontWeight: 'bold', color: '#d63384', marginBottom: 6 }}>
                                    Medications / الأدوية
                                  </div>
                                  {receipt.drugs && receipt.drugs.length > 0 ? (
                                    <List
                                      size="small"
                                      dataSource={receipt.drugs}
                                      renderItem={(drug: any) => (
                                        <List.Item style={{ padding: '3px 0', borderBottom: '1px solid #f9e4ef' }}>
                                          <Text strong>{drug.drug}</Text>
                                          {' — '}
                                          {getDrugSpecParts(drug).join(', ')}
                                        </List.Item>
                                      )}
                                    />
                                  ) : (
                                    <Text type="secondary" style={{ fontSize: 12 }}>No medications</Text>
                                  )}
                                  {receipt.notes && (
                                    <div style={{ marginTop: 8, fontSize: 12 }}>
                                      <Text strong>Notes: </Text>{receipt.notes}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Investigations */}
                              {investigations.length > 0 && (
                                <div style={{
                                  border: '1.5px solid #d63384', borderRadius: 6,
                                  padding: '8px 12px'
                                }}>
                                  <div style={{ fontSize: 12, fontWeight: 'bold', color: '#d63384', marginBottom: 6 }}>
                                    Investigations / الفحوصات
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
                                    {investigations.map(inv => (
                                      <span key={inv} style={{
                                        background: '#fff0f6', border: '1px solid #ffadd2',
                                        color: '#c41d7f', borderRadius: 4,
                                        padding: '1px 8px', fontSize: 12
                                      }}>
                                        {inv}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── Default layout (unchanged) ── */}
                          {!isObgyn && (
                            <div>
                              <Title level={5}>Medications</Title>
                              {receipt.drugs && receipt.drugs.length > 0 ? (
                                <List
                                  dataSource={receipt.drugs}
                                  renderItem={(drug: any) => (
                                    <List.Item>
                                      <Text strong>{drug.drug}</Text> - {getDrugSpecParts(drug).join(', ')}
                                    </List.Item>
                                  )}
                                />
                              ) : (
                                <Text>No medications prescribed</Text>
                              )}
                              {receipt.notes && (
                                <div style={{ marginTop: 16 }}>
                                  <Text strong>Notes: </Text>
                                  {receipt.notes}
                                </div>
                              )}
                            </div>
                          )}
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <Text>No prescriptions for this visit</Text>
                )}
              </Space>
            </div>
          )}
        </Modal>
      </Card>

      {/* Edit Prescription Modal */}
      <ReceiptModal
        visible={editReceiptVisible}
        onCancel={() => { setEditReceiptVisible(false); setReceiptToEdit(null); setEditVisitId(null); }}
        patient={selectedPatient as any}
        editMode={true}
        receiptToEdit={receiptToEdit}
        visitId={editVisitId}
        onReceiptAdded={async (updatedPatient) => {
          setEditReceiptVisible(false);
          setReceiptToEdit(null);
          setEditVisitId(null);
          // OB/GYN preview reads obstetric fields from selectedPatient.obgynHistory — merge from API response
          if (updatedPatient?.obgynHistory != null) {
            setSelectedPatient((prev: any) =>
              prev ? { ...prev, obgynHistory: updatedPatient.obgynHistory } : prev
            );
          }
          // Re-fetch visit list and refresh the open visit modal receipts (drugs, investigations)
          const freshHistory = await fetchPatientHistory();
          if (freshHistory && selectedVisit) {
            const targetId = selectedVisit._id || (selectedVisit as any).visit_id;
            const freshVisit = (freshHistory.visits || []).find(
              (v: any) => v._id === targetId || v.visit_id === targetId
            );
            if (freshVisit) setSelectedVisit(freshVisit);
          }
        }}
      />

      {/* Quick Notification Modal */}
      <SendNotification
        visible={notificationModalVisible}
        onCancel={() => setNotificationModalVisible(false)}
        patientName={selectedPatient?.patient_name}
        quickSend={true}
      />

      <Modal
        title={language === 'ar' ? 'تحويل الزيارة لطبيب آخر' : 'Transfer visit to another doctor'}
        open={transferModalVisible}
        onCancel={() => !transferSubmitting && setTransferModalVisible(false)}
        onOk={handleTransferVisit}
        okText={language === 'ar' ? 'تأكيد التحويل' : 'Confirm transfer'}
        cancelText={language === 'ar' ? 'إلغاء' : 'Cancel'}
        confirmLoading={transferSubmitting}
        okButtonProps={{ disabled: !transferDoctorId }}
        destroyOnClose
      >
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#fff7e6', color: '#873800' }}>
          {language === 'ar'
            ? 'ستظل نفس الزيارة ظاهرة عند الطبيب الأول وتظهر للطبيب المستلم بنفس رقم الزيارة. لن يتم إنشاء زيارة انتظار أو فاتورة أو إيراد جديد.'
            : 'The same visit remains visible to the original doctor and appears for the receiving doctor with the same visit ID. No waiting entry, bill, or additional revenue will be created.'}
        </div>
        <div style={{ marginBottom: 6, fontWeight: 600 }}>
          {language === 'ar' ? 'الطبيب المستلم' : 'Receiving doctor'}
        </div>
        <Select
          value={transferDoctorId || undefined}
          onChange={setTransferDoctorId}
          placeholder={language === 'ar' ? 'اختر طبيباً من نفس العيادة' : 'Choose a doctor in this clinic'}
          style={{ width: '100%', marginBottom: 16 }}
          showSearch
          optionFilterProp="label"
          options={transferDoctors.map((doctor) => ({ value: doctor.id, label: `د. ${doctor.name}` }))}
        />
        <div style={{ marginBottom: 6, fontWeight: 600 }}>
          {language === 'ar' ? 'سبب التحويل (اختياري)' : 'Reason (optional)'}
        </div>
        <Input.TextArea
          value={transferReason}
          onChange={(event) => setTransferReason(event.target.value)}
          maxLength={500}
          showCount
          rows={3}
          placeholder={language === 'ar' ? 'مثال: يحتاج مراجعة تخصص آخر' : 'Example: needs another specialty review'}
        />
      </Modal>

      {/* Next Visit Reminder Modal */}
      {sessionDoctorId && (
        <NextVisitForm
          visible={nextVisitModalVisible}
          onCancel={() => setNextVisitModalVisible(false)}
          patient={selectedPatient}
        />
      )}

      {/* Billing Modal */}
      <BillingModal
        visible={billingModalVisible}
        onCancel={() => setBillingModalVisible(false)}
        patient={selectedPatient}
        visitId={undefined}
        onBillingComplete={() => {
          setBillingModalVisible(false);
          fetchPatientBillings();
          if (selectedPatient?.patient_id) {
            const doctorId = localStorage.getItem('doctorId');
            if (doctorId) {
              axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_ACTIVE_PACKAGES(selectedPatient.patient_id)}`, { params: { doctor_id: doctorId } })
                .then(r => { if (r.data?.success) setActivePackages(r.data.data || []); });
              axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_PACKAGES(selectedPatient.patient_id)}`, { params: { doctor_id: doctorId } })
                .then(r => { if (r.data?.success) setAllPatientPackages(r.data.data || []); });
            }
          }
        }}
      />

      {/* Active package popup */}
      <Modal
        title="Patient has an active package"
        open={activePackagePopupVisible && activePackages.length > 0}
        onCancel={() => setActivePackagePopupVisible(false)}
        footer={[<Button key="ok" type="primary" onClick={() => setActivePackagePopupVisible(false)}>OK</Button>]}
      >
        {activePackages.map(pp => (
          <div key={pp.patient_package_id} style={{ marginBottom: 12 }}>
            <Text strong>{pp.package_name}</Text>
            <br />
            <Text>Remaining Sessions: {pp.remaining_sessions}</Text>
            {pp.expiry_date && <><br /><Text type="secondary">Expiry: {moment(pp.expiry_date).format('YYYY-MM-DD')}</Text></>}
          </div>
        ))}
      </Modal>

      {/* Assign Package Modal */}
      <InsuranceProgramModal
        open={insuranceProgramVisible}
        patient={selectedPatient}
        doctorId={selectedPatient.doctor_id || sessionDoctorId || ''}
        ownerDoctorId={ownerDoctorIdForClinic}
        clinicId={selectedClinicId || selectedPatient.clinic_id || ''}
        billings={patientBillings}
        onClose={() => setInsuranceProgramVisible(false)}
        onSaved={handleInsuranceProgramSaved}
        onPatientInsuranceUpdated={(update) => setSelectedPatient((current: any) => {
          if (!current) return current;
          const unchanged = String(current.insurance_company_id || '') === String(update.insurance_company_id || '') &&
            String(current.insurance_number || '') === String(update.insurance_number || '') &&
            Number(current.coverage_percentage ?? 0) === Number(update.coverage_percentage ?? 0);
          return unchanged ? current : { ...current, ...update };
        })}
      />

      <Modal
        title={t('assignPackageModalTitle')}
        open={assignPackageModalVisible}
        onCancel={() => { setAssignPackageModalVisible(false); setSelectedPackageToAssign(null); }}
        onOk={handleAssignPackage}
        okText={t('assignPackageOk')}
        confirmLoading={assignPackageSubmitting}
        okButtonProps={{ disabled: !selectedPackageToAssign }}
      >
        <Select
          placeholder={t('selectPackagePlaceholder')}
          style={{ width: '100%' }}
          value={selectedPackageToAssign || undefined}
          onChange={(v) => setSelectedPackageToAssign(v)}
          showSearch
          optionFilterProp="children"
        >
          {doctorPackagesForAssign.map((p: any) => (
            <Select.Option key={p.package_id} value={p.package_id}>
              {p.name} – {p.sessions_count} sessions, {p.price?.toLocaleString()} EGP
            </Select.Option>
          ))}
        </Select>
      </Modal>

      {/* Discount Modal */}
      <Modal
        title={t('applyDiscountTitle')}
        open={discountModalVisible}
        onCancel={() => setDiscountModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={handleApplyDiscount}
        >
          <Form.Item
            name="amount"
            label={t('discountAmountLabel')}
            rules={[{ required: true, message: t('discountAmountRequired') }, { type: 'number', min: 0.01, message: t('discountAmountPositive') }]}
          >
            <InputNumber min={0.01} step={1} placeholder="e.g. 50" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label={t('discountReasonOptional')}>
            <Input.TextArea rows={2} placeholder="e.g. خصم لظروف خاصة" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setDiscountModalVisible(false)} disabled={discountSubmitting}>{t('cancel')}</Button>
              <Button type="primary" htmlType="submit" icon={<PercentageOutlined />} loading={discountSubmitting} disabled={discountSubmitting}>
                {t('applyDiscountButton')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={imagePreviewVisible}
        footer={null}
        onCancel={() => setImagePreviewVisible(false)}
        width={800}
        centered
      >
        <OfflineImage
          alt="Preview"
          style={{ width: '100%' }}
          src={previewImage}
        />
      </Modal>

      {/* Edit Complaint & Diagnosis Modal */}
      <Modal
        open={!!editComplaintEntry}
        title={t('editComplaintDiagnosis')}
        onCancel={() => { setEditComplaintEntry(null); setEditComplaintValue(''); setEditDiagnosisValue(''); setEditProcedureValue(''); }}
        footer={null}
        destroyOnClose
      >
        <Row gutter={[12, 12]}>
          <Col xs={24}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('complaint')}</div>
            <AutoComplete
              value={editComplaintValue}
              options={filterComplaintsSuggestion(editComplaintValue)}
              filterOption={false}
              style={{ width: '100%' }}
              onChange={(val) => setEditComplaintValue(val)}
              onSelect={(val) => setEditComplaintValue(val)}
            >
              <Input.TextArea rows={3} placeholder={t('enterComplaintPlaceholder')} style={{ fontSize: 14 }} />
            </AutoComplete>
          </Col>
          <Col xs={24}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('diagnosis')}</div>
            <AutoComplete
              value={editDiagnosisValue}
              options={filterDiagnosesSuggestion(editDiagnosisValue)}
              filterOption={false}
              style={{ width: '100%' }}
              onChange={(val) => setEditDiagnosisValue(val)}
              onSelect={(val) => setEditDiagnosisValue(val)}
            >
              <Input.TextArea rows={3} placeholder={t('enterDiagnosisPlaceholder')} style={{ fontSize: 14 }} />
            </AutoComplete>
          </Col>
          <Col xs={24}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('procedure')}</div>
            <Input.TextArea
              rows={3}
              value={editProcedureValue}
              onChange={(event) => setEditProcedureValue(event.target.value)}
              placeholder={t('enterProcedurePlaceholder')}
              style={{ fontSize: 14 }}
            />
          </Col>
          <Col xs={24} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button
              type="primary"
              loading={editComplaintLoading}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              onClick={async () => {
                if (!editComplaintEntry) return;
                try {
                  setEditComplaintLoading(true);
                  const doctorId = editComplaintEntry.doctor_id || selectedPatient?.doctor_id || localStorage.getItem('doctorId');
                  await axios.put(`${API.BASE_URL}/api/patients/complaint-history/${editComplaintEntry._id}`, {
                    ...buildComplaintIdentityPayload(
                      { ...(selectedPatient || {}), doctor_id: doctorId || '' },
                      editComplaintEntry.patient_mongo_id,
                    ),
                    complaint: editComplaintValue.trim(),
                    diagnosis: editDiagnosisValue.trim(),
                    procedure: editProcedureValue.trim(),
                  });
                  if (editComplaintValue.trim()) saveComplaintSuggestion(editComplaintValue.trim());
                  if (editDiagnosisValue.trim()) saveDiagnosisSuggestion(editDiagnosisValue.trim());
                  setEditComplaintEntry(null);
                  setEditComplaintValue('');
                  setEditDiagnosisValue('');
                  setEditProcedureValue('');
                  const refreshed = await fetchPatientHistory();
                  message.success(refreshed ? 'Complaint and diagnosis updated' : 'Updated, but the patient file could not refresh');
                } catch (err: any) {
                  console.error('Failed to update complaint entry', err);
                  message.error(err?.response?.data?.message || 'Failed to update complaint and diagnosis');
                } finally {
                  setEditComplaintLoading(false);
                }
              }}
            >
              {t('save')}
            </Button>
            <Button onClick={() => { setEditComplaintEntry(null); setEditComplaintValue(''); setEditDiagnosisValue(''); setEditProcedureValue(''); }}>
              {t('cancel')}
            </Button>
          </Col>
        </Row>
      </Modal>

      {/* Medical History Modal */}
      <DynamicHistoryForm
        visible={medicalHistoryModalVisible}
        onCancel={() => setMedicalHistoryModalVisible(false)}
        patient={selectedPatient}
        doctorId={selectedPatient?.doctor_id || ''}
      />

      {/* Medical Report Modal */}
      <MedicalReportModal
        visible={medicalReportModalVisible}
        onCancel={() => { setMedicalReportModalVisible(false); setMedicalReportToEdit(null); }}
        patient={selectedPatient}
        reportToEdit={medicalReportToEdit}
        latestVisitDiagnosis={latestVisitDiagnosis}
        previousDiagnoses={
          patientHistory?.visits
            ?.map(v => v.diagnosis)
            .filter((d): d is string => !!(d && d.trim())) ?? []
        }
        onReportSaved={async () => {
          setMedicalReportModalVisible(false);
          setMedicalReportToEdit(null);
          if (selectedPatient?._id) {
            try {
              const res = await axios.get(
                `${API.BASE_URL}${API.ENDPOINTS.MEDICAL_REPORTS(selectedPatient._id)}`
              );
              if (res.data?.success) {
                const sorted = (res.data.medical_reports || []).slice().reverse();
                setPatientMedicalReports(sorted);
              }
            } catch { }
          }
        }}
      />

      {/* Referral Modal */}
      <ReferralModal
        visible={referralModalVisible}
        onCancel={() => { setReferralModalVisible(false); setReferralToEdit(null); }}
        patient={selectedPatient}
        referralToEdit={referralToEdit}
        latestDiagnosis={latestPrescriptionDiagnosis}
        onReferralSaved={async () => {
          setReferralToEdit(null);
          if (selectedPatient?._id) {
            try {
              const res = await axios.get(
                `${API.BASE_URL}${API.ENDPOINTS.REFERRALS(selectedPatient._id)}`
              );
              if (res.data?.success) {
                const sorted = (res.data.referrals || []).slice().reverse();
                setPatientReferrals(sorted);
              }
            } catch { }
          }
        }}
      />

      <UploadReportModal
        visible={uploadReportModalVisible}
        onCancel={() => setUploadReportModalVisible(false)}
        patient={selectedPatient}
        assistantId={localStorage.getItem('doctorId') || undefined}
        onSuccess={async () => {
          if (selectedPatient) {
            try {
              setReportsLoading(true);
              const refreshParams: Record<string, string> = {
                patient_id: selectedPatient.patient_id,
                patient_phone: selectedPatient.patient_phone,
                doctor_id: selectedPatient.doctor_id,
              };
              if (selectedPatient._id) {
                refreshParams.mongo_id = selectedPatient._id;
              }
              const fn2 = selectedPatient.file_number || selectedPatient.file_id;
              if (fn2) {
                refreshParams.file_number = String(fn2).trim();
              }
              const res = await axios.get(`${API.BASE_URL}/api/patients/reports`, {
                params: refreshParams
              });
              if (res.data.success) setPatientReports(res.data.data || []);
            } catch { } finally {
              setReportsLoading(false);
            }
          }
        }}
      />

      {selectedPatient && (
        <ManualInventoryDeductionModal
          open={manualInventoryVisible}
          patient={selectedPatient}
          onClose={() => setManualInventoryVisible(false)}
        />
      )}

      {/* Specialty Diagram Modal (Dental Chart / Body Map) */}
      {(doctorSettings as any)?.diagramType && (doctorSettings as any).diagramType !== 'none' && diagramVisitId && selectedPatient && (
        <DiagramViewer
          visible={diagramModalVisible}
          onClose={() => { setDiagramModalVisible(false); setDiagramVisitId(null); }}
          diagramType={(doctorSettings as any).diagramType}
          patientId={selectedPatient.patient_id}
          doctorId={selectedPatient.doctor_id || localStorage.getItem('doctorId') || ''}
          visitId={diagramVisitId}
          initialData={diagramInitialData}
          onSaved={(data) => {
            setDiagramInitialData(data);
            setDiagramModalVisible(false);
            setDiagramVisitId(null);
            fetchPatientHistory();
          }}
        />
      )}

      {/* Select Print Modal */}
      <Modal
        title="Select Services to Print"
        open={printSelectModalVisible}
        onOk={() => {
          const selected = visibleExternalRequests.filter(r => selectedPrintIds.includes(r.request_id));
          const html = buildLabOrdersHtml(selected);
          if (html) triggerPrint(html);
          setPrintSelectModalVisible(false);
        }}
        onCancel={() => setPrintSelectModalVisible(false)}
        okText={<><PrinterOutlined /> Print</>}
        okButtonProps={{ disabled: selectedPrintIds.length === 0 }}
        cancelText="Cancel"
      >
        <Space style={{ marginBottom: 12 }}>
          <Button
            size="small"
            onClick={() =>
              setSelectedPrintIds(
                visibleExternalRequests.filter(r => r.status === 'pending').map(r => r.request_id)
              )
            }
          >
            Select All
          </Button>
          <Button size="small" onClick={() => setSelectedPrintIds([])}>
            Clear
          </Button>
        </Space>
        <Checkbox.Group
          value={selectedPrintIds}
          onChange={(vals) => setSelectedPrintIds(vals as string[])}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {visibleExternalRequests
              .filter(r => r.status === 'pending')
              .map(r => (
                <Checkbox key={r.request_id} value={r.request_id}>
                  <span style={{ fontWeight: 500 }}>{r.service_name}</span>
                  {r.provider_name && (
                    <span style={{ color: '#888', marginRight: 8, fontSize: 13 }}>
                      — {r.provider_name}
                    </span>
                  )}
                </Checkbox>
              ))}
          </Space>
        </Checkbox.Group>
      </Modal>

      {/* Select External Services for WhatsApp Modal */}
      <Modal
        title="Select services to send / اختر التحاليل والخدمات للإرسال"
        open={externalServicesWhatsAppModalVisible}
        onOk={sendExternalServicesViaWhatsApp}
        onCancel={() => {
          setExternalServicesWhatsAppModalVisible(false);
          setSelectedExternalRequestIdsForWA([]);
        }}
        okText={<><WhatsAppOutlined /> Send via WhatsApp / إرسال عبر واتساب</>}
        okButtonProps={{
          disabled: selectedExternalRequestIdsForWA.length === 0,
          loading: sendingExternalServicesWA,
          className: 'patient-whatsapp-button'
        }}
        cancelText="Cancel / إلغاء"
      >
        <Space style={{ marginBottom: 12 }}>
          <Button
            size="small"
            onClick={() => setSelectedExternalRequestIdsForWA(externalRequests.map(r => r.request_id))}
          >
            Select All / تحديد الكل
          </Button>
          <Button size="small" onClick={() => setSelectedExternalRequestIdsForWA([])}>
            Clear / إلغاء التحديد
          </Button>
        </Space>
        <Checkbox.Group
          value={selectedExternalRequestIdsForWA}
          onChange={(values) => setSelectedExternalRequestIdsForWA(values as string[])}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {externalRequests.map((request) => (
              <Checkbox key={request.request_id} value={request.request_id}>
                <span style={{ fontWeight: 500 }}>{request.service_name}</span>
                {request.provider_name && (
                  <span style={{ color: '#888', marginRight: 8, fontSize: 13 }}>
                    {' — '}{request.provider_name}
                  </span>
                )}
                <Tag
                  color={request.status === 'completed' ? 'green' : 'orange'}
                  style={{ marginInlineStart: 8 }}
                >
                  {request.status === 'completed' ? 'Completed / مكتمل' : 'Pending / قيد الانتظار'}
                </Tag>
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      </Modal>

    </div>
  );
};

export default PatientDetail;
